import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CustomCredentialFlow } from '../components/CustomCredentialFlow';
import { LayoutGrid, Plug, MessageSquare, UserCircle, Map, Activity, LayoutTemplate, FolderOpen, Monitor, ChevronDown, Bookmark, Plus, Settings, Paperclip, Send, X, Search, Clock, FileText, Folder, Play, Pause, CheckCircle2, Sparkles, ArrowRight, ChevronLeft, ChevronRight, ListChecks, Globe, Loader2, Lock, Users, Zap, Star, MoreHorizontal, Info, Pencil, Trash2, Download, Sun, Moon, History, AlertTriangle, Filter, Check, Bell, Minus, ChevronUp, ChevronsUpDown, PhoneCall, GraduationCap, Layers, CreditCard, LogOut } from 'lucide-react';
import { BillingModal } from '../components/BillingModal';
import { TracksView } from '../components/dashboard/TracksView';
import { GroupsView } from '../components/dashboard/GroupsView';
import { TablesView } from '../components/dashboard/TablesView';
import { TRACKS } from '../data/workspaceTracks';
// Recent chats shown in the sidebar. Kept short on purpose — it's a "pick up
// where you left off" list, not an archive (that's the Chats view).
const SIDEBAR_CHATS = ['SideShift connection check', 'Company Brain campaign brief', 'Q3 pipeline review', 'Creator outreach draft'];
// ── Activity log types ───────────────────────────────────────────────────────
// A system-wide, read-only feed of everything Giga did on your behalf. The
// source of one entry is the kind of automation that triggered it; the status
// is the outcome of that run. Both drive the Activity view's filters.
type ActivityType = 'Chat' | 'Pulse' | 'Journey' | 'Recipe' | 'Assistant';
type ActivityStatus = 'success' | 'running' | 'needs-attention';
// Timing filter ranges.
type ActivityRange = 'all' | '7d' | '30d' | '90d' | '6m' | '12m' | 'custom';
type ActivityEntry = {
  task: string;
  detail: string;
  type: ActivityType;
  status: ActivityStatus;
  source: string;
  tool: string;
  // Days ago used for date grouping and the timing filter.
  daysAgo: number;
  time: string;
};
// ── Starter prompt categories & data ────────────────────────────────────────
type StarterCategory = 'comms' | 'operations' | 'admin' | 'growth' | 'insights';
type PlanSite = {
  slug: string;
  label: string;
};
type StarterPrompt = {
  title: string;
  desc: string;
  tools: string[];
  // The domains/tools the plan will act on, shown under "Allow actions on…".
  sites: PlanSite[];
  // The step-by-step approach Giga proposes before acting.
  steps: string[];
};
const STARTER_CATEGORIES: {
  id: StarterCategory;
  label: string;
}[] = [{
  id: 'comms',
  label: 'Comms'
}, {
  id: 'operations',
  label: 'Operations'
}, {
  id: 'admin',
  label: 'Admin'
}, {
  id: 'growth',
  label: 'Growth'
}, {
  id: 'insights',
  label: 'Insights'
}];
const STARTER_PROMPTS: Record<StarterCategory, StarterPrompt[]> = {
  comms: [{
    title: 'Meeting recaps',
    desc: 'When a meeting transcript lands, create tasks for action items and email a recap to attendees.',
    tools: ['gmail', 'asana', 'slack'],
    sites: [{
      slug: 'asana',
      label: 'Asana'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Pull the latest meeting transcript and identify the action items', 'Create an Asana task for each action item with its owner and due date', 'Draft a concise recap summarizing decisions and next steps', 'Email the recap to every attendee from Gmail', 'Post a short summary to the relevant Slack channel']
  }, {
    title: 'Slack reply nudges',
    desc: "When I'm mentioned in a thread but don't reply within 2 hours, text me a reminder with context.",
    tools: ['slack'],
    sites: [{
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch every Slack thread where you are @-mentioned', 'Start a 2-hour timer the moment a mention lands', 'Check whether you have replied before the timer runs out', 'If not, summarize who asked and what they need', 'Send you a reminder with a direct link to the thread']
  }, {
    title: 'Daily briefing',
    desc: 'Send me a morning briefing pulled from my Calendar, Gmail, and open Asana tasks.',
    tools: ['google', 'gmail', 'asana'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ["Pull today's events from Google Calendar", 'Scan Gmail for unread or flagged messages that need a reply', 'List Asana tasks due today or overdue', 'Compose a single, scannable morning briefing', 'Deliver it to you at 8:00am each weekday']
  }],
  operations: [{
    title: 'Protect focus blocks',
    desc: 'On a fragmented day, see if meetings can shift to open up focus blocks.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }],
    steps: ["Review today's calendar for fragmentation and gaps", 'Identify meetings that could be moved without conflicts', 'Find at least one 90-minute focus block to protect', 'Draft reschedule proposals for the movable meetings', 'Ask for your approval before sending any invites']
  }, {
    title: 'Bug triage pipeline',
    desc: 'When a Sentry error spikes, open a GitHub issue and post the details to Slack.',
    tools: ['sentry', 'github', 'slack'],
    sites: [{
      slug: 'sentry',
      label: 'Sentry'
    }, {
      slug: 'github',
      label: 'GitHub'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Detect when a Sentry error crosses its spike threshold', 'Gather the stack trace, affected users, and release info', 'Open a GitHub issue with the details and a triage label', 'Post an alert with the issue link to the #engineering channel', 'Tag the most likely owner based on recent commits']
  }, {
    title: 'Stale issue sweep',
    desc: 'Each week, find Linear issues with no activity in 14 days and ping their owners.',
    tools: ['linear', 'slack'],
    sites: [{
      slug: 'linear',
      label: 'Linear'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Query Linear for issues with no activity in the last 14 days', 'Group the stale issues by assignee', 'Draft a friendly nudge for each owner with their issue list', 'Send each nudge as a Slack DM', 'Post a summary of the sweep to the team channel']
  }],
  admin: [{
    title: 'Friday inbox cleanup',
    desc: 'Every Friday, archive non-actionable emails and flag anything that needs a reply.',
    tools: ['gmail'],
    sites: [{
      slug: 'gmail',
      label: 'Gmail'
    }],
    steps: ["Scan this week's Gmail inbox and classify each message", 'Archive newsletters and notifications that need no action', 'Flag messages that still need a reply from you', 'Draft quick replies for the most routine threads', 'Send you a short summary of what was cleaned up']
  }, {
    title: 'Invoice reconciliation',
    desc: 'When a payment lands in Stripe, match it to an invoice and update the books.',
    tools: ['stripe', 'notion'],
    sites: [{
      slug: 'stripe',
      label: 'Stripe'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Detect the new payment as it lands in Stripe', 'Match the amount and customer to an open invoice', 'Mark the matched invoice as paid', 'Update the bookkeeping ledger in Notion', 'Flag any payment that has no matching invoice']
  }, {
    title: 'Document filing',
    desc: 'Sort new files into the right Drive folders and rename them to our convention.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Drive'
    }],
    steps: ['Watch the Drive inbox folder for newly added files', 'Read each file to infer its type and project', 'Rename it to match the team naming convention', 'Move it into the correct project folder', 'Log anything ambiguous for you to confirm']
  }],
  growth: [{
    title: 'Ad spend audit',
    desc: 'Pause ad sets under 0.8x ROAS in Meta Ads and export a summary PDF.',
    tools: ['meta', 'notion'],
    sites: [{
      slug: 'meta',
      label: 'Meta Ads'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Pull the last 14 days of spend and ROAS from Meta Ads', 'Flag every ad set performing under 0.8× ROAS', 'Pause the flagged ad sets to stop the bleed', 'Export a summary PDF of what changed and why', 'Save the report to Notion and notify you']
  }, {
    title: 'New deal onboarding',
    desc: 'When a deal closes, create the client workspace and send the welcome sequence.',
    tools: ['notion', 'gmail', 'asana'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ['Detect the closed-won deal and pull the client details', 'Spin up the client onboarding workspace in Notion', 'Create the standard onboarding tasks in Asana', 'Send the welcome email sequence from Gmail', 'Notify the account owner that onboarding has kicked off']
  }, {
    title: 'Lead enrichment',
    desc: 'Enrich new CRM contacts with firmographics and route hot ones to sales.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch Attio for newly added contacts', 'Enrich each one with firmographic and role data', 'Score the lead against your ideal-customer profile', 'Update the CRM record with the enriched fields', 'Route hot leads to sales in the #pipeline channel']
  }],
  insights: [{
    title: 'Weekly report generation',
    desc: 'Every Monday, pull last week metrics and post a digest to #leadership.',
    tools: ['notion', 'slack'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ["Pull last week's metrics from the Notion dashboards", 'Compare them against the prior week and targets', 'Highlight the biggest wins and the risks to watch', 'Compose a concise leadership digest', 'Post it to the #leadership channel every Monday at 9am']
  }, {
    title: 'Pipeline cleanup',
    desc: 'Flag stale or at-risk deals and summarize what changed since last week.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Scan the Attio pipeline for deals with no recent activity', 'Flag deals slipping past their expected close date', 'Summarize what changed on each deal since last week', 'Suggest a next step for every at-risk deal', 'Post the cleanup summary to the sales channel']
  }, {
    title: 'Support trends',
    desc: 'Cluster this week support tickets by theme and surface the top issues.',
    tools: ['crisp', 'notion'],
    sites: [{
      slug: 'crisp',
      label: 'Crisp'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ["Pull this week's resolved and open tickets from Crisp", 'Cluster the tickets by underlying theme', 'Rank the themes by volume and customer impact', 'Write up the top three issues with example tickets', 'Save the trends report to Notion for the team']
  }]
};
// Small tool/site icon chip reused across the rail.
function ToolIcon({
  slug,
  size = 'sm'
}: {
  slug: string;
  size?: 'sm' | 'md';
}) {
  const box = size === 'md' ? 'w-6 h-6' : 'w-5 h-5';
  const img = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  return <span className={`${box} rounded-md bg-white border border-neutral-200 flex items-center justify-center shrink-0`} data-id="element-6809" data-mp="3p99sthpun1iw">
      <img src={`https://cdn.simpleicons.org/${slug}/475569`} className={`${img} object-contain`} alt="" onError={e => {
      ;
      (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
    }} data-id="element-6810" data-mp="3kdkz3v0igeui" />
    </span>;
}
// Claude-style plan flow: shows the picked task as a message, "thinks" for a
// beat, then reveals a numbered plan with the sites it will touch and
// Approve / Make changes actions.
function PlanView({
  prompt,
  onBack
}: {
  prompt: StarterPrompt;
  onBack: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const [phase, setPhase] = useState<'thinking' | 'plan'>(reducedMotion ? 'plan' : 'thinking');
  const [approved, setApproved] = useState(false);
  useEffect(() => {
    if (reducedMotion) {
      setPhase('plan');
      return;
    }
    setPhase('thinking');
    const id = setTimeout(() => setPhase('plan'), 1100);
    return () => clearTimeout(id);
  }, [prompt, reducedMotion]);
  return <div className="flex flex-col" data-id="element-6811" data-mp="3gssony19ukbs">
      {/* Back affordance */}
      <button onClick={onBack} className="self-start flex items-center gap-1 text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors mb-4" data-id="element-6812" data-mp="3lsnd7uxkqxto">
        <ChevronLeft size={14} data-id="element-6813" data-mp="25grta09wjxn2" />
        Back to suggestions
      </button>

      {/* User message bubble */}
      <div className="flex justify-end mb-5" data-id="element-6814" data-mp="2abdujam9s9tf">
        <div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#2B211A] text-white text-sm leading-relaxed px-3.5 py-2.5" data-id="element-6815" data-mp="sfc29d8nm10v">
          {prompt.desc}
        </div>
      </div>

      {/* Working / creating plan */}
      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-4" data-id="element-6816" data-mp="11fig5089l30k">
        {phase === 'thinking' ? <>
            <Loader2 size={15} className="text-[#EA580C] animate-spin" data-id="element-6817" data-mp="xfzsvxsz0fuk" />
            Creating plan…
          </> : <>
            <Sparkles size={15} className="text-[#EA580C]" data-id="element-6818" data-mp="9n9jnwcgcanz" />
            Here&rsquo;s the plan
          </>}
      </div>

      <AnimatePresence data-id="element-6819" data-mp="ihpbnthzsl12">
        {phase === 'plan' && <motion.div initial={reducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: reducedMotion ? 0 : 0.4,
        ease: 'easeOut'
      }} className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-id="element-6820" data-mp="iiqwgpot8lvz">
            {/* Plan header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100" data-id="element-6821" data-mp="3uvpx51fwhaz8">
              <ListChecks size={16} className="text-neutral-500" data-id="element-6822" data-mp="3pf0dhhxawdp" />
              <span className="text-sm font-semibold text-neutral-900" data-id="element-6823" data-mp="8nq9fq0e92uv">
                Giga&rsquo;s plan
              </span>
            </div>

            {/* Sites it will act on */}
            <div className="px-4 py-3 border-b border-neutral-100" data-id="element-6824" data-mp="dfzxt1lv1uv1">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2.5" data-id="element-6825" data-mp="icq818azhw8j">
                <Globe size={12} data-id="element-6826" data-mp="rdy89vx29759" />
                Allow actions on these
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-6827" data-mp="rc50gdapogvo">
                {prompt.sites.map(site => <span key={site.slug} className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-700" data-id="element-6828" data-mp="wc9elwo5cziq">
                    <ToolIcon slug={site.slug} data-id="element-6829" data-mp="2y4ny306cac19" />
                    {site.label}
                  </span>)}
              </div>
            </div>

            {/* Numbered approach */}
            <div className="px-4 py-3.5" data-id="element-6830" data-mp="2ltehqujcik64">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-3" data-id="element-6831" data-mp="2lsbtkudeqvtw">
                Approach to follow
              </div>
              <ol className="flex flex-col gap-3" data-id="element-6832" data-mp="2bxbipxjsj1ne">
                {prompt.steps.map((step, i) => <motion.li key={i} initial={reducedMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: -6
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: reducedMotion ? 0 : 0.3,
              delay: reducedMotion ? 0 : 0.15 + i * 0.08
            }} className="flex items-start gap-3" data-id="element-6833" data-mp="39ipkghk9aomw" data-mpi={i}>
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full border border-neutral-300 flex items-center justify-center text-[11px] font-semibold text-neutral-500" data-id="element-6834" data-mp="1o77blg7e8jrn">
                      {i + 1}
                    </span>
                    <span className="text-sm text-neutral-700 leading-snug" data-id="element-6835" data-mp="b61fmv61rki0">
                      {step}
                    </span>
                  </motion.li>)}
              </ol>
            </div>

            {/* Actions */}
            <div className="px-4 pb-4 pt-1" data-id="element-6836" data-mp="68j7h6zut9b2">
              {approved ? <div className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-[#0F9D58]/10 text-[#0F9D58] text-sm font-semibold" data-id="element-6837" data-mp="66yc5f5ptmc5">
                  <CheckCircle2 size={16} data-id="element-6838" data-mp="3sfr5tpccwfxl" />
                  Plan approved — Giga is on it
                </div> : <div className="flex flex-col gap-2" data-id="element-6839" data-mp="3eoupawvpfp6w">
                  <button onClick={() => setApproved(true)} className="w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-6840" data-mp="3eq5r4r5lamvd">
                    Approve plan
                  </button>
                  <button onClick={onBack} className="w-full flex items-center justify-center gap-2 border border-neutral-200 hover:bg-neutral-50 text-neutral-700 text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-6841" data-mp="39o6oxkll5w00">
                    Make changes
                  </button>
                </div>}
              <p className="text-[11px] text-neutral-400 leading-relaxed mt-3" data-id="element-6842" data-mp="302oxzk1awm1c">
                Giga will only use the tools listed. You&rsquo;ll be asked
                before anything else.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ── Chat context controls: Space + Journey pickers ──────────────────────────
// The "+ Space" and "+ Journey" buttons above a chat open lightweight pickers:
// Space is a compact menu of workspaces with a check on the active one;
// Journey is a searchable popover listing the journeys you can attach. Both
// close on outside-click or Escape.
const CHAT_SPACES = ['Everyone', 'NoContact Ops', 'No Contact Private', 'NoContact Tech – Product & Engineering', 'Giga Master', 'Giga Gtm'];
const CHAT_JOURNEYS = ['Early User Activation', 'Ryan Paid Ads', 'Getting Giga Out There', 'Product Backlog', 'Giga Creator Outreach', 'Getting Operator Stable'];
function ChatContextControls() {
  // Which picker is open, if any.
  const [open, setOpen] = useState<null | 'space' | 'journey'>(null);
  const [activeSpace, setActiveSpace] = useState('Everyone');
  const [journeyQuery, setJourneyQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Close on outside-click and Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  // Focus the journey search when that popover opens.
  useEffect(() => {
    if (open === 'journey') {
      const id = window.setTimeout(() => searchRef.current?.focus(), 20);
      return () => window.clearTimeout(id);
    }
  }, [open]);
  const filteredJourneys = CHAT_JOURNEYS.filter(j => j.toLowerCase().includes(journeyQuery.trim().toLowerCase()));
  const triggerClass = (isOpen: boolean) => `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isOpen ? 'bg-neutral-200 text-neutral-900' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'}`;
  return <div ref={wrapRef} className="relative flex items-center gap-2" data-id="element-6843" data-mp="2shs8fe4zdago">
      {/* Space picker */}
      <div className="relative" data-id="element-6844" data-mp="2nip2lrh6f07n">
        <button onClick={() => setOpen(open === 'space' ? null : 'space')} aria-haspopup="menu" aria-expanded={open === 'space'} className={triggerClass(open === 'space')} data-id="element-6845" data-mp="2ngul6n545q8b">
          <Plus size={14} data-id="element-6846" data-mp="2hazoag0l2xyy" />
          Space
        </button>
        <AnimatePresence data-id="element-6847" data-mp="2h8desau4u8e3">
          {open === 'space' && <motion.div role="menu" initial={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} transition={{
          duration: 0.14,
          ease: 'easeOut'
        }} className="absolute left-0 top-full mt-1.5 z-50 w-72 origin-top-left rounded-xl bg-[#4B463F] text-white shadow-2xl ring-1 ring-black/20 p-1.5" data-id="element-6848" data-mp="2ccoeg5aagngq">
              <button role="menuitem" onClick={() => setOpen(null)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-white/90 hover:bg-white/10 transition-colors" data-id="element-6849" data-mp="1on3qt936x214">
                <Plus size={15} className="text-white/70" data-id="element-6850" data-mp="1fk2n1f1srn0m" />
                New space
              </button>
              <div className="my-1 h-px bg-white/10" data-id="element-6851" data-mp="1amt149dm9thj" />
              {CHAT_SPACES.map(s => {
            const active = activeSpace === s;
            return <button key={s} role="menuitemradio" aria-checked={active} onClick={() => {
              setActiveSpace(s);
              setOpen(null);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/90 hover:bg-white/10 transition-colors text-left" data-id="element-6852" data-mp="3r8pl6f83cot5">
                    <span className="w-4 shrink-0 flex items-center justify-center" data-id="element-6853" data-mp="33jeuxrodact9">
                      {active && <Check size={15} className="text-white" data-id="element-6854" data-mp="37kgu68iaaov8" />}
                    </span>
                    <span className="truncate" data-id="element-6855" data-mp="3cjti6wkw61ze">{s}</span>
                  </button>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Journey picker */}
      <div className="relative" data-id="element-6856" data-mp="3hcmi975f0m55">
        <button onClick={() => setOpen(open === 'journey' ? null : 'journey')} aria-haspopup="dialog" aria-expanded={open === 'journey'} className={triggerClass(open === 'journey')} data-id="element-6857" data-mp="2jvp8lzz794rf">
          <Plus size={14} data-id="element-6858" data-mp="3s565tzjlwifj" />
          Journey
        </button>
        <AnimatePresence data-id="element-6859" data-mp="3s3bmuqrwb0e3">
          {open === 'journey' && <motion.div initial={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} transition={{
          duration: 0.14,
          ease: 'easeOut'
        }} className="absolute left-0 top-full mt-1.5 z-50 w-80 origin-top-left rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 p-2.5" data-id="element-6860" data-mp="3n6l6s3g3c4so">
              <div className="relative mb-1" data-id="element-6861" data-mp="349k10gepq0no">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-6862" data-mp="3i81i3va44ewm" />
                <input ref={searchRef} type="text" value={journeyQuery} onChange={e => setJourneyQuery(e.target.value)} placeholder="Search journeys…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-6863" data-mp="3d7447uw4y70x" />
              </div>
              <div className="max-h-60 overflow-y-auto pt-1 flex flex-col gap-1" data-id="element-6864" data-mp="150iohbbz93gr">
                {filteredJourneys.length === 0 ? <p className="px-3 py-6 text-center text-xs text-neutral-400" data-id="element-6865" data-mp="r1srrwt2l5sx">
                    No journeys match “{journeyQuery}”.
                  </p> : filteredJourneys.map(j => <button key={j} onClick={() => {
              setOpen(null);
              setJourneyQuery('');
            }} className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-800 hover:bg-neutral-100 transition-colors truncate" data-id="element-6866" data-mp="1jon0jwgiygqk">
                      {j}
                    </button>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
type DashboardVariant = 'current' | 'calm-deferred' | 'progressive' | 'chat-first';
export function Dashboard() {
  // DEBUG: Variant switcher for comparing dashboard directions.
  // Defaults to the Progressive Disclosure direction (first in the toggle).
  const [variant, setVariant] = useState<DashboardVariant>('progressive');
  const [isDarkMode, setIsDarkMode] = useState(false);
  // Deep links: ?view=<navId> opens the dashboard directly on that view, so
  // other surfaces (the web chat's sidebar) can link to the right page rather
  // than dumping the user on the generic overview.
  const viewParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
  const [activeNav, setActiveNav] = useState(viewParam || 'overview');
  // ── Sidebar content vs. sidebar settings ────────────────────────────────
  // Tracks and Chats are the things people open all day, so they live in the
  // sidebar as real lists. The CONNECT / MANAGE / BRAIN nav is configuration
  // you visit occasionally, so it collapses behind one disclosure rather than
  // sitting above the content as 12 permanent rows.
  const [tracksExpanded, setTracksExpanded] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [activeTrack, setActiveTrack] = useState<string | null>(null);
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const [starterCat, setStarterCat] = useState<StarterCategory>('comms');
  const [activePlan, setActivePlan] = useState<StarterPrompt | null>(null);
  // Whether the "Create a pulse" slide-over panel is open.
  const [pulseCreateOpen, setPulseCreateOpen] = useState(false);
  // Whether the "Create a journey" slide-over panel is open.
  const [journeyCreateOpen, setJourneyCreateOpen] = useState(false);
  // Whether the "Create an assistant" slide-over panel is open.
  const [assistantCreateOpen, setAssistantCreateOpen] = useState(false);
  // Selected category in the Recipes filter tabs ('All' = no filter).
  const [recipeCategory, setRecipeCategory] = useState('All');
  // Selected function in the Assistants filter tabs ('All' = no filter).
  const [assistantCategory, setAssistantCategory] = useState('All');
  // ── Chat tab strip (right panel) ────────────────────────────────────────
  // The chat panel hosts one or more open chat sessions as closeable tabs.
  // Closing a tab removes it; if it was the active one, focus falls to the
  // neighboring tab. Closing the LAST tab leaves the panel in a calm empty
  // state ("No active chats") with a button to start a fresh one.
  type ChatTab = {
    id: number;
    label: string;
  };
  const [chatTabs, setChatTabs] = useState<ChatTab[]>([{
    id: 1,
    label: 'New Chat'
  }, {
    id: 2,
    label: 'Session 39200'
  }]);
  const [activeChatId, setActiveChatId] = useState<number>(1);
  const nextChatIdRef = useRef(3);
  // In the "calm-deferred" and "chat-first" directions the persistent right
  // chat panel is hidden on load; it only appears once the user explicitly
  // enters a task. This boolean tracks that deferred-entry state.
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  // In the "progressive" direction the overview swings between a first-time
  // guided setup and the everyday returning dashboard. We lift that state up
  // here so the right chat panel can stay hidden during first-time setup —
  // a brand-new user shouldn't be greeted by a chat they haven't earned yet.
  const [progressiveState, setProgressiveState] = useState<'first' | 'returning'>('first');
  const openNewChat = () => {
    const id = nextChatIdRef.current++;
    setChatTabs(tabs => [...tabs, {
      id,
      label: 'New Chat'
    }]);
    setActiveChatId(id);
    setActivePlan(null);
    setChatPanelOpen(true);
  };
  const closeChat = (id: number) => {
    setChatTabs(tabs => {
      const idx = tabs.findIndex(t => t.id === id);
      const next = tabs.filter(t => t.id !== id);
      // If the closed tab was active, move focus to the neighbor (prefer the
      // tab to the left, falling back to the new first tab).
      if (id === activeChatId) {
        const fallback = next[idx - 1] ?? next[0];
        setActiveChatId(fallback ? fallback.id : -1);
        setActivePlan(null);
      }
      if (next.length === 0) {
        setChatPanelOpen(false);
      }
      return next;
    });
  };
  // Integrations tab controls
  const [intView, setIntView] = useState<'app' | 'list'>('app');
  const [intTab, setIntTab] = useState<'all' | 'popular' | 'connected'>('all');
  const [intQuery, setIntQuery] = useState('');
  // Activity log filters
  const [activityType, setActivityType] = useState<ActivityType | 'all'>('all');
  const [activityStatus, setActivityStatus] = useState<ActivityStatus | 'all'>('all');
  // Timing filter: which date buckets to include in the Activity feed.
  const [activityRange, setActivityRange] = useState<ActivityRange>('all');
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  // Bottom-left account menu + the billing / manage-subscription modal it opens.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const navGroups = [{
    items: [{
      id: 'overview',
      label: 'Home',
      icon: LayoutGrid
    }]
  }, {
    label: 'CONNECT',
    items: [{
      id: 'integrations',
      label: 'Integrations',
      icon: Plug
    }, {
      id: 'power-ups',
      label: 'Power Ups',
      icon: Zap
    }]
  }, {
    label: 'MANAGE',
    items: [{
      id: 'groups',
      label: 'Groups',
      icon: Layers
    }, {
      id: 'spaces-tracks',
      label: 'Tracks',
      icon: Map
    }, {
      id: 'assistants',
      label: 'Assistants',
      icon: UserCircle
    }, {
      id: 'skills',
      label: 'Skills',
      icon: GraduationCap
    }, {
      id: 'journeys',
      label: 'Workflows',
      icon: ListChecks
    }, {
      id: 'pulses',
      label: 'Pulses',
      icon: Activity
    }, {
      id: 'conversations',
      label: 'Chats',
      icon: MessageSquare
    }]
  }, {
    label: 'BRAIN',
    items: [{
      id: 'files',
      label: 'Files',
      icon: FolderOpen
    }, {
      id: 'tables',
      label: 'Tables',
      icon: Layers
    }, {
      id: 'viewer',
      label: 'Viewer',
      icon: Monitor
    }]
  }];
  // Everything except Home lives behind the "Manage workspace" disclosure —
  // Tracks and Chats are surfaced above as real content lists instead.
  const managedNavGroups = navGroups.filter(g => !!g.label);
  const managedNavActive = managedNavGroups.some(g => g.items.some(i => i.id === activeNav)) && !activeTrack && !activeChat;
  // Active tracks only, alphabetical — the sidebar is a shortcut list, not the
  // full filterable library (that's the Tracks view).
  const sidebarTracks = TRACKS.filter(t => t.status === 'active').slice().sort((a, b) => a.name.localeCompare(b.name));
  const integrations = [{
    name: 'AgentMail',
    desc: 'productivity',
    icon: 'gmail',
    connected: false
  }, {
    name: 'Airtable',
    desc: 'productivity',
    icon: 'airtable',
    connected: false
  }, {
    name: 'Anthropic',
    desc: 'Claude models and the Anthropic API.',
    icon: 'anthropic',
    connected: false
  }, {
    name: 'Asana',
    desc: '1 account connected',
    icon: 'asana',
    connected: true
  }, {
    name: 'Attio',
    desc: 'sales',
    icon: 'attio',
    connected: false
  }, {
    name: 'Cloudflare',
    desc: 'infrastructure',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Cloudflare R2',
    desc: 'S3-compatible object storage with …',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Crisp',
    desc: '1 account connected',
    icon: 'crisp',
    connected: true
  }, {
    name: 'Custom',
    desc: 'Credentials for services without a d…',
    icon: 'box',
    connected: false
  }, {
    name: 'Expo',
    desc: '1 account connected',
    icon: 'expo',
    connected: true
  }, {
    name: 'Facebook',
    desc: 'marketing',
    icon: 'facebook',
    connected: false
  }, {
    name: 'Folk',
    desc: 'sales',
    icon: 'box',
    connected: false
  }, {
    name: 'GitHub',
    desc: '2 accounts connected',
    icon: 'github',
    connected: true
  }, {
    name: 'Google Gemini',
    desc: 'ai',
    icon: 'googlegemini',
    connected: false
  }, {
    name: 'Google Workspace',
    desc: 'Gmail, Drive, Calendar, Docs, Sheets.',
    icon: 'google',
    connected: false
  }, {
    name: 'Granola',
    desc: 'AI meeting notepad. Search transcri…',
    icon: 'box',
    connected: false
  }, {
    name: 'Growi',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Heroku',
    desc: 'infrastructure',
    icon: 'heroku',
    connected: false
  }, {
    name: 'Linear',
    desc: 'Issue tracking and project managem…',
    icon: 'linear',
    connected: false
  }, {
    name: 'Magic Patterns',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Meta Ads',
    desc: 'Facebook + Instagram ad managem…',
    icon: 'meta',
    connected: false
  }, {
    name: 'Notion',
    desc: 'Pages, databases, and structured d…',
    icon: 'notion',
    connected: false
  }, {
    name: 'OpenAI',
    desc: 'GPT models and the OpenAI API.',
    icon: 'openai',
    connected: false
  }, {
    name: 'OpenRouter',
    desc: 'ai',
    icon: 'box',
    connected: false
  }, {
    name: 'Postgres',
    desc: 'Open-source relational database.',
    icon: 'postgresql',
    connected: false
  }, {
    name: 'RevenueCat',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Sentry',
    desc: '1 account connected',
    icon: 'sentry',
    connected: true
  }];
  // The handful most teams connect first — a curated discovery shortlist so the
  // long list isn't the only way in. Matched by name against `integrations`.
  const POPULAR_INTEGRATIONS = new Set(['Google Workspace', 'Slack', 'AgentMail', 'Notion', 'GitHub', 'Asana', 'Linear', 'Meta Ads', 'Airtable', 'Attio']);
  return (
    // Flat, calm product surface. We intentionally avoid the
    // `min-h-screen bg-[#FAF8F4]` combination here: a global rule in index.css
    // stamps the marketing halftone dot-texture onto any such container, which
    // made the dashboard read "old school." `h-screen` + an explicit cream
    // canvas keeps the app chrome clean and modern. Using the brand cream
    // (#FAF8F4) — lighter than the previous #F4F2EE — so it reads as a very
    // subtle warm accent rather than a heavy tint.
    <div className={`h-screen bg-[#FAF8F4] flex font-body overflow-hidden ${isDarkMode ? 'dark-dashboard' : ''}`} data-id="element-6867" data-mp="jslxeypd5ey6">
      {isDarkMode && <style data-id="element-6868" data-mp="a0zcubr4y3ir">{`
          .dark-dashboard {
            color-scheme: dark;
          }
          .dark-dashboard .bg-white { background-color: #160F0A !important; }
          .dark-dashboard .bg-\\[\\#FAF8F4\\] { background-color: #1A120C !important; }
          .dark-dashboard .bg-neutral-50 { background-color: #1A120C !important; }
          .dark-dashboard .bg-neutral-100 { background-color: #2B211A !important; }
          .dark-dashboard .bg-neutral-200 { background-color: #6B5D52 !important; }
          .dark-dashboard .bg-\\[\\#2B211A\\] { background-color: #EAE4D9 !important; }
          
          .dark-dashboard .text-neutral-900 { color: #F4F0EA !important; }
          .dark-dashboard .text-neutral-800 { color: #EAE4D9 !important; }
          .dark-dashboard .text-neutral-700 { color: #E7E0D7 !important; }
          .dark-dashboard .text-neutral-600 { color: #9C8E82 !important; }
          .dark-dashboard .text-neutral-500 { color: #9C8E82 !important; }
          .dark-dashboard .text-neutral-400 { color: #6B5D52 !important; }
          .dark-dashboard .text-\\[\\#2B211A\\] { color: #F4F0EA !important; }
          .dark-dashboard .text-white { color: #160F0A !important; }
          
          .dark-dashboard .border-neutral-100 { border-color: #1A120C !important; }
          .dark-dashboard .border-neutral-200 { border-color: #2B211A !important; }
          .dark-dashboard .border-neutral-300 { border-color: #6B5D52 !important; }
          
          .dark-dashboard .hover\\:bg-neutral-50:hover { background-color: #1A120C !important; }
          .dark-dashboard .hover\\:bg-neutral-100:hover { background-color: #2B211A !important; }
          .dark-dashboard .hover\\:bg-neutral-200:hover { background-color: #6B5D52 !important; }
          .dark-dashboard .hover\\:bg-\\[\\#1A1410\\]:hover { background-color: #D5CFC5 !important; }
          
          .dark-dashboard .hover\\:text-neutral-900:hover { color: #F4F0EA !important; }
          .dark-dashboard .hover\\:text-neutral-700:hover { color: #EAE4D9 !important; }
          .dark-dashboard .hover\\:text-neutral-600:hover { color: #E7E0D7 !important; }
          .dark-dashboard .hover\\:text-\\[\\#2B211A\\]:hover { color: #F4F0EA !important; }
          
          /* Fix simpleicons inversion in dark mode */
          .dark-dashboard img[src*="simpleicons.org"] { filter: brightness(0) invert(1) opacity(0.8); }
        `}</style>}
      {/* Left Sidebar */}
      <div className="w-[240px] bg-white border-r border-neutral-200 flex flex-col shrink-0 h-screen" data-id="element-6869" data-mp="1hbaqgfs8r3q9">
        {/* Workspace Switcher */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors" data-id="element-6870" data-mp="18b5lecaishf9">
          <div className="flex items-center gap-2.5" data-id="element-6871" data-mp="13bt0vy74fmfu">
            <div className="w-6 h-6 rounded bg-[#2B211A] flex items-center justify-center text-white text-xs font-bold" data-id="element-6872" data-mp="ydrh1b1vfj3b">
              G
            </div>
            <span className="font-semibold text-sm text-[#2B211A]" data-id="element-6873" data-mp="tkermypdb89i">
              Giga Main
            </span>
          </div>
          <ChevronDown size={14} className="text-neutral-400" data-id="element-6874" data-mp="kltfpmeeirhp" />
        </div>

        {/* Nav — content first (Tracks, Chats), configuration collapsed below */}
        <div className="flex-1 py-3 px-3 flex flex-col gap-4 overflow-y-auto" data-id="element-6875" data-mp="fm6w8v7zcf0g">
          {/* Home */}
          <button onClick={() => {
            setActiveNav('overview');
            setActiveTrack(null);
            setActiveChat(null);
          }} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeNav === 'overview' && !activeTrack && !activeChat ? 'bg-[#2B211A] text-white' : 'text-neutral-600 hover:bg-neutral-100 hover:text-[#2B211A]'}`} data-id="element-6876" data-mp="aof7ub6jnb10">
            <LayoutGrid size={18} strokeWidth={activeNav === 'overview' ? 2.5 : 2} data-id="element-6877" data-mp="1uh6qfocem5mp" />
            Home
          </button>

          {/* Tracks — the working library. Capped at 12 with a Show more, so a
           long list never pushes Chats out of view. */}
          <div className="flex flex-col gap-0.5" data-id="element-6878" data-mp="16tp1e0kixlnf">
            <div className="flex items-center justify-between px-3 py-1" data-id="element-6879" data-mp="16v037uuesjbw">
              <button type="button" onClick={() => setActiveNav('spaces-tracks')} className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-6880" data-mp="1avk51bpxgb4d">
                Tracks
              </button>
              <button type="button" onClick={() => setActiveNav('spaces-tracks')} aria-label="All tracks" className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-6881" data-mp="i8alybhxry3f">
                <Plus size={13} data-id="element-6882" data-mp="db0ms44qw82e" />
              </button>
            </div>
            {sidebarTracks.slice(0, tracksExpanded ? sidebarTracks.length : 12).map(t => {
              const isActive = activeTrack === t.name;
              return <button key={t.name} onClick={() => {
                setActiveTrack(t.name);
                setActiveChat(null);
                setActiveNav('spaces-tracks');
              }} title={t.name} className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13.5px] transition-colors ${isActive ? 'bg-[#2B211A] text-white font-semibold' : 'text-neutral-600 hover:bg-neutral-100 hover:text-[#2B211A] font-medium'}`} data-id="element-6883" data-mp="117529ox1t6lp">
                  <Folder size={14} className={isActive ? 'text-white shrink-0' : 'text-neutral-400 shrink-0'} data-id="element-6884" data-mp="1jwuv46h93iaz" />
                  <span className="truncate" data-id="element-6885" data-mp="1fx2hhfd02lyr">{t.name}</span>
                </button>;
            })}
            {sidebarTracks.length > 12 && <button type="button" onClick={() => setTracksExpanded(v => !v)} className="px-3 py-1.5 text-left text-[13px] font-medium text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-6886" data-mp="1nlv1n01ryk9b">
                {tracksExpanded ? 'Show less' : `Show ${sidebarTracks.length - 12} more`}
              </button>}
          </div>

          {/* Chats */}
          <div className="flex flex-col gap-0.5" data-id="element-6887" data-mp="26btb9kl80lf2">
            <div className="flex items-center justify-between px-3 py-1" data-id="element-6888" data-mp="26a8d7kzp6kcg">
              <button type="button" onClick={() => setActiveNav('conversations')} className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-6889" data-mp="21byjletmfrsy">
                Chats
              </button>
              <button type="button" onClick={() => setActiveNav('conversations')} aria-label="New chat" className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-6890" data-mp="2b9bjt9vbsymj">
                <Plus size={13} data-id="element-6891" data-mp="gypt17v18ked" />
              </button>
            </div>
            {SIDEBAR_CHATS.map(c => {
              const isActive = activeChat === c;
              return <button key={c} onClick={() => {
                setActiveChat(c);
                setActiveTrack(null);
                setActiveNav('conversations');
              }} title={c} className={`flex items-center px-3 py-1.5 rounded-lg text-[13.5px] transition-colors ${isActive ? 'bg-[#2B211A] text-white font-semibold' : 'text-neutral-600 hover:bg-neutral-100 hover:text-[#2B211A] font-medium'}`} data-id="element-6892" data-mp="2xjusexxg72eh">
                  <span className="truncate" data-id="element-6893" data-mp="2dvucqeo47zpn">{c}</span>
                </button>;
            })}
          </div>

          {/* Everything else — one disclosure instead of three always-open
           groups. Opens automatically when one of its views is active. */}
          <div className="mt-auto border-t border-neutral-200 pt-3" data-id="element-6894" data-mp="24wqsh7d7pe9k">
            <button type="button" onClick={() => setManageOpen(o => !o)} aria-expanded={manageOpen} className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-6895" data-mp="3uadi46vfcla1">
              <span className="flex items-center gap-3" data-id="element-6896" data-mp="3srn8515g13sz">
                <Settings size={18} strokeWidth={2} data-id="element-6897" data-mp="3ntlu4umvjuod" />
                Manage workspace
              </span>
              <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${manageOpen || managedNavActive ? 'rotate-180' : ''}`} data-id="element-6898" data-mp="3iur0exndclph" />
            </button>
            <AnimatePresence initial={false} data-id="element-6899" data-mp="kepsoji9ax4l">
              {(manageOpen || managedNavActive) && <motion.div key="manage-nav" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-6900" data-mp="fek4jdb73sr9">
                  <div className="flex flex-col gap-3 pt-2" data-id="element-6901" data-mp="yajqo7hhs22x">
                    {managedNavGroups.map((group, i) => <div key={i} className="flex flex-col gap-0.5" data-id="element-6902" data-mp="16ndbtly93f9k" data-mpi={i}>
                        {group.label && <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-6903" data-mp="1andyftvi6wrp">
                            {group.label}
                          </div>}
                        {group.items.map(item => {
                      const isActive = activeNav === item.id && !activeTrack && !activeChat;
                      return <button key={item.id} onClick={() => {
                        setActiveNav(item.id);
                        setActiveTrack(null);
                        setActiveChat(null);
                      }} className={`flex items-center gap-3 rounded-lg px-3 py-1.5 text-[13.5px] font-medium transition-colors ${isActive ? 'bg-[#2B211A] text-white' : 'text-neutral-600 hover:bg-neutral-100 hover:text-[#2B211A]'}`} data-id="element-6904" data-mp="1tlq9xm9w0iqg">
                              <item.icon size={16} strokeWidth={isActive ? 2.5 : 2} data-id="element-6905" data-mp="2d8xnxiz4bm40" />
                              {item.label}
                            </button>;
                    })}
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* User Profile — clicking the row opens an account menu (above it)
            with a cog affordance and access to Billing / manage subscription. */}
        <div className="relative p-4 border-t border-neutral-200" data-id="element-6906" data-mp="2r2051hgspkuy">
          <button type="button" onClick={() => setAccountMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={accountMenuOpen} className={`flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 transition-colors ${accountMenuOpen ? 'bg-neutral-100' : 'hover:bg-neutral-100'}`} data-id="element-6907" data-mp="2r4cj3jjfnb2q">
            
            <span className="flex items-center gap-3 min-w-0" data-id="element-6908" data-mp="2w3pe4rkgjoge">
              <span className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-bold text-xs shrink-0" data-id="element-6909" data-mp="fb4fsav09p6b">
                F
              </span>
              <span className="flex flex-col min-w-0 text-left" data-id="element-6910" data-mp="itljfktum13u">
                <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-6911" data-mp="ntg95l1w0knb">
                  Faith Alqahtani
                </span>
                <span className="text-xs text-neutral-500 truncate" data-id="element-6912" data-mp="8xqru07ncvox">
                  faith@example.com
                </span>
              </span>
            </span>
            <ChevronsUpDown size={15} className={`shrink-0 transition-colors ${accountMenuOpen ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-6913" data-mp="11kkzfwjd80kz" />
            
          </button>

          <AnimatePresence data-id="element-6914" data-mp="16m1x7j6jz6xc">
            {accountMenuOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setAccountMenuOpen(false)} data-id="element-6915" data-mp="rurzxtwhzbx3" />
              
                <motion.div role="menu" initial={{
                opacity: 0,
                y: 6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 6,
                scale: 0.98
              }} transition={{
                duration: 0.14,
                ease: 'easeOut'
              }} className="absolute bottom-[calc(100%-0.5rem)] left-4 right-4 z-50 origin-bottom overflow-hidden rounded-xl border border-neutral-200 bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.5)]" data-id="element-6916" data-mp="wsl6wp5mk9yf">
                
                  <button role="menuitem" type="button" onClick={() => {
                  setAccountMenuOpen(false);
                  setBillingOpen(true);
                }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#2B211A] transition-colors hover:bg-neutral-100" data-id="element-6917" data-mp="1g83adcnitrfu">
                  
                    <CreditCard size={16} className="text-neutral-500" data-id="element-6918" data-mp="12ebyvyq8bxhh" />
                    Usage &amp; billing
                  </button>
                  <button role="menuitem" type="button" onClick={() => setAccountMenuOpen(false)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#2B211A] transition-colors hover:bg-neutral-100" data-id="element-6919" data-mp="shpf7d9n3ch1">
                  
                    <Settings size={16} className="text-neutral-500" data-id="element-6920" data-mp="396i9o6x5eoxz" />
                    Settings
                  </button>
                  <div className="my-1 h-px bg-neutral-100" data-id="element-6921" data-mp="3i5vicxdg79hn" />
                  <button role="menuitem" type="button" onClick={() => setAccountMenuOpen(false)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100" data-id="element-6922" data-mp="3i39aewmnaryw">
                  
                    <LogOut size={16} className="text-neutral-500" data-id="element-6923" data-mp="2uezmnp5vfu54" />
                    Log out
                  </button>
                </motion.div>
              </>}
          </AnimatePresence>
        </div>
      </div>

      {/* Billing / manage-subscription modal */}
      <BillingModal open={billingOpen} onClose={() => setBillingOpen(false)} data-id="element-6924" data-mp="26pougw2l782l" />

      {/* Main Content Column */}
      <div className="flex-1 flex flex-col min-w-0 h-screen" data-id="element-6925" data-mp="103xhi8hdv64y">
        {/* Top Bar */}
        <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-6 shrink-0" data-id="element-6926" data-mp="v4kv1oufc12w">
          <div className="flex items-center gap-4" data-id="element-6927" data-mp="1a1tr2vuo9t0y">
            <button className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors px-3 py-1.5 rounded-md hover:bg-neutral-100" data-id="element-6928" data-mp="151z06sawd3fw">
              <LayoutGrid size={16} className="text-neutral-400" data-id="element-6929" data-mp="1iznjhvpbg2iu" />
              All Spaces
              <ChevronDown size={14} className="text-neutral-400" data-id="element-6930" data-mp="1e0kqj9mm1mr8" />
            </button>
            <div className="w-px h-4 bg-neutral-200" data-id="element-6931" data-mp="1sr09ehrk0im8" />
            <button className="text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-6932" data-mp="1nrx898ep2wjj">
              <Bookmark size={18} data-id="element-6933" data-mp="21ryg57tdn48i" />
            </button>
          </div>
          <div className="flex items-center gap-3" data-id="element-6934" data-mp="21pvj43tt4aua">
            <div className="relative" data-id="element-6935" data-mp="1xro0f3fs8zgg">
              <button onClick={() => setWhatsNewOpen(!whatsNewOpen)} className={`p-2 transition-colors rounded-lg ${whatsNewOpen ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'}`} aria-label="What's new" data-id="element-6936" data-mp="1xojrxtvabhjz">
                <div className="relative" data-id="element-6937" data-mp="27d7777gdjpdb">
                  <Bell size={18} data-id="element-6938" data-mp="27gl8sdh7rujp" />
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#EA580C] border border-white" data-id="element-6939" data-mp="2ccts832g29wo" />
                </div>
              </button>
              <AnimatePresence data-id="element-6940" data-mp="1xmmajpk9gux1">
                {whatsNewOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setWhatsNewOpen(false)} data-id="element-6941" data-mp="22gqh47pm70xl" />
                    <motion.div initial={{
                    opacity: 0,
                    y: 4,
                    scale: 0.98
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: 4,
                    scale: 0.98
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute right-0 top-full mt-2 w-80 z-50 bg-white rounded-xl shadow-xl border border-neutral-200 overflow-hidden" data-id="element-6942" data-mp="1opc026f9p9t6">
                      <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50" data-id="element-6943" data-mp="1q3rtrgcr5xes">
                        <h3 className="text-sm font-semibold text-neutral-900" data-id="element-6944" data-mp="1uyfc0xh5xvkx">
                          What's new
                        </h3>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto p-2 flex flex-col gap-1" data-id="element-6945" data-mp="24x4rykpi15yj">
                        {WHATS_NEW.map((n, i) => <div key={i} className="flex items-start gap-3 rounded-lg hover:bg-neutral-50 px-3 py-2.5 transition-colors cursor-pointer" data-id="element-6946" data-mp="29ris10f3w19j" data-mpi={i}>
                            <span className={`shrink-0 mt-0.5 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${n.tag === 'New' ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-6947" data-mp="2iqvtp6wznljp">
                              {n.tag}
                            </span>
                            <div data-id="element-6948" data-mp="2strl8seqyfy3">
                              <div className="text-sm font-semibold text-neutral-900" data-id="element-6949" data-mp="2sspyhqodc50j">
                                {n.title}
                              </div>
                              <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed" data-id="element-6950" data-mp="2ju4hhzyjoy1v">
                                {n.detail}
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors rounded-lg hover:bg-neutral-100" aria-label="Toggle dark mode" data-id="element-6951" data-mp="32s71u1g5e6tc">
              {isDarkMode ? <Sun size={18} data-id="element-6952" data-mp="3uiv2zdhtnc70" /> : <Moon size={18} data-id="element-6953" data-mp="3uibwcsxmga8a" />}
            </button>
            <button onClick={openNewChat} className="flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-6954" data-mp="3uhjy1acn3yeb">
              <Plus size={16} data-id="element-6955" data-mp="1xn0emo47iqyo" />
              New chat
            </button>
          </div>
        </header>

        {/* Dashboard Content Area */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-6956" data-mp="1nuck600p8z34">
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm min-h-full p-8" data-id="element-6957" data-mp="1nsrryh1uxs8f">
            {/* Overview — rendered per the active debug variant. */}
            {activeNav === 'overview' && variant === 'current' && <CurrentOverview data-id="element-6958" data-mp="zxp0s2fkzcos" />}
            {activeNav === 'overview' && variant === 'calm-deferred' && <CommandCenterOverview onStartTask={openNewChat} onNavigate={setActiveNav} data-id="element-6959" data-mp="14w0a8c1yrl6h" />}
            {activeNav === 'overview' && variant === 'progressive' && <EmptyStateOverview onStartTask={openNewChat} onNavigate={setActiveNav} data-id="element-6960" data-mp="2ubpxohrklpm8" />}
            {activeNav === 'overview' && variant === 'chat-first' && <ChatFirstOverview onStartTask={openNewChat} data-id="element-6961" data-mp="nmxt5swfrrig" />}

            {/* Activity */}
            {activeNav === 'activity' && <ActivityView typeFilter={activityType} setTypeFilter={setActivityType} statusFilter={activityStatus} setStatusFilter={setActivityStatus} rangeFilter={activityRange} setRangeFilter={setActivityRange} data-id="element-6962" data-mp="iocysa3v821x" />}

            {/* Integrations */}
            {activeNav === 'integrations' && <div className="flex flex-col" data-id="element-6963" data-mp="16d5o3npzfbnk">
                <div className="flex items-center justify-between mb-6" data-id="element-6964" data-mp="16bulhr89w9y1">
                  <div className="flex items-center gap-4" data-id="element-6965" data-mp="12clqkj1hy60w">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-6966" data-mp="12e6kq7jwfmy8">
                      Integrations
                    </h1>
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-6967" data-mp="1bc8v3dpivry6">
                      <button onClick={() => setIntView('app')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${intView === 'app' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-6968" data-mp="3i2gewtmc7492">
                        App View
                      </button>
                      <button onClick={() => setIntView('list')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${intView === 'list' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-6969" data-mp="3jjce3durg9zr">
                        List View
                      </button>
                    </div>
                  </div>
                  <CustomCredentialFlow data-id="element-6970" data-mp="2vsq4hi53yhnn" />
                </div>

                <div className="relative mb-6" data-id="element-6971" data-mp="30ufjt9zd5hhj">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-6972" data-mp="35sqtnkpnrrzr" />
                  <input type="text" value={intQuery} onChange={e => setIntQuery(e.target.value)} placeholder={`Search ${integrations.length} integrations`} className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-neutral-200 bg-white text-sm focus:outline-none focus:border-neutral-300 transition-colors" data-id="element-6973" data-mp="3aj77v71azhn6" />
                </div>

                <div className="flex items-center justify-between mb-6 border-b border-neutral-100" data-id="element-6974" data-mp="z6qmh0dwzb3s">
                  <div className="flex items-center gap-6" data-id="element-6975" data-mp="v7rlfqvazyhm">
                    <button onClick={() => setIntTab('all')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'all' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-6976" data-mp="q73lgfp3k7b9">
                      All integrations{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-6977" data-mp="gb0kve9w8gqf">
                        {integrations.length}
                      </span>
                    </button>
                    <button onClick={() => setIntTab('popular')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'popular' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-6978" data-mp="7h3x5v12yw8w">
                      Popular{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-6979" data-mp="3oqah45r3qrqa">
                        {integrations.filter(x => POPULAR_INTEGRATIONS.has(x.name)).length}
                      </span>
                    </button>
                    <button onClick={() => setIntTab('connected')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'connected' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-6980" data-mp="3k1rgsdr9v6l2">
                      Connected{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-6981" data-mp="1n0j5h1uh3v1">
                        {integrations.filter(x => x.connected).length}
                      </span>
                    </button>
                  </div>
                </div>

                {(() => {
                const visible = integrations.filter(x => {
                  if (intTab === 'connected' && !x.connected) return false;
                  if (intTab === 'popular' && !POPULAR_INTEGRATIONS.has(x.name)) return false;
                  if (intQuery.trim() && !x.name.toLowerCase().includes(intQuery.toLowerCase())) return false;
                  return true;
                });
                if (visible.length === 0) {
                  return <div className="border border-neutral-200 rounded-xl px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-6982" data-mp="251awmr3si9jh">
                        <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-6983" data-mp="2esndyoryphb9">
                          <Search size={18} className="text-neutral-400" data-id="element-6984" data-mp="1r3unv8y0u3ok" />
                        </div>
                        <p className="text-sm font-semibold text-neutral-700" data-id="element-6985" data-mp="1m6ca959270a8">
                          No integrations found
                        </p>
                        <p className="text-xs text-neutral-500 mt-1" data-id="element-6986" data-mp="1w2foval3kre2">
                          Try a different search or switch back to all
                          integrations.
                        </p>
                      </div>;
                }
                if (intView === 'list') {
                  return <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-6987" data-mp="14vyomk29huu1">
                        <div className="divide-y divide-neutral-100" data-id="element-6988" data-mp="19tqrg94g31d0">
                          {visible.map((integration, i) => <div key={i} className="flex items-center gap-4 px-5 py-3.5 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-6989" data-mp="1et3byn7ufwcf" data-mpi={i}>
                              <div className="w-9 h-9 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-6990" data-mp="1jledfui8fq73">
                                <img src={`https://cdn.simpleicons.org/${integration.icon}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                            ;
                            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                          }} data-id="element-6991" data-mp="1sk8dxvyop0px" />
                              </div>
                              <span className="font-semibold text-neutral-900 text-sm shrink-0 w-44 truncate" data-id="element-6992" data-mp="386c838a6eec">
                                {integration.name}
                              </span>
                              <span className="text-xs text-neutral-500 truncate flex-1" data-id="element-6993" data-mp="n8emq9mr0a5">
                                {integration.desc}
                              </span>
                              {integration.connected ? <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-6994" data-mp="3i1wzebng3brv">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-6995" data-mp="jjhw7njqq8yw" />
                                  Connected
                                </span> : <span className="text-[11px] font-medium text-neutral-400 px-2 py-0.5 shrink-0" data-id="element-6996" data-mp="9lkab6pcso9g">
                                  Not connected
                                </span>}
                            </div>)}
                        </div>
                      </div>;
                }
                return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6997" data-mp="2pcyej6rxtviv">
                      {visible.map((integration, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-neutral-200 hover:border-neutral-300 transition-colors cursor-pointer bg-white" data-id="element-6998" data-mp="1p6inooo8ssn0" data-mpi={i}>
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-6999" data-mp="1fgz7xrvttewd">
                            <img src={`https://cdn.simpleicons.org/${integration.icon}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                        ;
                        (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                      }} data-id="element-7000" data-mp="1af01ua8pc3vq" />
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-7001" data-mp="1u3scc23t509s">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-7002" data-mp="mra1gz0ckmnd">
                              {integration.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-7003" data-mp="irrgy4cfudl2">
                              {integration.connected && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-7004" data-mp="2ub78soqub01u" />}
                              <span className="text-xs text-neutral-500 truncate" data-id="element-7005" data-mp="2yc9815krbc3t">
                                {integration.desc}
                              </span>
                            </div>
                          </div>
                        </div>)}
                    </div>;
              })()}
              </div>}

            {/* Conversations */}
            {activeNav === 'conversations' && <ConversationsView data-id="element-7006" data-mp="3i4pavt9kmov0" />}

            {/* Assistants */}
            {activeNav === 'assistants' && (() => {
              // Assistants are grouped by the business FUNCTION they serve
              // (Sales, Marketing, Support, …) — that's how teams reason
              // about who does what — plus a richer card: what it actually
              // does, the tools it works in, and how much it's handled. The
              // function tabs default to "All", mirroring the Recipes filter.
              const ASSISTANTS = [{
                name: 'Sales Assistant',
                desc: 'Handles outbound, logs every touchpoint, and keeps the CRM clean so reps focus on selling.',
                initial: 'S',
                color: 'bg-amber-100 text-amber-700',
                active: true,
                func: 'Sales',
                tasks: '1.2k tasks',
                tools: ['salesforce', 'gmail', 'slack']
              }, {
                name: 'Pipeline Watcher',
                desc: 'Flags stalled deals and at-risk opportunities before the weekly sync.',
                initial: 'P',
                color: 'bg-amber-100 text-amber-700',
                active: true,
                func: 'Sales',
                tasks: '430 tasks',
                tools: ['hubspot', 'slack']
              }, {
                name: 'Content Writer',
                desc: 'Drafts posts, emails, and landing copy in your brand voice, ready for review.',
                initial: 'C',
                color: 'bg-purple-100 text-purple-700',
                active: true,
                func: 'Marketing',
                tasks: '860 tasks',
                tools: ['notion', 'gmail']
              }, {
                name: 'Campaign Analyst',
                desc: 'Pulls campaign metrics into shareable performance recaps.',
                initial: 'C',
                color: 'bg-purple-100 text-purple-700',
                active: false,
                func: 'Marketing',
                tasks: '210 tasks',
                tools: ['mailchimp', 'meta']
              }, {
                name: 'Support Triage',
                desc: 'Tags incoming tickets, drafts replies from help docs, and escalates the rest.',
                initial: 'S',
                color: 'bg-sky-100 text-sky-700',
                active: true,
                func: 'Support',
                tasks: '2.4k tasks',
                tools: ['zendesk', 'slack']
              }, {
                name: 'Finance Analyst',
                desc: 'Reconciles payments to invoices, categorizes expenses, and builds cash snapshots.',
                initial: 'F',
                color: 'bg-rose-100 text-rose-700',
                active: true,
                func: 'Finance',
                tasks: '540 tasks',
                tools: ['stripe', 'quickbooks']
              }, {
                name: 'Research Bot',
                desc: 'Finds and summarizes market and account data on demand.',
                initial: 'R',
                color: 'bg-emerald-100 text-emerald-700',
                active: true,
                func: 'Operations',
                tasks: '690 tasks',
                tools: ['notion', 'google']
              }, {
                name: 'Ops Manager',
                desc: 'Monitors workflows across tools and flags anything that breaks or stalls.',
                initial: 'O',
                color: 'bg-blue-100 text-blue-700',
                active: false,
                func: 'Operations',
                tasks: '310 tasks',
                tools: ['linear', 'slack']
              }, {
                name: 'PR Summarizer',
                desc: 'Digests new pull requests and posts a daily summary to the team.',
                initial: 'P',
                color: 'bg-indigo-100 text-indigo-700',
                active: true,
                func: 'Engineering',
                tasks: '980 tasks',
                tools: ['github', 'slack']
              }];
              const FUNCTIONS = ['All', ...Array.from(new Set(ASSISTANTS.map(a => a.func)))];
              const visible = assistantCategory === 'All' ? ASSISTANTS : ASSISTANTS.filter(a => a.func === assistantCategory);
              return <div className="flex flex-col" data-id="element-7007" data-mp="1m4xffdi8gcl5">
                    <div className="flex items-start justify-between gap-4 mb-5" data-id="element-7008" data-mp="1m68mpnaw02h0">
                      <div data-id="element-7009" data-mp="1r1e4c9x9auza">
                        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7010" data-mp="11f3bav61ferp">
                          Assistants
                        </h1>
                        <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-7011" data-mp="weyuf417wi0y">
                          Dedicated AI teammates, each focused on one part of
                          the business — with their own instructions, tools, and
                          memory. Filter by function to find the right one.
                        </p>
                      </div>
                      <button onClick={() => setAssistantCreateOpen(true)} className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-7012" data-mp="dqabgnmaomrt">
                        <Plus size={16} data-id="element-7013" data-mp="nm3tgmis98gz" />
                        New assistant
                      </button>
                    </div>

                    {/* Function filter tabs — defaults to "All" */}
                    <div role="tablist" aria-label="Filter assistants by function" className="flex flex-wrap gap-2 mb-6" data-id="element-7014" data-mp="3r45b1ne1xxkc">
                      {FUNCTIONS.map(f => {
                    const count = f === 'All' ? ASSISTANTS.length : ASSISTANTS.filter(a => a.func === f).length;
                    const active = assistantCategory === f;
                    return <button key={f} role="tab" aria-selected={active} onClick={() => setAssistantCategory(f)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-[#2B211A] text-white shadow-sm' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-7015" data-mp="21mqj359jobsu">
                            {f}
                            <span className={`ml-1.5 ${active ? 'text-white/60' : 'text-neutral-400'}`} data-id="element-7016" data-mp="2gbecp5ztybg5">
                              {count}
                            </span>
                          </button>;
                  })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7017" data-mp="18ycl2pighfha">
                      {visible.map((a, i) => <div key={i} className="flex flex-col p-5 rounded-xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all cursor-pointer bg-white" data-id="element-7018" data-mp="20mp3ylth2xmj" data-mpi={i}>
                          <div className="flex items-start justify-between mb-3" data-id="element-7019" data-mp="1rl7mp04bwpw9">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base ${a.color}`} data-id="element-7020" data-mp="1rnbz66gamx85">
                              {a.initial}
                            </div>
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${a.active ? 'text-emerald-600 bg-emerald-50' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-7021" data-mp="2fb231rnuwwl9">
                              <span className={`w-1.5 h-1.5 rounded-full ${a.active ? 'bg-emerald-500' : 'bg-neutral-400'}`} data-id="element-7022" data-mp="25ls9e7dd75v1" />
                              {a.active ? 'Active' : 'Paused'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1" data-id="element-7023" data-mp="2y7ut7f1q9hbd">
                            <h3 className="font-semibold text-neutral-900 text-sm" data-id="element-7024" data-mp="35mdrwsx9q5iz">
                              {a.name}
                            </h3>
                            <span className="text-[10px] font-medium text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded-full" data-id="element-7025" data-mp="3fji728sp50qa">
                              {a.func}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500 leading-relaxed mb-4" data-id="element-7026" data-mp="3oiwt8w1typ4z">
                            {a.desc}
                          </p>
                          <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t border-neutral-100" data-id="element-7027" data-mp="24dgt4wf1k15">
                            <div className="flex items-center -space-x-1.5" data-id="element-7028" data-mp="73q2vnfgqn2o">
                              {a.tools.map((tool, t) => <span key={t} className="w-6 h-6 rounded-md bg-white border border-neutral-200 flex items-center justify-center" title={tool} data-id="element-7029" data-mp="b5tj0pd2ufua" data-mpi={t}>
                                  <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-3.5 h-3.5 object-contain" alt="" onError={e => {
                            ;
                            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                          }} data-id="element-7030" data-mp="g1sesy0qj04q" />
                                </span>)}
                            </div>
                            <span className="text-[11px] font-medium text-neutral-400" data-id="element-7031" data-mp="1dw0nxubokzjf">
                              {a.tasks}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>;
            })()}

            {/* Skills */}
            {activeNav === 'skills' && <div className="flex flex-col" data-id="element-7032" data-mp="1ws9zoaz41e09">
                <div className="flex flex-wrap items-start justify-between gap-6 mb-6" data-id="element-7033" data-mp="1wqyuq7txi07k">
                  <div className="min-w-[280px] flex-1" data-id="element-7034" data-mp="2khbeqh2g7nei">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7035" data-mp="2p9mbjb1w6t2u">
                      Skills
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-7036" data-mp="dssvj2hez8a3">
                      Reusable capabilities you invoke with{' '}
                      <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-neutral-600" data-id="element-7037" data-mp="mn7990maqar3">
                        /skill-name
                      </code>
                      .
                    </p>
                  </div>
                  <div className="flex items-center flex-wrap gap-4 shrink-0" data-id="element-7038" data-mp="3r1oxf26dcmw9">
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-7039" data-mp="3m2kpsfz3ex81">
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-7040" data-mp="3wpk160o9xah">
                        Private (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium bg-white rounded-md shadow-sm text-neutral-900 font-bold" data-id="element-7041" data-mp="1fbcstk1m5tpc">
                        Global (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-7042" data-mp="7yu8kamdzxoo">
                        Space (0)
                      </button>
                    </div>
                    <div className="w-px h-6 bg-neutral-200" data-id="element-7043" data-mp="gzs404irewvn" />
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-7044" data-mp="h0bljk4hzj95">
                      <button className="px-3 py-1.5 text-sm font-medium bg-white rounded-md shadow-sm text-neutral-900 font-bold" data-id="element-7045" data-mp="ly3fejopdffz">
                        Custom (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-7046" data-mp="3pfvba95ymjjg">
                        Giga (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-7047" data-mp="30sqnt0p64es">
                        Community (0)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-7048" data-mp="36mhr8z3sbxim">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100" data-id="element-7049" data-mp="2z2yyi14qopwu">
                    <div className="flex items-center gap-2" data-id="element-7050" data-mp="2p75czs91lm2x">
                      <button className="p-1 text-neutral-400 hover:text-neutral-600 border-b-2 border-neutral-900 text-neutral-900" data-id="element-7051" data-mp="2p5c569mp0vtc">
                        <LayoutGrid size={16} data-id="element-7052" data-mp="2g6gzqzrh3pr3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-7053" data-mp="2bg0lxejqq247">
                      <span className="text-xs font-medium text-neutral-400" data-id="element-7054" data-mp="2befsjs95wp7x">
                        Filter by
                      </span>
                      <div className="flex items-center bg-neutral-100 rounded-md p-0.5" data-id="element-7055" data-mp="21hulz2hhv61z">
                        <button className="px-2.5 py-1 text-xs font-medium bg-white rounded shadow-sm text-neutral-900" data-id="element-7056" data-mp="1xfzibrc3cgl1">
                          None
                        </button>
                        <button className="px-2.5 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-700" data-id="element-7057" data-mp="1sj8vml5s6mqn">
                          Tag
                        </button>
                        <button className="px-2.5 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-700" data-id="element-7058" data-mp="ejxfr5cib2h">
                          Pack
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-7059" data-mp="38uvjxuc55f4n">
                    <h3 className="text-lg font-semibold text-neutral-400 mb-2" data-id="element-7060" data-mp="3duhxy5zsmzei">
                      No skills yet
                    </h3>
                    <p className="text-sm text-neutral-400" data-id="element-7061" data-mp="2zwb7vc1363pe">
                      Invoke them with{' '}
                      <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-neutral-500" data-id="element-7062" data-mp="34vnt5scb72pv">
                        /skill-name
                      </code>
                      .
                    </p>
                  </div>
                </div>
              </div>}

            {/* Journeys */}
            {activeNav === 'journeys' && <div className="flex flex-col" data-id="element-7063" data-mp="1870mz5a3fwoj">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-7064" data-mp="188bqd3zmn2f4">
                  <div data-id="element-7065" data-mp="1lyoyp7gfkj4z">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7066" data-mp="1m004fctfs0yq">
                      Journeys
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-7067" data-mp="1h5vwaq8fpmw2">
                      Ongoing projects you and your assistant work through
                      together over time — with shared context and
                      back-and-forth as things evolve, not a one-off task.
                    </p>
                  </div>
                  <button onClick={() => setJourneyCreateOpen(true)} className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-7068" data-mp="20wr3fkz77ant">
                    <Plus size={16} data-id="element-7069" data-mp="6jrkbhvlzybs" />
                    Create journey
                  </button>
                </div>

                <div className="flex items-center justify-between mb-6" data-id="element-7070" data-mp="2aljge4m3qhz5">
                  <div className="flex items-center gap-3" data-id="element-7071" data-mp="2alrsvvebbl9t">
                    <button className="p-1 text-neutral-400 hover:text-neutral-600" data-id="element-7072" data-mp="2fkcr5pa07uvm">
                      <LayoutGrid size={18} data-id="element-7073" data-mp="2kk8pb7w0ss39" />
                    </button>
                    <div className="flex items-center gap-1.5" data-id="element-7074" data-mp="2olaoxptun65r">
                      {[{
                      initials: 'GM',
                      count: 19,
                      color: 'bg-sky-700'
                    }, {
                      initials: 'GG',
                      count: 8,
                      color: 'bg-neutral-800'
                    }, {
                      initials: 'NC',
                      count: 3,
                      color: 'bg-indigo-600'
                    }, {
                      initials: 'Ev',
                      count: 2,
                      color: 'bg-rose-800'
                    }, {
                      initials: 'NT',
                      count: 1,
                      color: 'bg-neutral-900'
                    }, {
                      initials: 'NO',
                      count: 1,
                      color: 'bg-rose-700'
                    }].map((a, i) => <div key={i} className="relative" data-id="element-7075" data-mp="mo0jtkl3b0ku" data-mpi={i}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${a.color} ring-2 ring-white`} data-id="element-7076" data-mp="mpv3kvkmkmnc">
                            {a.initials}
                          </div>
                          <div className="absolute -top-2 -right-1.5 bg-white text-neutral-500 text-[9px] font-bold px-1 rounded-full border border-neutral-200" data-id="element-7077" data-mp="1alfysszt2kpg">
                            {a.count}
                          </div>
                        </div>)}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer" data-id="element-7078" data-mp="1yas3pgo3o6ly">
                    <input type="checkbox" className="rounded border-neutral-300 text-[#2B211A] focus:ring-[#2B211A]" data-id="element-7079" data-mp="16ngzrzx7zv3p" />
                    Show favourites
                  </label>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-7080" data-mp="xntyde4hqpan">
                  <div className="divide-y divide-neutral-100" data-id="element-7081" data-mp="11nunbsp7uivy">
                    {[{
                    author: 'NC',
                    color: 'bg-indigo-600',
                    name: 'Ryan Paid Ads',
                    desc: "Updated Jun 20 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Getting Giga Out There',
                    desc: "Updated Jun 19 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Viktor Product Updates',
                    desc: "Updated Jun 19 '26",
                    status: 'Unset'
                  }, {
                    author: 'E',
                    color: 'bg-rose-700',
                    name: 'Product Backlog',
                    desc: "Updated Jun 18 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Giga Creator Outreach',
                    desc: "Updated Jun 18 '26",
                    status: 'Unset'
                  }, {
                    author: 'E',
                    color: 'bg-rose-700',
                    name: 'Twitter Linkedin Content',
                    desc: "Updated Jun 17 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Newsjacking For Giga Content',
                    desc: "Updated Jun 16 '26",
                    status: 'Unset'
                  }, {
                    author: 'GM',
                    color: 'bg-sky-700',
                    name: 'Early User Activation',
                    desc: "Created Jun 16 '26 · Updated Jun 16 '26",
                    status: 'In focus',
                    statusColor: 'text-emerald-600'
                  }, {
                    author: 'GM',
                    color: 'bg-sky-700',
                    name: 'Getting Operator Stable',
                    desc: "Updated May 22 '26",
                    status: 'Unset'
                  }].map((j, i) => <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer group" data-id="element-7082" data-mp="2pwwzf1kswej8" data-mpi={i}>
                        <div className="flex items-center gap-4 min-w-0" data-id="element-7083" data-mp="2kw8ztriiapde">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 ${j.color}`} data-id="element-7084" data-mp="55mr729irfu9">
                            {j.author}
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-7085" data-mp="1i3od49apyp5b">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-7086" data-mp="1n4kpzcd1tlmv">
                              {j.name}
                            </span>
                            <span className="text-xs text-neutral-400 truncate" data-id="element-7087" data-mp="1e3mkepmyp626">
                              {j.desc}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0" data-id="element-7088" data-mp="25uk8fgedw2hb">
                          <span className={`text-xs font-medium ${j.statusColor || 'text-neutral-400'}`} data-id="element-7089" data-mp="25wmy1zbmjtli">
                            {j.status}
                          </span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7090" data-mp="1x0nswkfq5oav">
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-7091" data-mp="2kq8e79zcoxnb">
                              <Info size={16} data-id="element-7092" data-mp="2zfbd1wvbf972" />
                            </button>
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-7093" data-mp="2ufygulik0prt">
                              <Star size={16} data-id="element-7094" data-mp="2pfki5u2ues6g" />
                            </button>
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-7095" data-mp="2ki0ts1cfkz0s">
                              <MoreHorizontal size={16} data-id="element-7096" data-mp="2ggpb6frz5e23" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Pulses */}
            {activeNav === 'pulses' && <div className="flex flex-col" data-id="element-7097" data-mp="21scc39xlbf3d">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-7098" data-mp="1wrx1gq4p0lp3">
                  <div data-id="element-7099" data-mp="1wu1311f4d8m7">
                    <div className="flex items-center gap-2" data-id="element-7100" data-mp="1sqbkotp1fn9m">
                      <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7101" data-mp="1sr4rfunzycrr">
                        Pulses
                      </h1>
                      <span className="text-sm text-neutral-400 font-medium" data-id="element-7102" data-mp="c9vy5saj3mfd">
                        1
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-7103" data-mp="h9ib00mg30nn">
                      Recurring tasks Giga runs on a schedule.
                    </p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0" data-id="element-7104" data-mp="3kraby4ik6uxz">
                    <button className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-id="element-7105" data-mp="3po0mkcblgyc0">
                      <Settings size={16} data-id="element-7106" data-mp="3ulsidhfd14lh" />
                      Pulse Settings
                    </button>
                    <button onClick={() => setPulseCreateOpen(true)} className="flex items-center gap-1.5 text-sm font-medium text-neutral-900 hover:text-neutral-700 transition-colors" data-id="element-7107" data-mp="3b0uonyvcqfr">
                      <Plus size={16} data-id="element-7108" data-mp="36w7woqrtnt8i" />
                      New pulse
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mb-4" data-id="element-7109" data-mp="18h0mowq7klpx">
                  <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer" data-id="element-7110" data-mp="18ee8wfcq59z9">
                    <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-7111" data-mp="1m9qclc8uyasb" />
                    Show disabled
                  </label>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm" data-id="element-7112" data-mp="1w7we3sqr5ewn">
                  <div className="px-6 py-5 flex items-center justify-between gap-4" data-id="element-7113" data-mp="256rh6vyi28vq">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-7114" data-mp="254dr71iofmv1">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" data-id="element-7115" data-mp="2086gkvfc5n6r" />
                      <div className="flex flex-col min-w-0" data-id="element-7116" data-mp="ati4ywekk99s">
                        <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-7117" data-mp="5tvtaree3133">
                          No Contact Daily Brief
                        </span>
                        <span className="text-xs text-neutral-400 truncate" data-id="element-7118" data-mp="2hj2874ylkl82">
                          Daily at 07:00 Etc/UTC
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-5 shrink-0" data-id="element-7119" data-mp="28mumno7d8shh">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-neutral-400" data-id="element-7120" data-mp="2cofyd685z0q3">
                        <Clock size={14} data-id="element-7121" data-mp="1yp6ik7nve8mn" />
                        in 19h
                      </div>
                      <button className="flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-id="element-7122" data-mp="23qvs1ivg2e8m">
                        <Play size={14} data-id="element-7123" data-mp="1tqv8bvtnxw3w" />
                        Run
                      </button>
                      <span className="text-sm font-medium text-neutral-900" data-id="element-7124" data-mp="3k3auavcoi4ts">
                        On
                      </span>
                      <div className="flex items-center gap-1" data-id="element-7125" data-mp="3o4w60ddh8d2e">
                        <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors" data-id="element-7126" data-mp="10xi0x6c680lk">
                          <Pencil size={16} data-id="element-7127" data-mp="vwkgso7lowhf" />
                        </button>
                        <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors" data-id="element-7128" data-mp="rx3bbus6bzc9">
                          <Trash2 size={16} data-id="element-7129" data-mp="mzjzf1iy7ynh" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Power Ups */}
            {activeNav === 'power-ups' && (() => {
              const POWER_UPS = [{
                name: 'Journey Manager',
                desc: 'Uses the Giga Primitives (Journeys, integrations, pulses) to be an extremely...',
                tag: 'on journey',
                icon: LayoutTemplate
              }, {
                name: 'Process Emails',
                desc: 'Archives non-human emails',
                tag: 'Email',
                icon: LayoutTemplate
              }, {
                name: 'Process Meeting Notes',
                desc: 'Meeting Note Taker',
                tag: 'Meeting Note Taker',
                icon: LayoutTemplate
              }, {
                name: 'Product Backlog Manager',
                desc: 'Manages your product backlog',
                tag: 'on journey',
                icon: LayoutTemplate
              }];
              return <div className="flex flex-col" data-id="element-7130" data-mp="2ttkk7szloxta">
                  <div className="flex items-center gap-2 mb-2" data-id="element-7131" data-mp="2ts9gtua2hs2p">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7132" data-mp="2yk0zzjqw8vit">
                      Power Ups
                    </h1>
                    <span className="text-sm text-neutral-400 font-medium" data-id="element-7133" data-mp="33ica7vl3p81k">
                      {POWER_UPS.length}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 leading-relaxed mb-6 max-w-xl" data-id="element-7134" data-mp="3cjsjg006j447">
                    Ready-made automations. Pick a power up and we'll set it up with you.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7135" data-mp="3swpb0nz6goxd">
                    {POWER_UPS.map((p, i) => <div key={i} className="flex flex-col p-5 rounded-xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all bg-white" data-id="element-7136" data-mp="bn0xxphsivca" data-mpi={i}>
                        <div className="flex items-start gap-4 mb-4" data-id="element-7137" data-mp="3f4j2no29nm8h">
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center text-neutral-400 shrink-0" data-id="element-7138" data-mp="3f2ojae6n826i">
                            <p.icon size={20} data-id="element-7139" data-mp="3ovds11at71y5" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1" data-id="element-7140" data-mp="3k3lwsdgpk81o">
                            <h3 className="font-semibold text-neutral-900 text-sm mb-1 leading-snug" data-id="element-7141" data-mp="2web29dpyb9vz">
                              {p.name}
                            </h3>
                            <p className="text-xs text-neutral-500 leading-relaxed" data-id="element-7142" data-mp="2rd3ydpqxiuul">
                              {p.desc}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-5 mt-auto" data-id="element-7143" data-mp="wyztpo9qm0yz">
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-neutral-500 bg-neutral-100 px-2.5 py-1 rounded-full" data-id="element-7144" data-mp="wwwtkbevergj">
                            {p.tag === 'Email' ? <Paperclip size={10} className="mr-0.5" data-id="element-7145" data-mp="1po6r2jxggkin" /> : null}
                            {p.tag}
                          </span>
                        </div>

                        <button className="w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-7146" data-mp="1gnqsnovkk3kn">
                          <Download size={16} data-id="element-7147" data-mp="56qysotkpgoi" />
                          Install
                        </button>
                      </div>)}
                  </div>
                </div>;
            })()}

            {/* Groups — the access boundary for tracks, files and tables */}
            {activeNav === 'groups' && <GroupsView data-id="element-7148" data-mp="39d2sqr3xxwvu" />}

            {/* Tracks */}
            {activeNav === 'spaces-tracks' && <TracksView data-id="element-7149" data-mp="3dfha16ryool0" />}

            {/* Tables */}
            {activeNav === 'tables' && <TablesView data-id="element-7150" data-mp="3ifc0j97trc5j" />}


            {/* Files */}
            {activeNav === 'files' && <div className="flex flex-col" data-id="element-7151" data-mp="2lojja895ne26">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-7152" data-mp="2lpulw4qv6frp">
                  <div data-id="element-7153" data-mp="2ppd6ezerwou0">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7154" data-mp="2pqy24sctqdtg">
                      Files
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-7155" data-mp="2un4wrozrs2w3">
                      A shared workspace for documents — the files you upload
                      for Giga to work from, and the reports, exports, and
                      deliverables it creates for you.
                    </p>
                  </div>
                  <button className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-7156" data-mp="1zge3eme8qaem">
                    <Plus size={16} data-id="element-7157" data-mp="1phomowy2yvzy" />
                    Upload
                  </button>
                </div>
                <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-7158" data-mp="1lh4mtlm4hea4">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-[11px] font-bold uppercase tracking-wide text-neutral-400" data-id="element-7159" data-mp="1ghs1j5awgf9n">
                    <span data-id="element-7160" data-mp="16r761qqivquy">Name</span>
                    <span className="hidden sm:block w-20 text-right" data-id="element-7161" data-mp="16si6bgkreghb">
                      Size
                    </span>
                    <span className="w-28 text-right" data-id="element-7162" data-mp="11u6y1aa44e17">Modified</span>
                  </div>
                  <div className="divide-y divide-neutral-100" data-id="element-7163" data-mp="xq91n7c69hbu">
                    {[{
                    name: 'Reports',
                    type: 'folder',
                    size: '—',
                    modified: 'Jun 14'
                  }, {
                    name: 'Exports',
                    type: 'folder',
                    size: '—',
                    modified: 'Jun 12'
                  }, {
                    name: 'meeting-notes-jun-12.pdf',
                    type: 'file',
                    size: '184 KB',
                    modified: 'Jun 12'
                  }, {
                    name: 'q3-forecast.xlsx',
                    type: 'file',
                    size: '52 KB',
                    modified: 'Jun 10'
                  }, {
                    name: 'brand-guidelines.pdf',
                    type: 'file',
                    size: '3.1 MB',
                    modified: 'Jun 8'
                  }, {
                    name: 'invoices-184.zip',
                    type: 'file',
                    size: '8.4 MB',
                    modified: 'Jun 6'
                  }].map((f, i) => <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3.5 items-center hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-7164" data-mp="3aakqelg18n2l" data-mpi={i}>
                        <div className="flex items-center gap-3 min-w-0" data-id="element-7165" data-mp="35ayag48tksq3">
                          <div className="w-8 h-8 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-7166" data-mp="3j1tbiud2u5j4">
                            {f.type === 'folder' ? <Folder size={16} className="text-neutral-400 fill-neutral-200" data-id="element-7167" data-mp="2rbf49gzkjrgf" /> : <FileText size={16} className="text-neutral-400" data-id="element-7168" data-mp="2rdsyjnmfczmx" />}
                          </div>
                          <span className="text-sm font-medium text-neutral-900 truncate" data-id="element-7169" data-mp="2meyiuujr7r71">
                            {f.name}
                          </span>
                        </div>
                        <span className="hidden sm:block w-20 text-right text-xs text-neutral-500" data-id="element-7170" data-mp="2vfekdygxso99">
                          {f.size}
                        </span>
                        <span className="w-28 text-right text-xs text-neutral-400" data-id="element-7171" data-mp="am9mk7jfv99v">
                          {f.modified}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Viewer */}
            {activeNav === 'viewer' && <div className="flex flex-col h-full" data-id="element-7172" data-mp="1fum8izqiyljs">
                <div className="mb-6" data-id="element-7173" data-mp="1kuieh4is8n1z">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7174" data-mp="1kvjmt18eyvg0">
                    Viewer
                  </h1>
                  <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-7175" data-mp="1ovkcji0yqt2d">
                    A focused, full-screen preview pane. Open any file or
                    conversation here to read it in detail without leaving the
                    dashboard.
                  </p>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center min-h-[320px] text-center border border-dashed border-neutral-200 rounded-xl" data-id="element-7176" data-mp="23n2p15um65gz">
                  <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-7177" data-mp="2cmpr7tv63fb3">
                    <Monitor size={26} className="text-neutral-400" data-id="element-7178" data-mp="zw2uhus9dus" />
                  </div>
                  <h2 className="text-lg font-bold text-neutral-900 mb-2" data-id="element-7179" data-mp="3t1c5jbjf0nv8">
                    Nothing to preview yet
                  </h2>
                  <p className="text-neutral-500 max-w-sm text-sm leading-relaxed" data-id="element-7180" data-mp="3o4vf2aoqtxez">
                    Select a file or conversation and it will open here for a
                    full-screen preview.
                  </p>
                </div>
              </div>}
          </div>
        </main>
      </div>

      {/* Right Chat Panel — persistent in "current" and "progressive"; in the
                                                                                                           calm-deferred and chat-first directions it stays hidden until the user
                                                                                                           explicitly enters a task (chatPanelOpen). In "progressive" it also stays
                                                                                                           hidden during first-time setup so a brand-new user isn't greeted by a
                                                                                                           chat — it appears once they finish setup, switch to the returning
                                                                                                           dashboard, or explicitly start a task (chatPanelOpen). */}
      {(variant === 'current' || variant === 'progressive' && progressiveState === 'returning' || chatPanelOpen) && <div className="w-[320px] lg:w-[380px] bg-white border-l border-neutral-200 flex flex-col shrink-0 h-screen hidden md:flex" data-id="element-7181" data-mp="206ue26qx0vho">
          {/* Chat Tabs */}
          <div className="h-14 flex items-end gap-px px-2 border-b border-neutral-200 bg-neutral-50/50 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-7182" data-mp="3uir6o313g7eb">
            {chatTabs.map(tab => {
            const isActive = tab.id === activeChatId;
            return <div key={tab.id} onClick={() => {
              setActiveChatId(tab.id);
              setActivePlan(null);
            }} className={`shrink-0 px-4 py-2 flex items-center gap-3 text-sm font-medium cursor-pointer transition-colors ${isActive ? 'bg-white border border-neutral-200 border-b-0 rounded-t-lg text-neutral-900 relative top-px' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-7183" data-mp="8htnfzs03hsb">
                  {tab.label}
                  <button onClick={e => {
                e.stopPropagation();
                closeChat(tab.id);
              }} aria-label={`Close ${tab.label}`} className="text-neutral-400 hover:text-neutral-600" data-id="element-7184" data-mp="r94ag5ulvaex">
                    <X size={14} data-id="element-7185" data-mp="117tua462b4xt" />
                  </button>
                </div>;
          })}
            <button onClick={openNewChat} aria-label="New chat" className="shrink-0 ml-1 mb-1.5 flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/70 transition-colors" data-id="element-7186" data-mp="3708nif0es4h2">
              <Plus size={15} data-id="element-7187" data-mp="3rgpn5m26rxoo" />
            </button>
            {/* In the deferred directions, let the user collapse the panel back
             out of view so chat truly stays out of the way until summoned. */}
            {(variant === 'calm-deferred' || variant === 'chat-first') && <button onClick={() => setChatPanelOpen(false)} aria-label="Hide chat panel" title="Hide chat" className="shrink-0 ml-auto mb-1.5 flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/70 transition-colors" data-id="element-7188" data-mp="94b20mzqzd4b">
                <ChevronRight size={16} data-id="element-7189" data-mp="3hjkz1gos5s58" />
              </button>}
          </div>

          {/* Chat Content Area */}
          <div className="flex-1 p-4 flex flex-col overflow-y-auto" data-id="element-7190" data-mp="3mkijjzx9iy9w">
            {chatTabs.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-id="element-7191" data-mp="wtlw5lmle99h">
                <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-4" data-id="element-7192" data-mp="11s6uffiaaiva">
                  <MessageSquare size={24} className="text-neutral-400" data-id="element-7193" data-mp="1bwx3e5c3un90" />
                </div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-1" data-id="element-7194" data-mp="16yvkbkeoinxj">
                  No active chats
                </h3>
                <p className="text-xs text-neutral-500 leading-relaxed max-w-[15rem] mb-5" data-id="element-7195" data-mp="125963nsvabzm">
                  You closed your open chats. Start a new one to ask Giga to get
                  something done.
                </p>
                <button onClick={openNewChat} className="inline-flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-7196" data-mp="o7b4zk3k4c22">
                  <Plus size={16} data-id="element-7197" data-mp="f7nywkzvui2o" />
                  New chat
                </button>
              </div> : activePlan ? <PlanView prompt={activePlan} onBack={() => setActivePlan(null)} data-id="element-7198" data-mp="2dn27hr1nbsey" /> : <>
                <div className="flex items-center justify-between mb-6" data-id="element-7199" data-mp="29kno95u2eqn5">
                  <ChatContextControls data-id="element-7200" data-mp="29jcgkuxi0yqr" />
                  <button className="text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-7201" data-mp="3cey76b9ihabk">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7202" data-mp="3heb2lkeg7ppr">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" data-id="element-7203" data-mp="3qfsgcw4dvfba"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-7204" data-mp="3qdg32w9klt4k"></path>
                    </svg>
                  </button>
                </div>

                {/* Get moving — starter prompts based on connected tools */}
                <div className="flex items-center gap-2 mb-1.5" data-id="element-7205" data-mp="32py3wfnz7srk">
                  <Sparkles size={15} className="text-[#EA580C]" data-id="element-7206" data-mp="37gwqfwzk5n39" />
                  <h3 className="text-sm font-semibold text-[#2B211A]" data-id="element-7207" data-mp="37i7rhp1mcgqo">
                    Not sure where to start?
                  </h3>
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed mb-4" data-id="element-7208" data-mp="2ezu1ucqfs12o">
                  Here are a few things you can ask, based on the tools you’ve
                  connected.
                </p>

                {/* Category tabs */}
                <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-7209" data-mp="22fx4k6p001nm">
                  {STARTER_CATEGORIES.map(cat => {
                const isActive = starterCat === cat.id;
                return <button key={cat.id} onClick={() => setStarterCat(cat.id)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${isActive ? 'bg-[#2B211A] text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-7210" data-mp="1tgjtxap5171b">
                        {cat.label}
                      </button>;
              })}
                </div>

                {/* Prompt suggestions */}
                <div className="flex flex-col gap-2.5" data-id="element-7211" data-mp="1aseuugjfea8u">
                  {STARTER_PROMPTS[starterCat].map((prompt, i) => <button key={i} onClick={() => setActivePlan(prompt)} className="group text-left p-3.5 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-7212" data-mp="15r7p0n0ufl4t" data-mpi={i}>
                      <div className="flex items-center justify-between gap-2 mb-1.5" data-id="element-7213" data-mp="r3qv3doqdg9q">
                        <span className="text-sm font-semibold text-[#2B211A]" data-id="element-7214" data-mp="w5pu6bdftr0v">
                          {prompt.title}
                        </span>
                        <div className="flex items-center -space-x-1.5 shrink-0" data-id="element-7215" data-mp="m5fi4u352pna">
                          {prompt.tools.slice(0, 3).map((tool, t) => <span key={t} className="w-5 h-5 rounded-md bg-white border border-neutral-200 flex items-center justify-center" data-id="element-7216" data-mp="19wl0ph0grw1a" data-mpi={t}>
                              <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-3 h-3 object-contain" alt="" onError={e => {
                        ;
                        (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                      }} data-id="element-7217" data-mp="1ewh35btijfeq" />
                            </span>)}
                        </div>
                      </div>
                      <p className="text-xs text-neutral-500 leading-relaxed mb-2.5" data-id="element-7218" data-mp="1xmysd6f4zsvc">
                        {prompt.desc}
                      </p>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#EA580C] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7219" data-mp="3dbeuk3cevi1n">
                        Try it
                        <ArrowRight size={12} data-id="element-7220" data-mp="3n1zr7l8iychx" />
                      </span>
                    </button>)}
                </div>
              </>}
          </div>

          {/* Chat Input — hidden when there are no open chats */}
          {chatTabs.length > 0 && <div className="p-4 border-t border-neutral-200 bg-white" data-id="element-7221" data-mp="2uforecsqsfm6">
              <div className="flex items-end gap-2" data-id="element-7222" data-mp="38e4uzuvb5q03">
                <button className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-7223" data-mp="34c04v642jbi7">
                  <Paperclip size={20} data-id="element-7224" data-mp="nml6izos9yim" />
                </button>
                <div className="flex-1 relative" data-id="element-7225" data-mp="noz1z9ndlcqp">
                  <textarea placeholder="Enter your message" className="w-full border border-neutral-200 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none min-h-[44px] max-h-[120px]" rows={1} data-id="element-7226" data-mp="jkhjrewnz7i1" />
                </div>
                <button className="p-2.5 text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-7227" data-mp="1nw4e7x8rdfrb">
                  <Send size={18} data-id="element-7228" data-mp="1swif8vbxzpfu" />
                </button>
              </div>
            </div>}
        </div>}

      {/* Create-a-pulse slide-over */}
      <PulseCreatePanel open={pulseCreateOpen} onClose={() => setPulseCreateOpen(false)} data-id="element-7229" data-mp="1xsqvkc1vlool" />

      {/* Create-a-journey slide-over */}
      <JourneyCreatePanel open={journeyCreateOpen} onClose={() => setJourneyCreateOpen(false)} data-id="element-7230" data-mp="26mlrcth3y2rk" />

      {/* Create-an-assistant slide-over */}
      <AssistantCreatePanel open={assistantCreateOpen} onClose={() => setAssistantCreateOpen(false)} data-id="element-7231" data-mp="2bl6ryu09uogj" />

      {/* Persistent Onboarding Widget */}
      <OnboardingWidget onNavigate={setActiveNav} data-id="element-7232" data-mp="2ljmnd96c0jvu" />

      {/* Persistent "Book a call" — always available bottom-right for anyone
                   who's stuck or hasn't set anything up yet. Sits above the debug
                   cycler so the two don't overlap. */}
      <a href="/demo/book" className="fixed bottom-16 right-5 z-50 inline-flex items-center gap-2 h-11 pl-4 pr-5 rounded-full bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold shadow-xl transition-colors" data-id="element-7233" data-mp="502fezuxp4pd">
        <PhoneCall size={16} className="text-[#EA580C]" data-id="element-7234" data-mp="sn0qtj475mgf" />
        Book a call
      </a>

      {/* DEBUG: Variant cycler — one subtle button, bottom-right. Each tap
                                                                           advances to the next dashboard direction. A short label flashes on
                                                                           hover / right after a change so you know which one you're seeing,
                                                                           then fades away to stay out of the way. */}
      <VariantCycler variant={variant} setVariant={setVariant} data-id="element-7235" data-mp="16kyunukw60ho" />
    </div>
  );
}
// One small, low-key control for flipping between dashboard directions while
// designing. Stays tucked in the corner; reveals the current variant's name on
// hover or for a beat after each tap, otherwise it's just a dot.
const DASHBOARD_VARIANTS: {
  id: DashboardVariant;
  label: string;
}[] = [{
  id: 'progressive',
  label: 'Progressive Disclosure'
}, {
  id: 'calm-deferred',
  label: 'Calm + Deferred Chat'
}, {
  id: 'chat-first',
  label: 'Chat-First'
}, {
  id: 'current',
  label: 'Current'
}];
function VariantCycler({
  variant,
  setVariant
}: {
  variant: DashboardVariant;
  setVariant: (v: DashboardVariant) => void;
}) {
  const [hovered, setHovered] = useState(false);
  // Briefly reveal the label right after a tap, then let it fade.
  const [justChanged, setJustChanged] = useState(false);
  const current = DASHBOARD_VARIANTS.find(v => v.id === variant);
  const cycle = () => {
    const idx = DASHBOARD_VARIANTS.findIndex(v => v.id === variant);
    const next = DASHBOARD_VARIANTS[(idx + 1) % DASHBOARD_VARIANTS.length];
    setVariant(next.id);
    setJustChanged(true);
    window.setTimeout(() => setJustChanged(false), 1400);
  };
  const showLabel = hovered || justChanged;
  return <button onClick={cycle} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} title="Switch dashboard variant" aria-label={`Dashboard variant: ${current?.label}. Click to switch.`} className="fixed bottom-5 right-5 z-[9999] flex items-center gap-2 h-8 pl-2.5 pr-2.5 rounded-full bg-[#2B211A]/80 hover:bg-[#2B211A] text-white/70 hover:text-white backdrop-blur-sm border border-white/10 shadow-lg transition-all duration-300" data-id="element-7236" data-mp="25nwo94judk0u">
      <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C] shrink-0" data-id="element-7237" data-mp="2jlbjipws0m0q" />
      <span className={`overflow-hidden whitespace-nowrap text-[11px] font-medium tracking-wide transition-all duration-300 ${showLabel ? 'max-w-[200px] opacity-100' : 'max-w-0 opacity-0'}`} data-id="element-7238" data-mp="u6n2uch5khww">
        {current?.label}
      </span>
    </button>;
}
// ── Shared Overview data ─────────────────────────────────────────────────────
// Reused across the dashboard direction variants so each mockup reads as the
// same product with the same underlying data — only the layout/hierarchy
// changes between directions.
const OVERVIEW_STATS = [{
  label: 'Tasks Completed',
  value: '14,231',
  note: '+12% this week',
  tone: 'good' as const
}, {
  label: 'Hours Saved',
  value: '342',
  note: '~14 days of work',
  tone: 'good' as const
}, {
  label: 'Active Automations',
  value: '12',
  note: 'Running smoothly',
  tone: 'neutral' as const
}];
const RECENT_ACTIVITY = [{
  task: 'Drafted Q3 renewal brief for Northwind',
  tool: 'notion',
  time: '10 mins ago'
}, {
  task: 'Pulled account history from HubSpot',
  tool: 'hubspot',
  time: '12 mins ago'
}, {
  task: 'Replied to support ticket #4092',
  tool: 'zendesk',
  time: '1 hour ago'
}, {
  task: 'Summarized weekly sync meeting',
  tool: 'slack',
  time: '3 hours ago'
}, {
  task: 'Updated 14 stale deals in CRM',
  tool: 'salesforce',
  time: '5 hours ago'
}];
// Full activity log — the browsable, filterable record behind the Overview's
// "Recent Activity" teaser. Mixes chats, pulses, journeys, recipes, and
// assistants, with outcomes so failures surface.
const ACTIVITY_LOG: ActivityEntry[] = [{
  task: 'Drafted Q3 renewal brief for Northwind',
  detail: 'Pulled the account history and assembled a one-page brief.',
  type: 'Chat',
  status: 'success',
  source: 'You',
  tool: 'notion',
  daysAgo: 0,
  time: '10 mins ago'
}, {
  task: 'Morning briefing delivered',
  detail: 'Calendar, unread mail, and tasks due today, sent at 8:00am.',
  type: 'Pulse',
  status: 'success',
  source: 'Daily Briefing',
  tool: 'gmail',
  daysAgo: 0,
  time: '12 mins ago'
}, {
  task: 'Enriching 6 new inbound leads',
  detail: 'Filling in firmographics and scoring against your ICP.',
  type: 'Recipe',
  status: 'running',
  source: 'Lead Enrichment',
  tool: 'hubspot',
  daysAgo: 0,
  time: '20 mins ago'
}, {
  task: 'Could not post recap to #leadership',
  detail: 'Slack returned a permissions error — reconnect may be needed.',
  type: 'Pulse',
  status: 'needs-attention',
  source: 'Weekly Report',
  tool: 'slack',
  daysAgo: 0,
  time: '1 hour ago'
}, {
  task: 'Replied to support ticket #4092',
  detail: 'Drafted from help docs and sent after your approval.',
  type: 'Assistant',
  status: 'success',
  source: 'Support Bot',
  tool: 'zendesk',
  daysAgo: 0,
  time: '2 hours ago'
}, {
  task: 'Checked in on “Early User Activation”',
  detail: 'Summarized progress since last week and proposed next steps.',
  type: 'Journey',
  status: 'success',
  source: 'Early User Activation',
  tool: 'notion',
  daysAgo: 0,
  time: '3 hours ago'
}, {
  task: 'Summarized weekly sync meeting',
  detail: 'Action items captured and assigned to owners.',
  type: 'Chat',
  status: 'success',
  source: 'You',
  tool: 'slack',
  daysAgo: 1,
  time: '4:12 PM'
}, {
  task: 'Opened GitHub issue for Sentry spike',
  detail: 'Bundled the stack trace and tagged the likely owner.',
  type: 'Recipe',
  status: 'success',
  source: 'Bug Triage',
  tool: 'github',
  daysAgo: 1,
  time: '1:48 PM'
}, {
  task: 'Updated 14 stale deals in CRM',
  detail: 'Refreshed stages and logged notes after each touchpoint.',
  type: 'Assistant',
  status: 'success',
  source: 'Sales Assistant',
  tool: 'salesforce',
  daysAgo: 1,
  time: '11:30 AM'
}, {
  task: 'Invoice chase paused — missing connection',
  detail: 'QuickBooks token expired before reminders went out.',
  type: 'Recipe',
  status: 'needs-attention',
  source: 'Invoice Chaser',
  tool: 'quickbooks',
  daysAgo: 1,
  time: '9:05 AM'
}, {
  task: 'Compiled the weekly leadership digest',
  detail: 'Pulled last week’s metrics and posted the summary to #leadership.',
  type: 'Recipe',
  status: 'success',
  source: 'Weekly Report',
  tool: 'notion',
  daysAgo: 3,
  time: 'Mon, 9:00 AM'
}, {
  task: 'Onboarded a new closed-won deal',
  detail: 'Spun up the client workspace and sent the welcome sequence.',
  type: 'Recipe',
  status: 'success',
  source: 'Deal Onboarding',
  tool: 'gmail',
  daysAgo: 4,
  time: 'Mon, 2:30 PM'
}, {
  task: 'Swept 9 stale Linear issues',
  detail: 'Nudged each owner with their list of issues idle 14+ days.',
  type: 'Pulse',
  status: 'success',
  source: 'Stale Issue Sweep',
  tool: 'linear',
  daysAgo: 5,
  time: 'Tue, 8:15 AM'
}, {
  task: 'Generated monthly marketing performance report',
  detail: 'Aggregated spend and conversion metrics across all channels.',
  type: 'Recipe',
  status: 'success',
  source: 'Monthly Marketing Report',
  tool: 'google',
  daysAgo: 12,
  time: '9:00 AM'
}, {
  task: 'Processed 42 invoices from Stripe',
  detail: 'Matched payments to open invoices and updated the ledger.',
  type: 'Assistant',
  status: 'success',
  source: 'Finance Assistant',
  tool: 'stripe',
  daysAgo: 28,
  time: '4:30 PM'
}, {
  task: 'Audited Meta Ads spend',
  detail: 'Paused 3 underperforming ad sets and exported the summary.',
  type: 'Recipe',
  status: 'success',
  source: 'Ad Spend Audit',
  tool: 'meta',
  daysAgo: 75,
  time: '10:15 AM'
}, {
  task: 'Set up new hire onboarding for Alex',
  detail: 'Created Notion workspace, requested tool access, and queued intros.',
  type: 'Journey',
  status: 'success',
  source: 'New Hire Onboarding',
  tool: 'notion',
  daysAgo: 210,
  time: '9:00 AM'
}];
const ACTIVITY_TYPE_FILTERS: (ActivityType | 'all')[] = ['all', 'Chat', 'Pulse', 'Journey', 'Recipe', 'Assistant'];
// Small status pill + icon, reused per activity row.
function ActivityStatusBadge({
  status
}: {
  status: ActivityStatus;
}) {
  if (status === 'running') {
    return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-7239" data-mp="bbhawbzcavm1">
        <Loader2 size={11} className="animate-spin" data-id="element-7240" data-mp="3eyzsnrao2rc4" />
        Running
      </span>;
  }
  if (status === 'needs-attention') {
    return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-7241" data-mp="1lhlogp6g2bta">
        <AlertTriangle size={11} data-id="element-7242" data-mp="1gfmkpe6slcvt" />
        Needs attention
      </span>;
  }
  return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-7243" data-mp="1ua5g35objg4m">
      <CheckCircle2 size={11} data-id="element-7244" data-mp="246s0xugna76n" />
      Done
    </span>;
}
// The Activity view — a filterable, grouped audit feed of everything Giga did.
const RANGE_MAX_DAYS: Record<Exclude<ActivityRange, 'custom'>, number> = {
  all: Infinity,
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '6m': 180,
  '12m': 365
};
function getBucketForDaysAgo(daysAgo: number): string {
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo <= 6) return 'This week';
  return 'Earlier';
}
function ActivityView({
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  rangeFilter,
  setRangeFilter
}: {
  typeFilter: ActivityType | 'all';
  setTypeFilter: (t: ActivityType | 'all') => void;
  statusFilter: ActivityStatus | 'all';
  setStatusFilter: (s: ActivityStatus | 'all') => void;
  rangeFilter: ActivityRange;
  setRangeFilter: (r: ActivityRange) => void;
}) {
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const filtered = ACTIVITY_LOG.filter(e => {
    if (typeFilter !== 'all' && e.type !== typeFilter) return false;
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (rangeFilter === 'custom') {
      // In a real app, we'd parse dates. Here we just show everything if custom is selected but empty.
      // If dates are provided, we'd calculate daysAgo. For prototype, we'll just let it pass.
      return true;
    } else {
      const maxDays = RANGE_MAX_DAYS[rangeFilter];
      if (e.daysAgo > maxDays) return false;
    }
    return true;
  });
  const stats = {
    total: filtered.length,
    success: filtered.filter(e => e.status === 'success').length,
    running: filtered.filter(e => e.status === 'running').length,
    needsAttention: filtered.filter(e => e.status === 'needs-attention').length
  };
  const buckets = ['Today', 'Yesterday', 'This week', 'Earlier'];
  return <div className="flex flex-col" data-id="element-7245" data-mp="uypc8qtvyy36">
      <div className="mb-6" data-id="element-7246" data-mp="uzh5vw3xalqt">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7247" data-mp="v0a4g9sqa4xv">Activity</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-7248" data-mp="1slelnlqx4cz2">
          Everything Giga has done across your chats, pulses, journeys, recipes,
          and assistants — with the outcome of each run.
        </p>
      </div>

      {/* Filter-aware summary strip */}
      <div className="flex flex-wrap gap-3 mb-6 p-4 rounded-xl bg-neutral-50 border border-neutral-200" data-id="element-7249" data-mp="1dw0d2fv61zkq">
        <div className="flex-1 min-w-[120px]" data-id="element-7250" data-mp="2bd7ftjhq448d">
          <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1" data-id="element-7251" data-mp="26lpcios89rk8">
            Total
          </div>
          <div className="text-2xl font-bold text-neutral-900" data-id="element-7252" data-mp="2dxn8zcjk5xtk">
            {stats.total}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-7253" data-mp="2iwq3w45tqnnt">
          <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1" data-id="element-7254" data-mp="2nv1dcds7iw5i">
            Done
          </div>
          <div className="text-2xl font-bold text-emerald-700" data-id="element-7255" data-mp="2suntow3c0sij">
            {stats.success}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-7256" data-mp="2wuy7uw1a2p8h">
          <div className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1" data-id="element-7257" data-mp="2wtvemhghgape">
            Running
          </div>
          <div className="text-2xl font-bold text-amber-700" data-id="element-7258" data-mp="36kznczfkezbs">
            {stats.running}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-7259" data-mp="3bjkor26jzp1t">
          <div className="text-xs font-medium text-rose-600 uppercase tracking-wide mb-1" data-id="element-7260" data-mp="3bmgjkhn3txm1">
            Needs Attention
          </div>
          <div className="text-2xl font-bold text-rose-700" data-id="element-7261" data-mp="3kkit5lkwlykx">
            {stats.needsAttention}
          </div>
        </div>
      </div>

      {/* Filters: type tabs + status dropdown */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-id="element-7262" data-mp="5pssi3gddpcg">
        <div role="tablist" aria-label="Filter activity by type" className="flex flex-wrap gap-2" data-id="element-7263" data-mp="jp0uf43x3bkc">
          {ACTIVITY_TYPE_FILTERS.map(t => {
          const active = typeFilter === t;
          return <button key={t} role="tab" aria-selected={active} onClick={() => setTypeFilter(t)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-[#2B211A] text-white shadow-sm' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-7264" data-mp="eo4pqobqrldv">
                {t === 'all' ? 'All' : t}
              </button>;
        })}
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-7265" data-mp="3s39jw3b57m8a">
          {/* Timing filter — slice the feed by when it happened. */}
          <div className="flex items-center gap-2" data-id="element-7266" data-mp="34cx3s55quj6m">
            <div className="relative" data-id="element-7267" data-mp="2zfdmsxhnvsaz">
              <Clock size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-7268" data-mp="2zecakpowszro" />
              <select aria-label="Filter activity by time" value={rangeFilter} onChange={e => setRangeFilter(e.target.value as ActivityRange)} className="appearance-none border border-neutral-200 rounded-lg pl-8 pr-9 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300 cursor-pointer" data-id="element-7269" data-mp="1jokwt6ry6yjp">
                <option value="all" data-id="element-7270" data-mp="1xpotn8ke6yly">All time</option>
                <option value="7d" data-id="element-7271" data-mp="22nqe9xxgv5zj">Last 7 days</option>
                <option value="30d" data-id="element-7272" data-mp="22mf8jskgno5s">Last 30 days</option>
                <option value="90d" data-id="element-7273" data-mp="1oo7hg1a3vh2j">Last 90 days</option>
                <option value="6m" data-id="element-7274" data-mp="1ono28sbub6s7">Last 6 months</option>
                <option value="12m" data-id="element-7275" data-mp="1tm92usv07sh6">Last 12 months</option>
                <option value="custom" data-id="element-7276" data-mp="1tov82luf9vw8">Custom range</option>
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-7277" data-mp="d7h9acm4gf6a" />
            </div>
            {rangeFilter === 'custom' && <div className="flex items-center gap-2" data-id="element-7278" data-mp="sikhmxb0a7a5">
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300" data-id="element-7279" data-mp="xg2g1tred43w" />
                <span className="text-neutral-400 text-sm" data-id="element-7280" data-mp="ngldhl7fz6c5">to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300" data-id="element-7281" data-mp="9qhur47rturg" />
              </div>}
          </div>
          {/* Status filter — slice by outcome. */}
          <div className="relative" data-id="element-7282" data-mp="4s6p75oie6f1">
            <Filter size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-7283" data-mp="3mx9t4n2qocdv" />
            <select aria-label="Filter activity by status" value={statusFilter} onChange={e => setStatusFilter(e.target.value as ActivityStatus | 'all')} className="appearance-none border border-neutral-200 rounded-lg pl-8 pr-9 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300 cursor-pointer" data-id="element-7284" data-mp="3qz4utsol0rs6">
              <option value="all" data-id="element-7285" data-mp="3hckexpqjbpg8">All statuses</option>
              <option value="success" data-id="element-7286" data-mp="3cbo9v8ug5x98">Done</option>
              <option value="running" data-id="element-7287" data-mp="3ceajrf4t8oum">Running</option>
              <option value="needs-attention" data-id="element-7288" data-mp="3r8nf4q1tte0j">Needs attention</option>
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-7289" data-mp="3mavqc4wha80k" />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? <div className="border border-neutral-200 rounded-xl px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-7290" data-mp="3vzncfweuxy3k">
          <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-7291" data-mp="dvc07er3t5pq">
            <History size={18} className="text-neutral-400" data-id="element-7292" data-mp="8xaeem2amsak" />
          </div>
          <p className="text-sm font-semibold text-neutral-700" data-id="element-7293" data-mp="32oqkhj6wycc0">
            Nothing matches these filters
          </p>
          <p className="text-xs text-neutral-500 mt-1" data-id="element-7294" data-mp="36pan32a9a85j">
            Try a different type, time range, or status.
          </p>
        </div> : <div className="flex flex-col gap-6" data-id="element-7295" data-mp="3gekd8cljhgr0">
          {buckets.map(bucket => {
        const items = filtered.filter(e => getBucketForDaysAgo(e.daysAgo) === bucket);
        if (items.length === 0) return null;
        return <div key={bucket} data-id="element-7296" data-mp="3qc8i3vcv04mw">
                <h2 className="text-[11px] font-bold uppercase tracking-wide text-neutral-400 mb-2" data-id="element-7297" data-mp="3qbgpmtek6n0u">
                  {bucket}
                </h2>
                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-7298" data-mp="3827kp303kdz">
                  <div className="divide-y divide-neutral-100" data-id="element-7299" data-mp="200u6ey31cedo">
                    {items.map((e, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-7300" data-mp="24zp0iw6gdpd3" data-mpi={i}>
                        <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-7301" data-mp="1xh7lybjpzkd9">
                          <img src={`https://cdn.simpleicons.org/${e.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={ev => {
                    ;
                    (ev.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                  }} data-id="element-7302" data-mp="1snml8bqqscad" />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1" data-id="element-7303" data-mp="2bg702p9lwgv6">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-7304" data-mp="2be4ag6cd8pqz">
                            <span className="font-semibold text-neutral-900 text-sm" data-id="element-7305" data-mp="26f13uhgqmbgw">
                              {e.task}
                            </span>
                            <ActivityStatusBadge status={e.status} data-id="element-7306" data-mp="v1xm8p2zqail" />
                          </div>
                          <p className="text-xs text-neutral-500 leading-relaxed mt-0.5" data-id="element-7307" data-mp="qzu7nrl0ypt3">
                            {e.detail}
                          </p>
                          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-neutral-400" data-id="element-7308" data-mp="2euyshj6fb06t">
                            <span className="inline-flex items-center gap-1 font-medium text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full" data-id="element-7309" data-mp="2jpbados943gg">
                              {e.type}
                            </span>
                            <span data-id="element-7310" data-mp="2xmrl3chkyje0">·</span>
                            <span className="truncate" data-id="element-7311" data-mp="2xnsy3mi5pfyd">{e.source}</span>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-neutral-400 shrink-0 mt-0.5" data-id="element-7312" data-mp="32k04fgegstgu">
                          {e.time}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>;
      })}
        </div>}
    </div>;
}
const WHATS_NEW = [{
  tag: 'New',
  title: 'Pulses now support custom schedules',
  detail: 'Run recurring tasks on any cadence you define.'
}, {
  tag: 'Improved',
  title: 'Faster Slack + HubSpot sync',
  detail: 'Context updates land in under a second.'
}, {
  tag: 'New',
  title: 'Journeys keep context across check-ins',
  detail: 'Pick up long-running projects right where you left off.'
}];
function StatCard({
  label,
  value,
  note,
  tone
}: {
  label: string;
  value: string;
  note: string;
  tone: 'good' | 'neutral';
}) {
  return <div className="bg-white p-6 rounded-xl border border-neutral-200" data-id="element-7313" data-mp="qyhi9zrvnrpj">
      <div className="text-neutral-500 text-sm font-medium mb-2" data-id="element-7314" data-mp="lzxtzspta3u0">{label}</div>
      <div className="text-4xl font-bold text-neutral-900" data-id="element-7315" data-mp="m1qz33ks0fzw">{value}</div>
      <div className={`mt-2 text-xs font-medium inline-flex px-2 py-0.5 rounded-full ${tone === 'good' ? 'text-[#0F9D58] bg-[#0F9D58]/10' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-7316" data-mp="10plwuydn5aju">
        {note}
      </div>
    </div>;
}
function RecentActivityList() {
  return <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-7317" data-mp="39g3lusgbkbt">
      <div className="divide-y divide-neutral-100" data-id="element-7318" data-mp="37c0vg6agrd4">
        {RECENT_ACTIVITY.map((item, i) => <div key={i} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-7319" data-mp="sxbsr3lmurjd" data-mpi={i}>
            <div className="flex items-center gap-4" data-id="element-7320" data-mp="11pm3qn4cr11o">
              <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-7321" data-mp="11s8aikjf5ciu">
                <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-5 h-5 object-contain" alt="" data-id="element-7322" data-mp="9zho11rjf26s" />
              </div>
              <span className="font-medium text-neutral-900 text-sm" data-id="element-7323" data-mp="e0l1jhm44m4u">
                {item.task}
              </span>
            </div>
            <span className="text-xs font-medium text-neutral-400 sm:text-right" data-id="element-7324" data-mp="ny7l4hecx1v5">
              {item.time}
            </span>
          </div>)}
      </div>
    </div>;
}
// ── Direction: Current (unchanged baseline) ──────────────────────────────────
function CurrentOverview() {
  return <>
      <div className="flex items-center justify-between mb-8" data-id="element-7325" data-mp="3jvovs6y9pgqs">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7326" data-mp="3jv5aqhdbmc8j">Overview</h1>
        <div className="relative hidden md:block" data-id="element-7327" data-mp="3exn1epve5v00">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-7328" data-mp="3eyy6qu4hjat8" />
          <input type="text" placeholder="Search activity..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-64" data-id="element-7329" data-mp="3a3iuu7q1fozg" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10" data-id="element-7330" data-mp="2w5b2kd3y5bum">
        {OVERVIEW_STATS.map(s => <StatCard key={s.label} {...s} data-id="element-7331" data-mp="2r7sroh6dcmjz" />)}
      </div>
      <div data-id="element-7332" data-mp="2m86ci26zcw8j">
        <h2 className="font-bold text-neutral-900 text-lg mb-4" data-id="element-7333" data-mp="2m8g2hps0z3e8">
          Recent Activity
        </h2>
        <RecentActivityList data-id="element-7334" data-mp="pfwgx9s0zgq9" />
      </div>
    </>;
}
// ── Direction #1: Command Center ─────────────────────────────────────────────
// A state-aware dashboard for returning users. Surfaces failures/needs-attention
// items at the top, provides clickable stats routing to real destinations, and
// shows a cross-product heartbeat of what Giga has been doing.
function CommandCenterOverview({
  onStartTask,
  onNavigate
}: {
  onStartTask: () => void;
  onNavigate: (nav: string) => void;
}) {
  // In a real app, this would be derived from the activity log or system state.
  // We hardcode a single failure here to demonstrate the "needs attention" state.
  const needsAttention = [{
    task: 'Could not post recap to #leadership',
    detail: 'Slack returned a permissions error — reconnect may be needed.',
    tool: 'slack'
  }];
  const reducedMotion = useReducedMotion();
  const hasAttention = needsAttention.length > 0;
  // "Happening now" — the tools Giga is actively touching, shown as a live
  // logo strip so you can SEE work in motion rather than read a list.
  const happeningNow = [{
    slug: 'hubspot',
    label: 'Enriching 6 leads',
    live: true
  }, {
    slug: 'gmail',
    label: 'Drafting 2 replies',
    live: true
  }, {
    slug: 'notion',
    label: 'Updating brief',
    live: false
  }, {
    slug: 'slack',
    label: 'Posting recap',
    live: false
  }];
  // Visual-only week rhythm: tasks completed per day, Mon→Sun.
  const weekRhythm = [{
    day: 'M',
    value: 42
  }, {
    day: 'T',
    value: 61
  }, {
    day: 'W',
    value: 38
  }, {
    day: 'T',
    value: 74
  }, {
    day: 'F',
    value: 88
  }, {
    day: 'S',
    value: 29
  }, {
    day: 'S',
    value: 18
  }];
  const peak = Math.max(...weekRhythm.map(d => d.value));
  // ── Tasks: work Giga has teed up that needs YOUR call ────────────────────
  // Distinct from "Recent activity" (what Giga already finished on its own).
  // Tasks are the handful of things Giga paused on — a draft to approve, a
  // charge to confirm, a reconnect — so the human stays in the loop. Each
  // routes to where it gets resolved.
  const tasks = [{
    title: 'Approve 3 overdue-invoice reminders',
    detail: 'Giga drafted follow-ups to clients and is holding for your OK.',
    tool: 'gmail',
    cta: 'Review drafts',
    nav: 'conversations',
    accent: 'orange' as const
  }, {
    title: 'Confirm the supplier reorder',
    detail: '5 products are running low — approve the restock PO for Acme.',
    tool: 'shopify',
    cta: 'Confirm',
    nav: 'activity',
    accent: 'orange' as const
  }, {
    title: 'Reconnect Slack to resume the #leadership recap',
    detail: 'A permissions error paused one pulse. Reconnect to continue.',
    tool: 'slack',
    cta: 'Reconnect',
    nav: 'integrations',
    accent: 'rose' as const
  }];
  // ── Recent activity: a cross-product feed of finished work ───────────────
  const recentHeartbeat = [{
    task: 'Sent overdue-invoice reminders to 3 clients',
    type: 'Chat',
    tool: 'gmail',
    status: 'done',
    time: '4m ago'
  }, {
    task: 'Enriching 6 new leads with firmographics',
    type: 'Journey',
    tool: 'hubspot',
    status: 'running',
    time: 'now'
  }, {
    task: 'Posted the weekly digest to #leadership',
    type: 'Pulse',
    tool: 'slack',
    status: 'done',
    time: '1h ago'
  }, {
    task: 'Updated the Q3 launch brief',
    type: 'Recipe',
    tool: 'notion',
    status: 'done',
    time: '3h ago'
  }, {
    task: 'Filed 8 receipts and reconciled 2 payments',
    type: 'Assistant',
    tool: 'stripe',
    status: 'done',
    time: 'Yesterday'
  }];
  const ease = (delay: number) => reducedMotion ? {
    duration: 0
  } : {
    duration: 0.45,
    delay,
    ease: 'easeOut' as const
  };
  return <div className="max-w-5xl mx-auto py-2" data-id="element-7335" data-mp="1ketn2dz3r1tg">
      {/* ── Hero status module ─────────────────────────────────────────────
                                         One strong visual anchor on a warm dark stage (the on-brand surface
                                         used across the marketing site), with a faint radial glow + dot grid.
                                         It folds the greeting and the system state into a single focal point
                                         and carries the primary "Start a task" action. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0)} className="relative overflow-hidden rounded-3xl bg-[#1A1410] px-6 py-8 md:px-10 md:py-10 mb-6" data-id="element-7336" data-mp="vqxr73h4c0dc">
        {/* Faint warm glow — the only gradient allowed, on the dark stage. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        background: hasAttention ? 'radial-gradient(70% 60% at 80% 0%, rgba(225,29,72,0.18) 0%, transparent 70%)' : 'radial-gradient(70% 60% at 80% 0%, rgba(234,88,12,0.16) 0%, transparent 70%)'
      }} data-id="element-7337" data-mp="d0yb36399vm2" />
        {/* Subtle dot grid texture. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
        backgroundSize: '26px 26px',
        maskImage: 'radial-gradient(120% 120% at 80% 0%, #000 30%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(120% 120% at 80% 0%, #000 30%, transparent 75%)'
      }} data-id="element-7338" data-mp="3ugehpakv8qgv" />

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-7" data-id="element-7339" data-mp="36fb62hr66p8q">
          <div className="max-w-xl" data-id="element-7340" data-mp="3bdcpx4wf6sl9">
            <div className="flex items-center gap-2 mb-3" data-id="element-7341" data-mp="2x1b7nvv8stn">
              <span className={`relative flex h-2 w-2 ${hasAttention ? '' : ''}`} data-id="element-7342" data-mp="2xkmwmuxakz8">
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${reducedMotion ? '' : 'animate-ping'} ${hasAttention ? 'bg-rose-400' : 'bg-emerald-400'}`} data-id="element-7343" data-mp="7ut52ng2mp0h" />
                <span className={`relative inline-flex h-2 w-2 rounded-full ${hasAttention ? 'bg-rose-400' : 'bg-emerald-400'}`} data-id="element-7344" data-mp="3u2avcyx6icvc" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-white/55" data-id="element-7345" data-mp="2drfaxhh915e2">
                {hasAttention ? `${needsAttention.length} item${needsAttention.length > 1 ? 's' : ''} need attention` : 'All systems healthy'}
              </span>
            </div>
            <p className="text-sm font-medium text-[#EA580C] mb-1.5" data-id="element-7346" data-mp="2u30ivrqwqd80">
              Good morning, Faith
            </p>
            <h1 className="text-3xl md:text-[2.5rem] font-bold text-white tracking-tight leading-[1.08]" data-id="element-7347" data-mp="2p3e59h762uyo">
              {hasAttention ? 'One thing needs a quick fix.' : "You're all caught up."}
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-white/60" data-id="element-7348" data-mp="3cv37bp9sixst">
              {hasAttention ? 'Everything else is running smoothly. Clear the item below and Giga keeps moving.' : 'Giga has been working in the background. Pick up where you left off or start something new.'}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-7349" data-mp="1wgtjqxddhd31">
              <button onClick={onStartTask} className="inline-flex items-center gap-2 bg-white hover:bg-neutral-100 text-[#1A1410] px-5 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-7350" data-mp="1wi4nix6tiku5">
                <Sparkles size={16} className="text-[#EA580C]" data-id="element-7351" data-mp="6xmkzty5ycg3" />
                Start a task
              </button>
              <button onClick={() => onNavigate('activity')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/75 hover:text-white transition-colors" data-id="element-7352" data-mp="bwg2rykhgpm2">
                View all activity
                <ArrowRight size={15} data-id="element-7353" data-mp="qnyng9oaf8br" />
              </button>
            </div>
          </div>

          {/* Hero side panel — either the focused needs-attention card, or a
           calm week-rhythm sparkline so the hero always carries a visual. */}
          <div className="w-full md:w-[300px] shrink-0" data-id="element-7354" data-mp="zowgxy13nxg3">
            {hasAttention ? <div className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-4" data-id="element-7355" data-mp="14metrzi8mwtd">
                <div className="flex items-center gap-2 mb-3" data-id="element-7356" data-mp="31grpw5vz1num">
                  <div className="w-7 h-7 rounded-lg bg-rose-500/20 border border-rose-400/30 flex items-center justify-center shrink-0" data-id="element-7357" data-mp="36ekvoxtd4fud">
                    <AlertTriangle size={14} className="text-rose-300" data-id="element-7358" data-mp="2yewowzdj8mjd" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-rose-300" data-id="element-7359" data-mp="2udl4r9dfgtik">
                    Needs attention
                  </span>
                </div>
                {needsAttention.map((item, i) => <div key={i} data-id="element-7360" data-mp="33bn3tjdlpc37" data-mpi={i}>
                    <div className="flex items-center gap-2.5 mb-1.5" data-id="element-7361" data-mp="33fko48bx09oo">
                      <span className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shrink-0" data-id="element-7362" data-mp="3hbfzlt3ziii1">
                        <img src={`https://cdn.simpleicons.org/${item.tool}/e11d48`} className="w-4 h-4 object-contain" alt="" data-id="element-7363" data-mp="3d9bbtb07wg3b" />
                      </span>
                      <p className="text-sm font-semibold text-white leading-snug" data-id="element-7364" data-mp="3m3fzjlk21bem">
                        {item.task}
                      </p>
                    </div>
                    <p className="text-xs text-white/55 leading-relaxed mb-3" data-id="element-7365" data-mp="3w25g9b07spta">
                      {item.detail}
                    </p>
                    <button onClick={() => onNavigate('integrations')} className="w-full bg-rose-500 hover:bg-rose-600 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors" data-id="element-7366" data-mp="jjm93ylzmmt0">
                      Reconnect Slack
                    </button>
                  </div>)}
              </div> : <div className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-4" data-id="element-7367" data-mp="ei5ak9qz7cfl">
                <div className="flex items-center justify-between mb-3" data-id="element-7368" data-mp="3vyn2dbjbga5u">
                  <span className="text-xs font-semibold uppercase tracking-wide text-white/55" data-id="element-7369" data-mp="4lk4dl37zva6">
                    This week
                  </span>
                  <span className="text-xs font-semibold text-emerald-300" data-id="element-7370" data-mp="3n0uo8apbz8k1">
                    350 tasks
                  </span>
                </div>
                <div className="flex items-end justify-between gap-1.5 h-20" data-id="element-7371" data-mp="3d0c033qqh5wb">
                  {weekRhythm.map((d, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1.5" data-id="element-7372" data-mp="3i06pf2uv1nf9" data-mpi={i}>
                      <motion.div initial={reducedMotion ? false : {
                  height: 0
                }} animate={{
                  height: `${d.value / peak * 100}%`
                }} transition={{
                  duration: reducedMotion ? 0 : 0.5,
                  delay: reducedMotion ? 0 : 0.25 + i * 0.05,
                  ease: 'easeOut'
                }} className={`w-full rounded-sm ${i === 4 ? 'bg-[#EA580C]' : 'bg-white/25'}`} style={{
                  minHeight: 4
                }} data-id="element-7373" data-mp="1d2abi7ctzrs" />
                      <span className="text-[10px] font-medium text-white/40" data-id="element-7374" data-mp="39rawo8ja9ubi">
                        {d.day}
                      </span>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </motion.section>

      {/* ── Happening now ──────────────────────────────────────────────────
                                         A live tool strip — see Giga working, don't read about it. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.08)} className="mb-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-id="element-7375" data-mp="2qvktwj3j0twk">
        <div className="flex items-center gap-2 mb-4" data-id="element-7376" data-mp="2qn7dgntikxc0">
          <Loader2 size={14} className={`text-[#EA580C] ${reducedMotion ? '' : 'animate-spin'}`} data-id="element-7377" data-mp="2qlnyd0jlgsbt" />
          <h2 className="text-sm font-semibold text-neutral-900" data-id="element-7378" data-mp="2vkr46n7ef2ku">
            Happening now
          </h2>
          <span className="text-xs text-neutral-400" data-id="element-7379" data-mp="1xx8lajk0wk34">
            · {happeningNow.filter(h => h.live).length} active
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-7380" data-mp="27w7y8ebtn1vx">
          {happeningNow.map((h, i) => <div key={i} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5" data-id="element-7381" data-mp="2co7qrlpkasih" data-mpi={i}>
              <span className="relative w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-7382" data-mp="1f82bjm17j2tg">
                <img src={`https://cdn.simpleicons.org/${h.slug}/475569`} className="w-4.5 h-4.5 w-[18px] h-[18px] object-contain" alt="" onError={e => {
              ;
              (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
            }} data-id="element-7383" data-mp="2kjnanlg7nlrv" />
                {h.live && <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5" data-id="element-7384" data-mp="2yajq0al4m6gz">
                    <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 ${reducedMotion ? '' : 'animate-ping'}`} data-id="element-7385" data-mp="2ycms3szjzq22" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" data-id="element-7386" data-mp="387odqeume9zw" />
                  </span>}
              </span>
              <div className="min-w-0" data-id="element-7387" data-mp="3h9e3zhcrn2w3">
                <p className="text-xs font-semibold text-neutral-900 truncate" data-id="element-7388" data-mp="3h8cw1lr1qwil">
                  {h.label}
                </p>
                <p className="text-[11px] text-neutral-400" data-id="element-7389" data-mp="1iw4gu74y3a7u">
                  {h.live ? 'In progress' : 'Queued'}
                </p>
              </div>
            </div>)}
        </div>
      </motion.section>

      {/* ── Compact clickable stats ────────────────────────────────────────
                                         Supporting metrics, kept lean so they back up the hero rather than
                                         compete with it. Each routes to its real destination. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.16)} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-7390" data-mp="11mjxa9m5w63g">
        <button onClick={() => onNavigate('activity')} className="group text-left p-5 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-7391" data-mp="1pa9z7pa5zvdx">
          <div className="flex items-center gap-2 mb-3" data-id="element-7392" data-mp="1kdjhkxiporqe">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center" data-id="element-7393" data-mp="dvwcwov27o00">
              <CheckCircle2 size={15} className="text-emerald-600" data-id="element-7394" data-mp="8v8q6fdv5tbl" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-7395" data-mp="acxigxsbpq1y">
              Tasks done
            </span>
            <ArrowRight size={14} className="ml-auto text-neutral-300 group-hover:text-neutral-500 group-hover:translate-x-0.5 transition-all" data-id="element-7396" data-mp="fbil13v1sltk" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-7397" data-mp="5dcm8v4jfvsx">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-7398" data-mp="5fyt0sjlu7a3">14,231</span>
            <span className="text-xs font-semibold text-emerald-700" data-id="element-7399" data-mp="3nqsk033n5ax4">+12%</span>
          </div>
        </button>

        <button onClick={() => onNavigate('pulses')} className="group text-left p-5 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-7400" data-mp="3skwu2v87dz2f">
          <div className="flex items-center gap-2 mb-3" data-id="element-7401" data-mp="3ita94762clmh">
            <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center" data-id="element-7402" data-mp="34szi944r771b">
              <Activity size={15} className="text-neutral-600" data-id="element-7403" data-mp="39slwngwbitbp" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-7404" data-mp="2q6an1ehjbg0q">
              Active pulses
            </span>
            <ArrowRight size={14} className="ml-auto text-neutral-300 group-hover:text-neutral-500 group-hover:translate-x-0.5 transition-all" data-id="element-7405" data-mp="2l8r3q05zcd1x" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-7406" data-mp="2v5vlzowpfodg">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-7407" data-mp="2v4c3drnkt18i">12</span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500" data-id="element-7408" data-mp="3943o0b8llfvh">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-7409" data-mp="345ilg55vik3v" />
              smooth
            </span>
          </div>
        </button>

        <div className="group relative p-5 rounded-2xl border border-neutral-200 bg-white" data-id="element-7410" data-mp="3ix11g2yqgen8">
          <div className="flex items-center gap-2 mb-3" data-id="element-7411" data-mp="3dyhblrh0qipl">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-7412" data-mp="3rwo1olfq7eep">
              <Clock size={15} className="text-amber-600" data-id="element-7413" data-mp="3mxulumcyvbbd" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-7414" data-mp="1l85lzgtwhhf1">
              Hours saved
            </span>
            <div className="relative ml-auto group/info" data-id="element-7415" data-mp="1q55pbjzd862g">
              <Info size={14} className="text-neutral-300 cursor-help hover:text-neutral-500 transition-colors" data-id="element-7416" data-mp="1uwx838ca57i1" />
              <div className="absolute bottom-full right-0 mb-2 w-52 p-2.5 bg-neutral-900 text-white text-xs rounded-lg opacity-0 group-hover/info:opacity-100 transition-opacity pointer-events-none z-10 leading-relaxed" data-id="element-7417" data-mp="1yzaf002rjbge">
                Estimated based on average time per task type across all
                connected tools.
              </div>
            </div>
          </div>
          <div className="flex items-baseline gap-2" data-id="element-7418" data-mp="1g8aq0yv9q1n4">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-7419" data-mp="1g9ubr4zp14wa">342</span>
            <span className="text-xs font-medium text-neutral-500" data-id="element-7420" data-mp="2mntjs5mlt63q">
              ~14 days
            </span>
          </div>
        </div>
      </motion.section>

      {/* ── Tasks ───────────────────────────────────────────────────────────
                                         Work Giga teed up that needs the user's call — approvals, confirms,
                                         and reconnects. Complements Recent activity (what's done) with what
                                         still needs a human. Falls back to a calm empty state when clear. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.22)} className="mb-6" data-id="element-7421" data-mp="2j1od90bf7v4w">
        <div className="flex items-center justify-between mb-4" data-id="element-7422" data-mp="292z8m98jc76l">
          <div className="flex items-center gap-2.5" data-id="element-7423" data-mp="2938yzxxhsgct">
            <h2 className="text-base font-semibold text-neutral-900" data-id="element-7424" data-mp="251vubr64yj7h">Tasks</h2>
            {tasks.length > 0 && <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EA580C] text-white text-xs font-bold" data-id="element-7425" data-mp="7bvuh9zfsaha">
                {tasks.length}
              </span>}
          </div>
          <span className="text-xs font-medium text-neutral-400" data-id="element-7426" data-mp="1wry4dt9ubgll">
            Needs your review
          </span>
        </div>
        <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white shadow-sm" data-id="element-7427" data-mp="21krbuowp131d">
          {tasks.length === 0 ? <div className="flex items-center gap-4 px-5 py-8" data-id="element-7428" data-mp="1rtmujijjp63d">
              <span className="w-11 h-11 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center shrink-0" data-id="element-7429" data-mp="1ru6fz98emcm5">
                <ListChecks size={18} className="text-neutral-400" data-id="element-7430" data-mp="2ki26txbalab0" />
              </span>
              <div data-id="element-7431" data-mp="26ijbk53kxtg4">
                <p className="text-sm font-semibold text-neutral-900" data-id="element-7432" data-mp="26j2henfygrds">
                  Your tasks live here
                </p>
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-7433" data-mp="2bgb1wuokt7i7">
                  Whenever Giga needs a quick approval or decision, it&rsquo;ll
                  land here for you to review.
                </p>
              </div>
            </div> : <div className="divide-y divide-neutral-100" data-id="element-7434" data-mp="rlge6ica2x0v">
              {tasks.map((t, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors" data-id="element-7435" data-mp="rnjdxu38g4is" data-mpi={i}>
                  <span className={`relative w-10 h-10 rounded-xl bg-white border flex items-center justify-center shrink-0 shadow-sm ${t.accent === 'rose' ? 'border-rose-200' : 'border-neutral-200'}`} data-id="element-7436" data-mp="nm7iv91lar37">
                    <img src={`https://cdn.simpleicons.org/${t.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-7437" data-mp="8uynskex4dio" />
                    <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ring-2 ring-white ${t.accent === 'rose' ? 'bg-rose-500' : 'bg-[#EA580C]'}`} data-id="element-7438" data-mp="im33xngby4f3" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-7439" data-mp="3m8admqr6i689">
                    <p className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-7440" data-mp="3hbaaoopmljld">
                      {t.title}
                    </p>
                    <p className="text-sm text-neutral-500 leading-relaxed mt-0.5" data-id="element-7441" data-mp="2vt0drjq4yuoc">
                      {t.detail}
                    </p>
                  </div>
                  <button onClick={() => onNavigate(t.nav)} className={`shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${t.accent === 'rose' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-[#2B211A] hover:bg-[#1A1410] text-white'}`} data-id="element-7442" data-mp="35pdh79bhgmwa">
                    {t.cta}
                    <ArrowRight size={14} data-id="element-7443" data-mp="3jntk0p685t95" />
                  </button>
                </div>)}
            </div>}
        </div>
      </motion.section>

      {/* ── Recent timeline ────────────────────────────────────────────────
                                         A calmer, more visual rail of the latest work across the product. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.24)} data-id="element-7444" data-mp="6ahu6itys0jk">
        <div className="flex items-center justify-between mb-4" data-id="element-7445" data-mp="1qu3payj264ng">
          <h2 className="text-base font-semibold text-neutral-900" data-id="element-7446" data-mp="1mqp9o8z17qaj">
            Recent activity
          </h2>
          <button onClick={() => onNavigate('activity')} className="group flex items-center gap-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-7447" data-mp="1hsxbwybph9yf">
            View all
            <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" data-id="element-7448" data-mp="2af887wsa4opf" />
          </button>
        </div>
        <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white shadow-sm" data-id="element-7449" data-mp="25jjh8hubsg4q">
          <div className="divide-y divide-neutral-100" data-id="element-7450" data-mp="20onbyg02vw9o">
            {recentHeartbeat.map((item, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors group/item cursor-pointer" data-id="element-7451" data-mp="1vrdeqe6g6gba" data-mpi={i}>
                <div className="relative flex items-center justify-center shrink-0 mt-0.5" data-id="element-7452" data-mp="2ogkoyaf9kucd">
                  <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm transition-transform group-hover/item:scale-105" data-id="element-7453" data-mp="3q56j6s5rzay5">
                    <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                  ;
                  (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                }} data-id="element-7454" data-mp="3l8g1k0ebo7am" />
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white ${item.status === 'running' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-7455" data-mp="cwbl1xjurltt">
                    {item.status === 'running' ? <Loader2 size={9} className={`text-white ${reducedMotion ? '' : 'animate-spin'}`} data-id="element-7456" data-mp="hp4pekbess5d" /> : <Check size={9} className="text-white" data-id="element-7457" data-mp="vomhb8k04liy" />}
                  </span>
                </div>

                <div className="flex-1 min-w-0" data-id="element-7458" data-mp="qquehjhtjezz">
                  <div className="flex items-start justify-between gap-3 mb-1.5" data-id="element-7459" data-mp="qnygjv5z0qbj">
                    <span className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-7460" data-mp="1ace6xg3r4x47">
                      {item.task}
                    </span>
                    <span className="text-xs font-medium text-neutral-400 shrink-0 mt-0.5" data-id="element-7461" data-mp="10e6o0rpwk4zk">
                      {item.time}
                    </span>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-[10px] font-semibold uppercase tracking-wide text-neutral-500" data-id="element-7462" data-mp="15ekw29ri7exl">
                    {item.type}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </motion.section>

      {/* Mobile CTA */}
      <button onClick={onStartTask} className="sm:hidden mt-6 w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-7463" data-mp="mnbpa894b49p">
        <Sparkles size={16} data-id="element-7464" data-mp="cnkpps5p9z2r" />
        Start a task
      </button>
    </div>;
}
// ── Direction #2: Progressive disclosure based on state ──────────────────────
// First-ever load → a guided "get started" state. Once set up → the calm
// status overview. A sub-toggle lets you preview both moments.
function ProgressiveOverview({
  onConnect,
  onStartTask,
  onNavigate,
  state,
  onStateChange
}: {
  onConnect: () => void;
  onStartTask: () => void;
  onNavigate: (nav: string) => void; // first/returning is lifted to the parent Dashboard so the right chat panel
  // can stay hidden during first-time setup.
  state: 'first' | 'returning';
  onStateChange: (s: 'first' | 'returning') => void;
}) {
  const reducedMotion = useReducedMotion();
  const setState = onStateChange;
  // Track which step the user is currently on (1, 2, or 3)
  const [currentStep, setCurrentStep] = useState(1);
  // Set once all three steps are finished — shows the celebratory "all set"
  // payoff before the user drops into the everyday (returning) overview.
  const [allSet, setAllSet] = useState(false);
  const steps = [{
    id: 1,
    icon: Plug,
    title: 'Connect your tools',
    detail: 'Giga needs access to your apps to do work on your behalf.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-7465" data-mp="3ti7cqs81eg18">
          <div className="flex flex-wrap gap-2" data-id="element-7466" data-mp="3thedecbeqst4">
            {['slack', 'gmail', 'notion', 'linear', 'salesforce'].map(tool => <div key={tool} className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-7467" data-mp="25w805y2fmso">
                  <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-5 h-5 object-contain" alt={tool} onError={e => {
            ;
            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
          }} data-id="element-7468" data-mp="c1g16kl8w7dq" />
                </div>)}
            <div className="w-10 h-10 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center border-dashed" data-id="element-7469" data-mp="2hvfs2tx7d5z2">
              <span className="text-neutral-400 text-xs font-medium" data-id="element-7470" data-mp="2mv2635kuuq8x">+20</span>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-7471" data-mp="1ri9bcmhe1ohf">
            <button onClick={() => {
          onConnect();
          // Simulate completing the step after a short delay
          setTimeout(() => setCurrentStep(2), 500);
        }} className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-7472" data-mp="1mliy06wyx6zp">
              Connect integrations
            </button>
            <button onClick={() => setCurrentStep(2)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-7473" data-mp="18r9qnxgxf0ty">
              Skip for now
            </button>
          </div>
        </div>
  }, {
    id: 2,
    icon: Sparkles,
    title: 'Try your first task',
    detail: 'See Giga in action. Ask it to summarize a thread or draft an email.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-7474" data-mp="q1a9rxv8os1m">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm" data-id="element-7475" data-mp="l4jpszgbdcax">
            <p className="text-sm text-neutral-600 mb-3 font-medium" data-id="element-7476" data-mp="2wrn7o90yc31o">
              Try asking:
            </p>
            <div className="flex flex-col gap-2" data-id="element-7477" data-mp="31idezrsxufz0">
              <button onClick={() => {
            onStartTask();
            setTimeout(() => setCurrentStep(3), 500);
          }} className="text-left px-3 py-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors border border-transparent hover:border-neutral-200" data-id="element-7478" data-mp="31k6pxjal3mct">
                "Summarize my unread emails from today"
              </button>
              <button onClick={() => {
            onStartTask();
            setTimeout(() => setCurrentStep(3), 500);
          }} className="text-left px-3 py-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors border border-transparent hover:border-neutral-200" data-id="element-7479" data-mp="3kg6jgyj7mxyi">
                "What are my open tasks in Linear?"
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-7480" data-mp="1z1imak5jlv8l">
            <button onClick={() => {
          onStartTask();
          setTimeout(() => setCurrentStep(3), 500);
        }} className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-7481" data-mp="5l5xfypdcoda">
              Start a new chat
            </button>
            <button onClick={() => setCurrentStep(3)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-7482" data-mp="3n0cnt7bqkqfl">
              Skip for now
            </button>
          </div>
        </div>
  }, {
    id: 3,
    icon: Activity,
    title: 'Set up a recurring pulse',
    detail: 'Automate your routine. Have Giga send a daily brief or weekly report.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-7483" data-mp="343bghfupme8h">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200" data-id="element-7484" data-mp="344ml1hvzbq0n">
            <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-7485" data-mp="177vriw03faoo">
              <Clock size={18} className="text-[#EA580C]" data-id="element-7486" data-mp="1asyy1savtdy3" />
            </div>
            <div data-id="element-7487" data-mp="1etiv6vvgghk8">
              <p className="text-sm font-semibold text-neutral-900" data-id="element-7488" data-mp="1eszdng9pvv6q">
                Daily Morning Briefing
              </p>
              <p className="text-xs text-neutral-500" data-id="element-7489" data-mp="1jlskc9oqxdlg">
                Runs every weekday at 8:00 AM
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-7490" data-mp="rtl0l0dy1l2u">
            <button onClick={() => setAllSet(true)} className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-7491" data-mp="ruw6b5qy92wl">
              Complete setup
            </button>
            <button onClick={() => setAllSet(true)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-7492" data-mp="10wcdz5qdqqx4">
              Skip for now
            </button>
          </div>
        </div>
  }];
  const progressPercentage = (currentStep - 1) / steps.length * 100;
  return <div className="max-w-3xl mx-auto py-6" data-id="element-7493" data-mp="2ha2lynkmdzc1">
      <div className="flex justify-center mb-8" data-id="element-7494" data-mp="32bigouhxcazk">
        <div className="inline-flex p-1 rounded-full bg-neutral-100 border border-neutral-200" data-id="element-7495" data-mp="32a7cwuohb38g">
          {(['first', 'returning'] as const).map(s => <button key={s} onClick={() => setState(s)} aria-pressed={state === s} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${state === s ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'}`} data-id="element-7496" data-mp="3c2d78b27dwqt">
              {s === 'first' ? 'First-time' : 'Returning'}
            </button>)}
        </div>
      </div>

      {state === 'returning' ? <CommandCenterOverview onStartTask={onStartTask} onNavigate={onNavigate} data-id="element-7497" data-mp="3v1hcd8qmj6d7" /> : allSet ? <motion.div initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: reducedMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="flex flex-col items-center text-center py-10" data-id="element-7498" data-mp="3q24fdv61giww">
          <motion.span initial={reducedMotion ? false : {
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 18,
        delay: reducedMotion ? 0 : 0.05
      }} className="w-16 h-16 rounded-2xl bg-[#0F9D58] text-white flex items-center justify-center mb-6 shadow-sm" data-id="element-7499" data-mp="1vq7xotxeerf1">
            <CheckCircle2 size={32} data-id="element-7500" data-mp="6cq6jhblpaim" />
          </motion.span>
          <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-2" data-id="element-7501" data-mp="3oj4ft2yxcwao">
            You're all set, Faith
          </h1>
          <p className="text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-7502" data-mp="3thfqth0yhcuh">
            Giga is connected and ready. Ask it to get something done — or let
            it keep working in the background.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3" data-id="element-7503" data-mp="1dso34638pvff">
            <button onClick={onStartTask} className="inline-flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-6 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-7504" data-mp="1dqjq8ynd5m30">
              <Sparkles size={16} data-id="element-7505" data-mp="1z27wddgfzgi" />
              Start a task
            </button>
            <button onClick={() => setState('returning')} className="px-6 py-3 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-7506" data-mp="6tev8yi1xuxj">
              Go to dashboard
            </button>
          </div>
        </motion.div> : <motion.div initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: reducedMotion ? 0 : 0.45,
      ease: 'easeOut'
    }} data-id="element-7507" data-mp="krweaix0ij94">
          <div className="mb-10" data-id="element-7508" data-mp="asf9s4thybeq">
            <p className="text-sm font-medium text-[#EA580C] mb-1.5" data-id="element-7509" data-mp="avjk7jxk23du">
              Welcome, Faith
            </p>
            <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-3" data-id="element-7510" data-mp="fucvd5pddiau">
              Let's get Giga working for you
            </h1>
            <p className="text-lg text-neutral-500 leading-relaxed max-w-xl" data-id="element-7511" data-mp="30jtw5ni7rsqr">
              Setup takes about 2 minutes. Once you&rsquo;re connected,
              here&rsquo;s what you can put Giga to work on:
            </p>

            {/* What you can set up — concrete product surfaces, not a pitch */}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-7512" data-mp="1rkj3udm9tiy2">
              {[{
            icon: MessageSquare,
            label: 'Ask in chat'
          }, {
            icon: LayoutTemplate,
            label: 'Run recipes'
          }, {
            icon: Activity,
            label: 'Schedule pulses'
          }, {
            icon: Map,
            label: 'Track journeys'
          }, {
            icon: UserCircle,
            label: 'Build assistants'
          }].map(c => <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-700" data-id="element-7513" data-mp="13vq0hw3bk8tf">
                  <c.icon size={14} className="text-[#EA580C]" data-id="element-7514" data-mp="c5bt8ipt9uqq" />
                  {c.label}
                </span>)}
            </div>

            {/* Progress Bar */}
            <div className="mt-8 flex items-center gap-4" data-id="element-7515" data-mp="2b9k2hh5jitaj">
              <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden" data-id="element-7516" data-mp="2g8549l0fxl13">
                <div className="h-full bg-[#EA580C] transition-all duration-500 ease-out rounded-full" style={{
              width: `${progressPercentage}%`
            }} data-id="element-7517" data-mp="2kz3m4p12ur6g" />
              </div>
              <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider" data-id="element-7518" data-mp="1sbhg7a4dnagf">
                {currentStep - 1} of {steps.length} done
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4" data-id="element-7519" data-mp="228m2d9y835x8">
            {steps.map(step => {
          const isPast = step.id < currentStep;
          const isActive = step.id === currentStep;
          const isFuture = step.id > currentStep;
          return <div key={step.id} className={`relative rounded-2xl border transition-all duration-300 overflow-hidden ${isActive ? 'border-[#EA580C] bg-white shadow-md ring-1 ring-[#EA580C]/10' : isPast ? 'border-neutral-200 bg-neutral-50/50' : 'border-neutral-200 bg-white opacity-60'}`} data-id="element-7520" data-mp="3dnslaiikomfk">
                  <div className={`px-6 py-5 flex items-start gap-4 ${isActive ? '' : 'items-center'}`} data-id="element-7521" data-mp="122a2nbv722t0">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isPast ? 'bg-[#0F9D58] text-white' : isActive ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-7522" data-mp="x8nxs5v9v37r">
                      {isPast ? <CheckCircle2 size={24} data-id="element-7523" data-mp="japmxag5tox0" /> : <step.icon size={24} data-id="element-7524" data-mp="j9ejxcujgl6y" />}
                    </div>

                    <div className="flex-1 min-w-0 pt-1" data-id="element-7525" data-mp="eauswy138j7q">
                      <div className="flex items-center justify-between gap-4" data-id="element-7526" data-mp="edgzovg5muow">
                        <h3 className={`text-base font-semibold ${isPast ? 'text-neutral-500 line-through decoration-neutral-300' : 'text-neutral-900'}`} data-id="element-7527" data-mp="9dumgm0bteg3">
                          {step.title}
                        </h3>
                        {isPast && <button onClick={() => setCurrentStep(step.id)} className="text-xs font-medium text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-7528" data-mp="23ohqcjpjkuir">
                            Revisit
                          </button>}
                      </div>

                      {(!isPast || isActive) && <p className={`text-sm mt-1 ${isActive ? 'text-neutral-600' : 'text-neutral-400'}`} data-id="element-7529" data-mp="rgtfhy5ywvno">
                          {step.detail}
                        </p>}

                      {/* Expandable Content for Active Step */}
                      <div className={`grid transition-all duration-300 ease-in-out ${isActive ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-7530" data-mp="mfvw5i981vkl">
                        <div className="overflow-hidden" data-id="element-7531" data-mp="8meey440cuxq">{step.content}</div>
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          {/* Global escape hatch — never trap a new user in the flow */}
          <div className="mt-6 text-center" data-id="element-7532" data-mp="3oct5bf76hik">
            <button onClick={() => setState('returning')} className="text-sm font-medium text-neutral-400 hover:text-neutral-700 transition-colors" data-id="element-7533" data-mp="3g8gziqqoorn1">
              Skip setup — I'll explore on my own
            </button>
          </div>
        </motion.div>}
    </div>;
}
// ── Direction #3: Chat-first, everything else collapses ──────────────────────
// Leans fully into chat as the one surface: a big composer takes center stage,
// stats demote to a slim chip row beneath it.
function ChatFirstOverview({
  onStartTask
}: {
  onStartTask: () => void;
}) {
  return <div className="flex flex-col h-full min-h-[480px]" data-id="element-7534" data-mp="3v5r7hq44bfe0">
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4" data-id="element-7535" data-mp="cs9vgj4cwsdq">
        <span className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center mb-6" data-id="element-7536" data-mp="7snf40t8ew0p">
          <Sparkles size={26} className="text-white" data-id="element-7537" data-mp="mpei0k246pv2" />
        </span>
        <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 tracking-tight mb-3" data-id="element-7538" data-mp="hr3869btkfcu">
          What can I help you with?
        </h1>
        <p className="text-neutral-500 mb-8 max-w-md" data-id="element-7539" data-mp="ovceqy8x2jxf">
          Describe a task and Giga will get it done across your connected tools.
        </p>
        <div className="w-full max-w-2xl" data-id="element-7540" data-mp="tr1hfag8ggxy">
          <div className="relative rounded-2xl border border-neutral-200 bg-white shadow-sm focus-within:border-[#EA580C] focus-within:ring-1 focus-within:ring-[#EA580C] transition-colors" data-id="element-7541" data-mp="ys7fzowkqyfe">
            <textarea placeholder="Ask Giga to draft, summarize, update, or automate…" rows={3} className="w-full bg-transparent rounded-2xl px-5 py-4 pr-14 text-sm resize-none focus:outline-none" data-id="element-7542" data-mp="18j1zwmmcllxo" />
            <button onClick={onStartTask} aria-label="Send" className="absolute right-3 bottom-3 w-9 h-9 rounded-xl bg-[#2B211A] hover:bg-[#1A1410] text-white flex items-center justify-center transition-colors" data-id="element-7543" data-mp="1hip2vcuq6zsu">
              <Send size={16} data-id="element-7544" data-mp="3jbd8ttyr81cb" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 pt-8" data-id="element-7545" data-mp="3o8my99m58d05">
        {OVERVIEW_STATS.map(s => <span key={s.label} className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-xs" data-id="element-7546" data-mp="3gqysdvhxj7l3">
            <span className="font-bold text-neutral-900" data-id="element-7547" data-mp="3bx438cjk20em">{s.value}</span>
            <span className="text-neutral-500" data-id="element-7548" data-mp="370ly3bkbc01r">{s.label}</span>
          </span>)}
      </div>
    </div>;
}
// ── Create-a-pulse slide-over panel ──────────────────────────────────────────
// A right-anchored sheet for composing a new recurring pulse: name + prompt,
// a "Saved time" vs "Custom" run mode, a list of selectable saved times, a
// destination note, and an advanced-settings disclosure. Slides in from the
// right over a dimmed backdrop, mirroring the product's real pulse builder.
type SavedTime = {
  id: string;
  label: string;
  cadence: string;
};
const PULSE_SAVED_TIMES: SavedTime[] = [{
  id: 'morning',
  label: 'Morning',
  cadence: 'Daily at 08:00 Etc/UTC'
}, {
  id: 'afternoon',
  label: 'Afternoon',
  cadence: 'Daily at 13:00 Etc/UTC'
}, {
  id: 'evening',
  label: 'Evening',
  cadence: 'Daily at 18:00 Etc/UTC'
}, {
  id: 'weekly',
  label: 'Weekly',
  cadence: 'Mondays at 09:00 Etc/UTC'
}, {
  id: 'monthly',
  label: 'Monthly',
  cadence: 'Monthly (day 1) at 09:00 Etc/UTC'
}];
function PulseCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [runMode, setRunMode] = useState<'saved' | 'custom'>('saved');
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const toggleTime = (id: string) => setSelectedTimes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  return <AnimatePresence data-id="element-7549" data-mp="1p39fn4srm48h">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-7550" data-mp="1p1yab0jo8of9">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-7551" data-mp="23quicxlqa1fv" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create a pulse" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-7552" data-mp="164x5bfq4aobb">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-7553" data-mp="2rj2nxhd98q4i">
              <div data-id="element-7554" data-mp="3616ni48r8cte">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-7555" data-mp="314q0xeh7e6if">
                  New pulse
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-7556" data-mp="2w5n3og7gt4l0">
                  Create a pulse
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-7557" data-mp="3otr86yeavbm4">
                <X size={20} data-id="element-7558" data-mp="3jxac9i0uzsyh" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-7559" data-mp="3b2m6zrvaab9o">
              {/* Name */}
              <div data-id="element-7560" data-mp="bf4eks8lgb5f">
                <label htmlFor="pulse-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-7561" data-mp="bfo00ixgdho7">
                  Name
                </label>
                <input id="pulse-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Morning briefing" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-7562" data-mp="6n4hr8ntlyd2" />
              </div>

              {/* Prompt */}
              <div data-id="element-7563" data-mp="23ci6faupo0a6">
                <label htmlFor="pulse-prompt" className="block text-sm font-semibold text-neutral-900" data-id="element-7564" data-mp="238uf8icqnpym">
                  Prompt
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-7565" data-mp="2d833cukxaad2">
                  What the assistant should do when this pulse runs.
                </p>
                <textarea id="pulse-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Summarise my unread messages and flag anything urgent…" rows={5} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-7566" data-mp="2m1f1vi1ugytz" />
              </div>

              {/* When should it run? */}
              <div data-id="element-7567" data-mp="qzl1qbzzflo9">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3" data-id="element-7568" data-mp="r037q0cg5e9d">
                  When should it run?
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-7569" data-mp="10wpskp4rw5be">
                  <button type="button" onClick={() => setRunMode('saved')} aria-pressed={runMode === 'saved'} className={`relative flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${runMode === 'saved' ? 'border-neutral-900 bg-white' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-7570" data-mp="10v4wgv2t8ebf">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${runMode === 'saved' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-7571" data-mp="1ojxv4zath09q">
                      <Clock size={18} data-id="element-7572" data-mp="3qdncjlzrwqbg" />
                    </span>
                    <span className="min-w-0" data-id="element-7573" data-mp="3lfc51hwyarwe">
                      <span className="block text-sm font-semibold text-neutral-900" data-id="element-7574" data-mp="3lgnadm61o7pm">
                        Saved time
                      </span>
                      <span className="block text-xs text-neutral-500 leading-snug mt-0.5" data-id="element-7575" data-mp="25ub9r0j755gf">
                        Runs with your reusable times.
                      </span>
                    </span>
                    {runMode === 'saved' && <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-neutral-900 text-white" data-id="element-7576" data-mp="1w3qenn2qej29">
                        <CheckCircle2 size={14} data-id="element-7577" data-mp="1n4ld07vvi9lv" />
                      </span>}
                  </button>
                  <button type="button" onClick={() => setRunMode('custom')} aria-pressed={runMode === 'custom'} className={`relative flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${runMode === 'custom' ? 'border-neutral-900 bg-white' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-7578" data-mp="18844rpizdb3j">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${runMode === 'custom' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-7579" data-mp="37kyf5xa9bf15">
                      <Settings size={18} data-id="element-7580" data-mp="13glsjhox21vc" />
                    </span>
                    <span className="min-w-0" data-id="element-7581" data-mp="yhsd3jq2k0sj">
                      <span className="block text-sm font-semibold text-neutral-900" data-id="element-7582" data-mp="yghavoc9v13j">
                        Custom
                      </span>
                      <span className="block text-xs text-neutral-500 leading-snug mt-0.5" data-id="element-7583" data-mp="ojl01bywi2vt">
                        Its own schedule.
                      </span>
                    </span>
                    {runMode === 'custom' && <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-neutral-900 text-white" data-id="element-7584" data-mp="fr18evh10ykh">
                        <CheckCircle2 size={14} data-id="element-7585" data-mp="atrbyvv7zmn5" />
                      </span>}
                  </button>
                </div>
              </div>

              {/* Which times? (only for saved-time mode) */}
              {runMode === 'saved' && <div data-id="element-7586" data-mp="wcvwxd7kzvd">
                  <h3 className="text-sm font-semibold text-neutral-900" data-id="element-7587" data-mp="3mvpwo9d9hwzv">
                    Which times?
                  </h3>
                  <p className="text-xs text-neutral-400 mt-0.5 mb-3" data-id="element-7588" data-mp="3rtj5la5y953u">
                    Pick the saved time(s) this runs with. Manage them in Pulse
                    Settings.
                  </p>
                  <div className="space-y-2.5" data-id="element-7589" data-mp="5m1c6ptjp14r">
                    {PULSE_SAVED_TIMES.map(t => {
                const selected = selectedTimes.includes(t.id);
                return <button key={t.id} type="button" onClick={() => toggleTime(t.id)} aria-pressed={selected} className={`w-full flex items-center gap-4 rounded-xl border p-3.5 text-left transition-colors ${selected ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-7590" data-mp="393t8ggeps78r">
                          <span className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${selected ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-7591" data-mp="2lf07g5j8j97a">
                            <Clock size={18} data-id="element-7592" data-mp="2iclyob4v0zvq" />
                          </span>
                          <span className="min-w-0 flex-1" data-id="element-7593" data-mp="2iaj4rg0l6mlq">
                            <span className="block text-sm font-bold text-neutral-900" data-id="element-7594" data-mp="2dd9g42l0ueyy">
                              {t.label}
                            </span>
                            <span className="block text-sm text-neutral-500 truncate" data-id="element-7595" data-mp="2n32ge7fpr7nw">
                              {t.cadence}
                            </span>
                          </span>
                          {selected && <CheckCircle2 size={18} className="text-neutral-900 shrink-0" data-id="element-7596" data-mp="1ufq5iuix0oaf" />}
                        </button>;
              })}
                  </div>
                </div>}

              {/* Destination */}
              <div data-id="element-7597" data-mp="24cb9dcj77tco">
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-7598" data-mp="24bt3do6qi0rk">
                  Destination
                </h3>
                <p className="text-sm text-neutral-500 leading-relaxed mt-1" data-id="element-7599" data-mp="3kt76g9m2i3nb">
                  Sent via the destination set on the saved time(s) above —
                  manage it in Pulse Settings.
                </p>
              </div>

              {/* Advanced settings */}
              <div className="border-t border-neutral-100 pt-5" data-id="element-7600" data-mp="3sc6ks0uo94zt">
                <button type="button" onClick={() => setAdvancedOpen(v => !v)} aria-expanded={advancedOpen} className="flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:text-neutral-600 transition-colors" data-id="element-7601" data-mp="3ec5m8s1vq9ni">
                  <ChevronDown size={16} className={`transition-transform ${advancedOpen ? 'rotate-0' : '-rotate-90'}`} data-id="element-7602" data-mp="9xny2qht3xic" />
                  Advanced settings
                </button>
                <AnimatePresence initial={false} data-id="element-7603" data-mp="13stpkoieb3r">
                  {advancedOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-7604" data-mp="55nqqczeq2bq">
                      <div className="pt-4 space-y-3 text-sm text-neutral-500" data-id="element-7605" data-mp="8xni8c94awod">
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-7606" data-mp="43svezy7u1l2">
                          <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-7607" data-mp="3vbx0e3mgf8gp" />
                          Skip runs when there's nothing to report
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-7608" data-mp="rsc8dss7n2jh">
                          <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-7609" data-mp="mwdfpszumyd9" />
                          Include links to the source items
                        </label>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-7610" data-mp="2xmvevc6cidfo">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-7611" data-mp="1d772hgzx2yv5">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-7612" data-mp="1n30aqo6ls674">
                Create pulse
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// ── Create-a-journey slide-over panel ────────────────────────────────────────
// A right-anchored sheet for spinning up a new journey: name, space, status,
// an optional overview that seeds journey.md, and a check-in cadence. Slides in
// from the right over a dimmed backdrop, mirroring the pulse builder so the two
// creation flows feel like one family.
const JOURNEY_SPACES = ['Everyone', 'Giga Main', 'Marketing', 'Product', 'Sales'];
const JOURNEY_STATUSES = ['Unset', 'In focus', 'Active', 'Paused', 'Done'];
const JOURNEY_CADENCES = ['No check-ins', 'Daily', 'Weekly', 'Every 2 weeks', 'Monthly'];
function JourneyField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return <div className="relative" data-id="element-7613" data-mp="26dw9vhi2pu7z">
      <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className="w-full appearance-none border border-neutral-200 rounded-xl px-4 py-3 pr-10 text-sm font-medium text-neutral-900 bg-white focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] cursor-pointer" data-id="element-7614" data-mp="26cup2lb99td2">
        {options.map(o => <option key={o} value={o} data-id="element-7615" data-mp="1iqo8hanrn5b8">
            {o}
          </option>)}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-7616" data-mp="1nozh5i2br9rv" />
    </div>;
}
function JourneyCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [space, setSpace] = useState('Everyone');
  const [status, setStatus] = useState('Unset');
  const [overview, setOverview] = useState('');
  const [cadence, setCadence] = useState('No check-ins');
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-7617" data-mp="3qvygxkf9sizj">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-7618" data-mp="3qx9jxi0w5mpl">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-7619" data-mp="3lxx0z8d54zsa" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create a journey" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-7620" data-mp="3elh1940qdf1j">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-7621" data-mp="17lyxwhl9q5b">
              <div data-id="element-7622" data-mp="65e0li81cqmp">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-7623" data-mp="63t6ftpmv9pd">
                  New journey
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-7624" data-mp="3sc2jcqid1kyd">
                  Create a journey
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-7625" data-mp="2gy8hpekaig26">
                <X size={20} data-id="element-7626" data-mp="29cuw8g3qec1r" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-7627" data-mp="24hpbhkm2f3f9">
              {/* Name */}
              <div data-id="element-7628" data-mp="2jasrvonx7b4e">
                <label htmlFor="journey-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-7629" data-mp="2edslfcn5s6cr">
                  Name
                </label>
                <input id="journey-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q3 Launch" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-7630" data-mp="1qopufe6mx15l" />
              </div>

              {/* Space */}
              <div data-id="element-7631" data-mp="17wor9zmtuoqd">
                <label className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-7632" data-mp="17w5f6zjuyuk9">
                  Space
                </label>
                <JourneyField label="Space" value={space} onChange={setSpace} options={JOURNEY_SPACES} data-id="element-7633" data-mp="678mm7rrc4yp" />
              </div>

              {/* Status */}
              <div data-id="element-7634" data-mp="a9n16foe8ik6">
                <label className="block text-sm font-semibold text-neutral-900" data-id="element-7635" data-mp="a6r5yz3xk7zf">
                  Status
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-7636" data-mp="jyddolh27oxf">
                  How active this journey currently is.
                </p>
                <JourneyField label="Status" value={status} onChange={setStatus} options={JOURNEY_STATUSES} data-id="element-7637" data-mp="owyf2o81seng" />
              </div>

              {/* Overview */}
              <div data-id="element-7638" data-mp="xyo81yhkvlnc">
                <label htmlFor="journey-overview" className="block text-sm font-semibold text-neutral-900" data-id="element-7639" data-mp="xzhgd3w6qj7l">
                  Overview
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-7640" data-mp="2zpsveicha01v">
                  Optional — seeds the journey's journey.md. You can edit it
                  later.
                </p>
                <textarea id="journey-overview" value={overview} onChange={e => setOverview(e.target.value)} placeholder="What this journey is about, goals, context…" rows={6} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-7641" data-mp="34qp9tpugh4lj" />
              </div>

              {/* Check-in cadence */}
              <div className="border-t border-neutral-100 pt-6" data-id="element-7642" data-mp="3g4h4nf7oj1zd">
                <label className="block text-sm font-semibold text-neutral-900" data-id="element-7643" data-mp="3b54jcywgi2yw">
                  Check-in cadence
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2 leading-relaxed" data-id="element-7644" data-mp="365057fj0tkbu">
                  How often your assistant should proactively check in on this
                  journey. Leave as “No check-ins” to keep it passive.
                </p>
                <JourneyField label="Check-in cadence" value={cadence} onChange={setCadence} options={JOURNEY_CADENCES} data-id="element-7645" data-mp="1qpaprzlu29kz" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-7646" data-mp="1s4ltms04bscr">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-7647" data-mp="1x36u8sja8e1q">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-7648" data-mp="1n9g8vwigcjq9">
                Create journey
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function AssistantCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [space, setSpace] = useState('Everyone');
  const [description, setDescription] = useState('');
  const [identity, setIdentity] = useState('');
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-7649" data-mp="fvprvmx1byx6">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-7650" data-mp="fufrus6mggnw">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-7651" data-mp="uryo0flmpw5d" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create an assistant" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-7652" data-mp="zl1a62o2u3bj">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-7653" data-mp="2k0s8robb8qf1">
              <div data-id="element-7654" data-mp="2ovz86jxjr8y5">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-7655" data-mp="2twmy12a1hjqs">
                  New assistant
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-7656" data-mp="1waxur3f98vt7">
                  Create an assistant
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-7657" data-mp="266siue28zj3d">
                <X size={20} data-id="element-7658" data-mp="1dimy3rb71u3j" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-7659" data-mp="1ihq0t53pbo8c">
              {/* Name */}
              <div data-id="element-7660" data-mp="1axxbu1t0pd8f">
                <label htmlFor="assistant-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-7661" data-mp="1aygu5jmky3mz">
                  Name
                </label>
                <input id="assistant-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Glitch" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-7662" data-mp="15x9n5ms9h8hd" />
              </div>

              {/* Space */}
              <div data-id="element-7663" data-mp="n7beb4gn3bco">
                <label htmlFor="assistant-space" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-7664" data-mp="n6rufi7ficw0">
                  Space
                </label>
                <div className="relative" data-id="element-7665" data-mp="dbrj6k9wggoz">
                  <select id="assistant-space" value={space} onChange={e => setSpace(e.target.value)} className="w-full appearance-none border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] bg-white" data-id="element-7666" data-mp="8bc9q3sqntca">
                    <option value="Everyone" data-id="element-7667" data-mp="20hkew9wor1w6">Everyone</option>
                    <option value="Private" data-id="element-7668" data-mp="1vg4wkplw79hh">Private</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-neutral-400" data-id="element-7669" data-mp="29fe99faw3lgp">
                    <ChevronDown size={16} data-id="element-7670" data-mp="25flz4y5ul798" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div data-id="element-7671" data-mp="1cqf3c6xs2vro">
                <label htmlFor="assistant-description" className="block text-sm font-semibold text-neutral-900" data-id="element-7672" data-mp="1cqycay5ga9tk">
                  Description
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-7673" data-mp="1lqv59byvhuuf">
                  Short summary shown on the assistants list.
                </p>
                <textarea id="assistant-description" value={description} onChange={e => setDescription(e.target.value)} placeholder="What this assistant does" rows={3} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-7674" data-mp="xzxzyi1l1vs5" />
              </div>

              {/* Identity */}
              <div data-id="element-7675" data-mp="9vgejvww8j2j">
                <label htmlFor="assistant-identity" className="block text-sm font-semibold text-neutral-900" data-id="element-7676" data-mp="ewe4iuo5ahel">
                  Identity
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-7677" data-mp="12mzyxj588364">
                  The assistant's system prompt — who it is and how it behaves.
                  You can refine this later.
                </p>
                <textarea id="assistant-identity" value={identity} onChange={e => setIdentity(e.target.value)} placeholder="Describe who this assistant is and how it should behave..." rows={8} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-7678" data-mp="sq55vfrq2uz8" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-7679" data-mp="1jwvigr2ok06h">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-7680" data-mp="1f39fjqmbjanv">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-7681" data-mp="157fvlm69sew2">
                Create assistant
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// ── Conversations view ───────────────────────────────────────────────────────
type ConvOwner = 'user' | 'assistant';
type ConvScope = 'private' | 'shared';
type ConvSource = 'cli' | 'web' | 'slack';
type ConversationItem = {
  title: string;
  preview: string;
  time: string;
  owner: ConvOwner;
  scope: ConvScope;
  source: ConvSource;
  // Author of the conversation — drives the avatar filter row.
  authorId: string;
};
const CONV_AUTHORS: {
  id: string;
  name: string;
  initials: string;
  color: string;
}[] = [{
  id: 'nt',
  name: 'Nina Torres',
  initials: 'NT',
  color: 'bg-[#1A1410] text-white'
}, {
  id: 'nc',
  name: 'Noah Cole',
  initials: 'NC',
  color: 'bg-indigo-600 text-white'
}, {
  id: 'no',
  name: 'Nadia Owens',
  initials: 'NO',
  color: 'bg-rose-700 text-white'
}, {
  id: 'ev',
  name: 'Evan Vargas',
  initials: 'Ev',
  color: 'bg-rose-800 text-white'
}, {
  id: 'gm',
  name: 'Grace Mwangi',
  initials: 'GM',
  color: 'bg-sky-700 text-white'
}, {
  id: 'gg',
  name: 'Giga (Assistant)',
  initials: 'GG',
  color: 'bg-neutral-900 text-white'
}];
const CONVERSATIONS: ConversationItem[] = [{
  title: 'Q3 renewal strategy',
  preview: 'Here is the draft brief for the Northwind renewal…',
  time: '10 mins ago',
  owner: 'user',
  scope: 'private',
  source: 'web',
  authorId: 'nt'
}, {
  title: 'Weekly standup recap',
  preview: 'Summarized the engineering sync and posted to Slack.',
  time: '1 hour ago',
  owner: 'assistant',
  scope: 'shared',
  source: 'slack',
  authorId: 'gg'
}, {
  title: 'Ad spend audit results',
  preview: 'Paused 3 ad sets under 0.8× ROAS and exported a PDF.',
  time: 'Yesterday',
  owner: 'user',
  scope: 'shared',
  source: 'web',
  authorId: 'nc'
}, {
  title: 'Support ticket triage',
  preview: 'Replied to 6 tickets and escalated #4092 to eng.',
  time: 'Yesterday',
  owner: 'assistant',
  scope: 'private',
  source: 'cli',
  authorId: 'gg'
}, {
  title: 'Pipeline cleanup',
  preview: 'Updated 14 stale deals and flagged 2 at risk.',
  time: '2 days ago',
  owner: 'user',
  scope: 'private',
  source: 'cli',
  authorId: 'no'
}, {
  title: 'Onboarding doc for Acme',
  preview: 'Created the Notion onboarding doc and invited the client.',
  time: '3 days ago',
  owner: 'user',
  scope: 'shared',
  source: 'web',
  authorId: 'ev'
}, {
  title: 'Deploy hotfix to staging',
  preview: 'Ran the release script and confirmed the build is green.',
  time: '3 days ago',
  owner: 'user',
  scope: 'private',
  source: 'cli',
  authorId: 'gm'
}, {
  title: 'Customer NPS digest',
  preview: 'Pulled this week’s responses and flagged 2 detractors.',
  time: '4 days ago',
  owner: 'assistant',
  scope: 'shared',
  source: 'slack',
  authorId: 'gg'
}];
function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel
}: {
  options: {
    value: T;
    label: string;
    icon?: React.ReactNode;
  }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return <div role="tablist" aria-label={ariaLabel} className="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-neutral-200 bg-neutral-50" data-id="element-7682" data-mp="vhe73dk4oof1">
      {options.map(opt => {
      const selected = opt.value === value;
      return <button key={opt.value} role="tab" aria-selected={selected} onClick={() => onChange(opt.value)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selected ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'}`} data-id="element-7683" data-mp="liew3obw4gov">
            {opt.icon}
            {opt.label}
          </button>;
    })}
    </div>;
}
function ConversationsView() {
  const [owner, setOwner] = useState<ConvOwner>('user');
  const [scope, setScope] = useState<ConvScope>('private');
  const [source, setSource] = useState<'all' | ConvSource>('all');
  const [authorId, setAuthorId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const filtered = CONVERSATIONS.filter(c => {
    if (c.owner !== owner) return false;
    if (c.scope !== scope) return false;
    if (source !== 'all' && c.source !== source) return false;
    if (authorId && c.authorId !== authorId) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      if (!c.title.toLowerCase().includes(q) && !c.preview.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sourceFilters: {
    value: 'all' | ConvSource;
    label: string;
  }[] = [{
    value: 'all',
    label: 'All'
  }, {
    value: 'cli',
    label: 'CLI'
  }, {
    value: 'web',
    label: 'Web'
  }, {
    value: 'slack',
    label: 'Slack'
  }];
  return <div className="flex flex-col" data-id="element-7684" data-mp="1lf3y2dm9wtol">
      {/* Header row: title + Users/Assistants + Private/Shared */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-7685" data-mp="1qeynecqehb7j">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-7686" data-mp="1uco2fvffn63f">Conversations</h1>
        <div className="flex items-center gap-3 flex-wrap" data-id="element-7687" data-mp="1zeveel09im5r">
          <SegmentedToggle ariaLabel="Filter by owner" value={owner} onChange={setOwner} options={[{
          value: 'user',
          label: 'Users',
          icon: <UserCircle size={15} data-id="element-7689" data-mp="51dtl5r8s3ef" />
        }, {
          value: 'assistant',
          label: 'Assistants',
          icon: <Sparkles size={15} data-id="element-7690" data-mp="a0gwoknnvzjr" />
        }]} data-id="element-7688" data-mp="24d6rd4lutcs7" />
          <div className="hidden sm:block w-px h-6 bg-neutral-200" data-id="element-7691" data-mp="2bv4kvznn4d9s" />
          <SegmentedToggle ariaLabel="Filter by visibility" value={scope} onChange={setScope} options={[{
          value: 'private',
          label: 'Private',
          icon: <Lock size={14} data-id="element-7693" data-mp="2ll7rj2noppql" />
        }, {
          value: 'shared',
          label: 'Shared',
          icon: <Users size={14} data-id="element-7694" data-mp="2gozepvwym8ml" />
        }]} data-id="element-7692" data-mp="26xcu58yqex76" />
        </div>
      </div>

      {/* Filter row: author avatars + source filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-7695" data-mp="1ny8qkfpylvmw">
        <div className="flex items-center gap-2" data-id="element-7696" data-mp="22vhvkc86qxx3">
          <button onClick={() => setAuthorId(null)} aria-label="All authors" aria-pressed={authorId === null} className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${authorId === null ? 'bg-neutral-200 text-neutral-700' : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200'}`} data-id="element-7697" data-mp="22w9i6xjt1lb8">
            <LayoutGrid size={16} data-id="element-7698" data-mp="39baa8s5pljcj" />
          </button>
          <div className="flex items-center gap-2" data-id="element-7699" data-mp="3gutdik2ds9ck">
            {CONV_AUTHORS.map(a => {
            const selected = authorId === a.id;
            return <button key={a.id} onClick={() => setAuthorId(selected ? null : a.id)} title={a.name} aria-label={a.name} aria-pressed={selected} className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0 transition-all ${a.color} ${selected ? 'ring-2 ring-offset-2 ring-[#EA580C]' : authorId ? 'opacity-40 hover:opacity-100' : 'hover:opacity-90'}`} data-id="element-7700" data-mp="3lvh10vrei821">
                  {a.initials}
                </button>;
          })}
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-7701" data-mp="3qsr1d6gbw44n">
          <div className="relative hidden md:block" data-id="element-7702" data-mp="3uum3gd632ljh">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-7703" data-mp="3ut16kgwaqqig" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search conversations..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-56" data-id="element-7704" data-mp="mdr7lhnpe1ew" />
          </div>
          <div role="tablist" aria-label="Filter by source" className="inline-flex items-center gap-1" data-id="element-7705" data-mp="2wty98k3j9y05">
            {sourceFilters.map(s => {
            const selected = source === s.value;
            return <button key={s.value} role="tab" aria-selected={selected} onClick={() => setSource(s.value)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selected ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'}`} data-id="element-7706" data-mp="35qgynmck33rz">
                  {s.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-7707" data-mp="2n093bqf3lhmc">
        {filtered.length === 0 ? <div className="px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-7708" data-mp="1udejmvq3um9x">
            <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-7709" data-mp="1ubk5c09c9set">
              <MessageSquare size={18} className="text-neutral-400" data-id="element-7710" data-mp="emlxxh7fceog" />
            </div>
            <p className="text-sm font-semibold text-neutral-700" data-id="element-7711" data-mp="uxqvsc18gec">
              No conversations match these filters
            </p>
            <p className="text-xs text-neutral-500 mt-1" data-id="element-7712" data-mp="4x2h0h39uuw8">
              Try a different owner, source, or clear the author selection.
            </p>
          </div> : <div className="divide-y divide-neutral-100" data-id="element-7713" data-mp="xkok7o8l7xik">
            {filtered.map((c, i) => {
          const author = CONV_AUTHORS.find(a => a.id === c.authorId);
          return <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-7714" data-mp="jn9qw8f7r5lb" data-mpi={i}>
                  <div className="flex items-center gap-4 min-w-0" data-id="element-7715" data-mp="34auzhwujpgak">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${author?.color ?? 'bg-neutral-100 text-neutral-500'}`} data-id="element-7716" data-mp="34dqxtmab26zj">
                      {author?.initials ?? '?'}
                    </div>
                    <div className="flex flex-col min-w-0" data-id="element-7717" data-mp="19w7iqvadws8u">
                      <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-7718" data-mp="1547ljalp8dfy">
                        {c.title}
                      </span>
                      <span className="text-xs text-neutral-500 truncate" data-id="element-7719" data-mp="112cle9fi863r">
                        {c.preview}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0" data-id="element-7720" data-mp="r3wua6gh8wu9">
                    <span className="hidden sm:inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 capitalize" data-id="element-7721" data-mp="m51ze658jhts">
                      {c.source}
                    </span>
                    <span className="text-xs font-medium text-neutral-400" data-id="element-7722" data-mp="2gibdyccn1tiw">
                      {c.time}
                    </span>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </div>;
}
function EmptyStateOverview({
  onStartTask,
  onNavigate
}: {
  onStartTask: () => void;
  onNavigate: (view: string) => void;
}) {
  // DEBUG: mirror the connection + activity debug toggles so the populated vs
  // empty states can be previewed. `hasTasks` follows the activity toggle —
  // when Giga has been doing work, it also has things awaiting your review.
  const [toolsConnected, setToolsConnected] = useState(false);
  const [hasActivity, setHasActivity] = useState(false);
  const hasTasks = hasActivity;
  // Tasks: the handful of things Giga paused on and needs the user's call.
  const tasks = [{
    title: 'Approve 3 overdue-invoice reminders',
    detail: 'Giga drafted follow-ups to clients and is holding for your OK.',
    tool: 'gmail',
    cta: 'Review',
    nav: 'conversations',
    accent: 'orange' as const
  }, {
    title: 'Confirm the supplier reorder',
    detail: '5 products are running low — approve the restock PO for Acme.',
    tool: 'shopify',
    cta: 'Confirm',
    nav: 'activity',
    accent: 'orange' as const
  }, {
    title: 'Reconnect Slack to resume the #leadership recap',
    detail: 'A permissions error paused one pulse. Reconnect to continue.',
    tool: 'slack',
    cta: 'Reconnect',
    nav: 'integrations',
    accent: 'rose' as const
  }];
  const recentActivity = [{
    task: 'Sent overdue-invoice reminders to 3 clients',
    tool: 'gmail',
    type: 'Chat',
    status: 'done',
    time: '4m ago'
  }, {
    task: 'Enriching 6 new leads with firmographics',
    tool: 'hubspot',
    type: 'Journey',
    status: 'running',
    time: 'now'
  }, {
    task: 'Posted the weekly digest to #leadership',
    tool: 'slack',
    type: 'Pulse',
    status: 'done',
    time: '1h ago'
  }];
  return <div className="flex flex-col h-full" data-id="element-7723" data-mp="24xzcgfvbazwq">
      <div className="flex items-center justify-between mb-8" data-id="element-7724" data-mp="24ys9gp4gyb1o">
        <h2 className="text-2xl font-bold text-neutral-900" data-id="element-7725" data-mp="200798pp7vrd8">Overview</h2>
        <div className="flex items-center gap-2" data-id="element-7726" data-mp="2jl7p7l49rdat">
          {/* DEBUG toggles — preview populated vs empty states */}
          <button onClick={() => setToolsConnected(v => !v)} className={`px-2.5 py-1 rounded-full border border-dashed text-[11px] font-mono font-semibold uppercase tracking-wide transition-colors ${toolsConnected ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-neutral-300 bg-white text-neutral-500'}`} data-id="element-7727" data-mp="2oku1nsc9wpik">
            {toolsConnected ? 'tools: connected' : 'tools: none'}
          </button>
          <button onClick={() => setHasActivity(v => !v)} className={`px-2.5 py-1 rounded-full border border-dashed text-[11px] font-mono font-semibold uppercase tracking-wide transition-colors ${hasActivity ? 'border-[#EA580C]/40 bg-[#EA580C]/[0.06] text-[#C2410C]' : 'border-neutral-300 bg-white text-neutral-500'}`} data-id="element-7728" data-mp="25lpvqsg13bv4">
            {hasActivity ? 'activity: on' : 'activity: off'}
          </button>
          <button className="px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-50 transition-colors" data-id="element-7729" data-mp="1mv8995lshci7">
            Customize
          </button>
        </div>
      </div>

      {/* ── Core Areas ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-6 mb-8" data-id="element-7730" data-mp="3ev4asbtq4ns8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-7731" data-mp="3asptvx9m7y3l">
          {/* Conversations */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-7732" data-mp="bdm447potmte">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-7733" data-mp="6erdijlhatyq">
              <div className="flex items-center gap-2" data-id="element-7734" data-mp="2ceioq89sggq">
                <MessageSquare size={16} className="text-neutral-500" data-id="element-7735" data-mp="2b36q34o82ej" />
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-7736" data-mp="3tifl6ae661qr">
                  Conversations
                </h3>
              </div>
            </div>
            <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-7737" data-mp="2i4brz6feohmr">
              <span className="text-sm text-neutral-400 font-medium" data-id="element-7738" data-mp="xm9ofdhf4azy">
                No conversations yet.
              </span>
            </div>
          </div>

          {/* Journeys */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-7739" data-mp="16mpqciiija2p">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-7740" data-mp="1bd65q85w95rp">
              <div className="flex items-center gap-2" data-id="element-7741" data-mp="1gd0ua5275j9l">
                <Map size={16} className="text-neutral-500" data-id="element-7742" data-mp="1gc963faxintc" />
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-7743" data-mp="1laj6c4bigelt">
                  Journeys
                </h3>
              </div>
              <button className="text-xs font-medium text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors" data-id="element-7744" data-mp="1q8kn2ilgs1u4">
                Pinned Journeys <ChevronDown size={12} data-id="element-7745" data-mp="3s2trh0exh6gq" />
              </button>
            </div>
            <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-7746" data-mp="3w3dp053eyc3e">
              <span className="text-sm text-neutral-400 font-medium" data-id="element-7747" data-mp="3d752zd7y080t">
                No pinned journeys.
              </span>
            </div>
          </div>
        </div>

        {/* Assistants */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-7748" data-mp="3lzgpinzxrbbi">
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-7749" data-mp="3hxv5miozhwrt">
            <div className="flex items-center gap-2" data-id="element-7750" data-mp="2u7r36enj832e">
              <UserCircle size={16} className="text-neutral-500" data-id="element-7751" data-mp="2uan3uaqrl5uj" />
              <h3 className="text-sm font-semibold text-neutral-900" data-id="element-7752" data-mp="2par62t8nuanf">
                Assistants
              </h3>
            </div>
          </div>
          <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-7753" data-mp="2ya49b466y2zp">
            <span className="text-sm text-neutral-400 font-medium" data-id="element-7754" data-mp="iizv2ronj2vz">
              No assistants configured yet.
            </span>
          </div>
        </div>
      </div>

      {/* ── Tasks ─────────────────────────────────────────────────────────
                      Work Giga teed up that needs your call. Populates with the activity
                      toggle; otherwise shows a calm "your tasks live here" empty state. */}
      <div className="mb-8" data-id="element-7755" data-mp="c3j8j8m2r7y9">
        <div className="flex items-center justify-between mb-4" data-id="element-7756" data-mp="c4arnjyhjd7n">
          <div className="flex items-center gap-2.5" data-id="element-7757" data-mp="h116y52gu4s0">
            <h3 className="text-base font-semibold text-neutral-900" data-id="element-7758" data-mp="h3x7m15p77k5">Tasks</h3>
            {hasTasks && <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EA580C] text-white text-xs font-bold" data-id="element-7759" data-mp="14s6uz7ik83de">
                {tasks.length}
              </span>}
          </div>
          {hasTasks && <span className="text-xs font-medium text-neutral-400" data-id="element-7760" data-mp="100f754qsgbqy">
              Needs your review
            </span>}
        </div>
        <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm" data-id="element-7761" data-mp="1np83317eujlk">
          {!hasTasks ? <div className="flex items-center gap-4 px-5 py-8" data-id="element-7762" data-mp="1shrfvvy9xapb">
              <span className="w-11 h-11 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center shrink-0" data-id="element-7763" data-mp="2uhih4o57nvst">
                <ListChecks size={18} className="text-neutral-400" data-id="element-7764" data-mp="2zeim0vqbqsic" />
              </span>
              <div data-id="element-7765" data-mp="3484ndrr1f9yu">
                <p className="text-sm font-semibold text-neutral-900" data-id="element-7766" data-mp="347lef0jd7vwy">
                  Your tasks live here
                </p>
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-7767" data-mp="2bie83fdo6215">
                  Whenever Giga needs a quick approval or decision, it&rsquo;ll
                  land here for you to review.
                </p>
              </div>
            </div> : <div className="divide-y divide-neutral-100" data-id="element-7768" data-mp="2lfiyxsigmloa">
              {tasks.map((t, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors" data-id="element-7769" data-mp="2pi5qdsb7y4ky" data-mpi={i}>
                  <span className={`relative w-10 h-10 rounded-xl bg-white border flex items-center justify-center shrink-0 shadow-sm ${t.accent === 'rose' ? 'border-rose-200' : 'border-neutral-200'}`} data-id="element-7770" data-mp="pbcxntk318w">
                    <img src={`https://cdn.simpleicons.org/${t.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-7771" data-mp="1p8fnijrpco88" />
                    <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ring-2 ring-white ${t.accent === 'rose' ? 'bg-rose-500' : 'bg-[#EA580C]'}`} data-id="element-7772" data-mp="1bj3zlbdt16cr" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-7773" data-mp="11jmvuzi452jf">
                    <p className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-7774" data-mp="11itphzn2gf1t">
                      {t.title}
                    </p>
                    <p className="text-sm text-neutral-500 leading-relaxed mt-0.5" data-id="element-7775" data-mp="sjztobhk7spb">
                      {t.detail}
                    </p>
                  </div>
                  <button onClick={() => onNavigate(t.nav)} className={`shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${t.accent === 'rose' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-[#2B211A] hover:bg-[#1A1410] text-white'}`} data-id="element-7776" data-mp="2ruhogcqjvyd1">
                    {t.cta}
                    <ArrowRight size={14} data-id="element-7777" data-mp="2x1xymwb3eeq9" />
                  </button>
                </div>)}
            </div>}
        </div>
      </div>

      {/* Activity + Connection containers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1" data-id="element-7778" data-mp="3pq3k5l9z07r5">
        {/* Recent activity */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden" data-id="element-7779" data-mp="3trown5ilek0t">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100" data-id="element-7780" data-mp="3frw3l3jlfrpo">
            <h3 className="text-sm font-semibold text-neutral-900" data-id="element-7781" data-mp="3kqqme5hk9g9s">
              Recent activity
            </h3>
            {hasActivity && <button onClick={() => onNavigate('activity')} className="text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-7782" data-mp="24bpcktdfra6e">
                View all
              </button>}
          </div>
          {hasActivity ? <div className="divide-y divide-neutral-100" data-id="element-7783" data-mp="21qho7qlx1kvg">
              {recentActivity.map((item, i) => <div key={i} className="px-5 py-3.5 flex items-start gap-3.5" data-id="element-7784" data-mp="1wsptypxstmo3" data-mpi={i}>
                  <span className="relative w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-7785" data-mp="2arxv3odiv4ux">
                    <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-4.5 h-[18px] object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-7786" data-mp="1i4bk3v1ysxy1" />
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white ${item.status === 'running' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-7787" data-mp="1rw7rjwpxy0ex" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-7788" data-mp="zbhg1w5vqq8l">
                    <div className="flex items-start justify-between gap-3" data-id="element-7789" data-mp="udpk8r246jz4">
                      <p className="text-sm font-medium text-neutral-900 leading-snug" data-id="element-7790" data-mp="181zqe7myuipn">
                        {item.task}
                      </p>
                      <span className="text-xs text-neutral-400 shrink-0" data-id="element-7791" data-mp="1d0ubxhcbildg">
                        {item.time}
                      </span>
                    </div>
                    <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-[10px] font-semibold uppercase tracking-wide text-neutral-500" data-id="element-7792" data-mp="1mrff7i2xse2p">
                      {item.type}
                    </span>
                  </div>
                </div>)}
            </div> : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-7793" data-mp="20pvl56sz60js">
              <div className="w-12 h-12 rounded-full bg-white border border-neutral-200 flex items-center justify-center mb-4 shadow-sm" data-id="element-7794" data-mp="25prrhcp5a42i">
                <Activity size={20} className="text-neutral-400" data-id="element-7795" data-mp="6doy9aqpxvbn" />
              </div>
              <h4 className="text-base font-semibold text-neutral-900 mb-2" data-id="element-7796" data-mp="bc08hmkxe7ue">
                No recent activity
              </h4>
              <p className="text-sm text-neutral-500 max-w-sm mb-6" data-id="element-7797" data-mp="2onl63onddh0b">
                When Giga completes tasks, runs automations, or answers your
                questions, they will appear here.
              </p>
              <button onClick={onStartTask} className="px-4 py-2 bg-[#2B211A] hover:bg-[#1A1410] text-white rounded-lg text-sm font-medium transition-colors" data-id="element-7798" data-mp="2fqsmemm9rhwz">
                Start a new chat
              </button>
            </div>}
        </div>

        {/* Connection state */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden" data-id="element-7799" data-mp="33jiw0p6p9p43">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100" data-id="element-7800" data-mp="2yhtnbg6y9nj6">
            <h3 className="text-sm font-semibold text-neutral-900" data-id="element-7801" data-mp="2tmo10g9my6uk">
              Connected tools
            </h3>
            <button onClick={() => onNavigate('integrations')} className="text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-7802" data-mp="1n24zynpsamhu">
              Manage
            </button>
          </div>
          {toolsConnected ? <div className="divide-y divide-neutral-100" data-id="element-7803" data-mp="1om6l4ebjie01">
              {[{
            tool: 'slack',
            label: 'Slack',
            ok: true
          }, {
            tool: 'gmail',
            label: 'Gmail',
            ok: true
          }, {
            tool: 'notion',
            label: 'Notion',
            ok: false
          }].map((c, i) => <div key={i} className="px-5 py-3.5 flex items-center gap-3.5" data-id="element-7804" data-mp="10urc62p9cyft" data-mpi={i}>
                  <span className="w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-7805" data-mp="15odi7c0x242n">
                    <img src={`https://cdn.simpleicons.org/${c.tool}/475569`} className="w-4.5 h-[18px] object-contain" alt="" data-id="element-7806" data-mp="vwzd8oq74c24" />
                  </span>
                  <span className="flex-1 text-sm font-medium text-neutral-900" data-id="element-7807" data-mp="hyj995jpwzno">
                    {c.label}
                  </span>
                  {c.ok ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full pl-2 pr-2.5 py-1" data-id="element-7808" data-mp="3aj7577rbkrr">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-7809" data-mp="3uidhga009wbd" />
                      Connected
                    </span> : <button onClick={() => onNavigate('integrations')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-full pl-2 pr-2.5 py-1 transition-colors" data-id="element-7810" data-mp="d6m7q8mynbcl">
                      <AlertTriangle size={11} data-id="element-7811" data-mp="hak3qagzo61f" />
                      Reconnect
                    </button>}
                </div>)}
            </div> : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-7812" data-mp="vxvhmj0n824p">
              <div className="w-12 h-12 rounded-full bg-white border border-neutral-200 flex items-center justify-center mb-4 shadow-sm" data-id="element-7813" data-mp="r1515ukxf4ir">
                <Plug size={20} className="text-neutral-400" data-id="element-7814" data-mp="14xsbcz1w3r6m" />
              </div>
              <h4 className="text-base font-semibold text-neutral-900 mb-2" data-id="element-7815" data-mp="10zum5w84d538">
                Connect your tools
              </h4>
              <p className="text-sm text-neutral-500 max-w-sm mb-6" data-id="element-7816" data-mp="27flof9py2ea5">
                Giga works best when it can access your apps. Connect Slack,
                Gmail, Notion, and more to get started.
              </p>
              <button onClick={() => onNavigate('integrations')} className="px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg text-sm font-medium transition-colors" data-id="element-7817" data-mp="2lcqqyzqg8v0n">
                View integrations
              </button>
            </div>}
        </div>
      </div>

      {/* ── Your Impact ─────────────────────────────────────────────────────────
          The high-level metrics moved down here so the top of the overview can
          focus on actionable areas (Conversations, Journeys, Assistants). */}
      <div className="mt-8 pt-8 border-t border-neutral-200" data-id="element-7818" data-mp="15vqgq9hsnmut">
        <h3 className="text-base font-semibold text-neutral-900 mb-4" data-id="element-7819" data-mp="19x21a0lt9hw5">
          Your Impact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7820" data-mp="12d9jpidgi969">
          {[{
          label: 'Tasks Completed',
          value: hasActivity ? '128' : '0',
          note: hasActivity ? '+12% this week' : 'Ready for your first task',
          icon: CheckCircle2
        }, {
          label: 'Hours Saved',
          value: hasActivity ? '46' : '0',
          note: hasActivity ? '~6 days of work' : 'Let Giga do the heavy lifting',
          icon: Clock
        }, {
          label: 'Active Automations',
          value: hasActivity ? '4' : '0',
          note: hasActivity ? 'Running smoothly' : 'Create a pulse or journey',
          icon: Zap
        }].map((stat, i) => <div key={i} className={`bg-white rounded-xl border border-neutral-200 p-5 shadow-sm flex flex-col gap-3 transition-opacity ${hasActivity ? '' : 'opacity-60'}`} data-id="element-7821" data-mp="5obndvq2636z" data-mpi={i}>
              <div className="flex items-center justify-between" data-id="element-7822" data-mp="r1ta2rq53ct">
                <span className="text-sm font-medium text-neutral-500" data-id="element-7823" data-mp="3rwkziqayoaex">
                  {stat.label}
                </span>
                <stat.icon size={16} className="text-neutral-400" data-id="element-7824" data-mp="3qkucba7304ac" />
              </div>
              <div className="flex items-baseline gap-2" data-id="element-7825" data-mp="3lolqxf2ajeuq">
                <span className="text-3xl font-bold text-neutral-900" data-id="element-7826" data-mp="3gny2n15g5c47">
                  {stat.value}
                </span>
              </div>
              <span className="text-xs font-medium text-neutral-400" data-id="element-7827" data-mp="3bpeh31urm2cd">
                {stat.note}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
// Giga-specific onboarding checklist. Each step maps to a real part of getting
// Giga working across your apps — not a generic marketing funnel. The dashboard
// section a step links to is used to jump the user straight there.
type OnboardingStep = {
  id: number;
  title: string;
  desc: string;
  cta: string;
  nav: string;
  done?: boolean;
};
const ONBOARDING_STEPS: OnboardingStep[] = [{
  id: 1,
  title: 'Create your Giga account',
  desc: 'Your workspace is ready — Giga is standing by.',
  cta: 'Done',
  nav: 'overview',
  done: true
}, {
  id: 2,
  title: 'Connect your tools',
  desc: 'Link Gmail, Notion, and the rest of your stack so Giga can act on your behalf across the tools you already use.',
  cta: 'Connect other tools',
  nav: 'integrations'
}, {
  id: 3,
  title: '@mention Giga in a chat',
  desc: 'Add Giga to any Slack channel and just @mention it to kick off your first task.',
  cta: 'Open conversations',
  nav: 'conversations'
}, {
  id: 4,
  title: 'Set up a Pulse',
  desc: 'Let Giga watch for something and act automatically — like a morning briefing or a reply nudge.',
  cta: 'Create a pulse',
  nav: 'pulses'
}, {
  id: 5,
  title: 'Build your first Journey',
  desc: 'Chain a few steps into a repeatable workflow Giga can run end to end.',
  cta: 'Create a journey',
  nav: 'journeys'
}];
function OnboardingWidget({
  onNavigate
}: {
  onNavigate: (nav: string) => void;
}) {
  const [expanded, setExpanded] = useState<number | null>(2);
  const [minimized, setMinimized] = useState(false);
  // Slack is required to create a Giga account, so by the time the user reaches
  // this dashboard it is always connected. Detected here (true) so the
  // "Connect your tools" step can show Slack as linked instead of asking again.
  const slackConnected = true;
  const completed = ONBOARDING_STEPS.filter(s => s.done).length;
  const pct = Math.round(completed / ONBOARDING_STEPS.length * 100);
  if (minimized) {
    return <button onClick={() => setMinimized(false)} aria-label="Open setup checklist" className="fixed bottom-6 left-6 md:left-[256px] z-50 flex items-center gap-2.5 h-11 pl-2.5 pr-4 rounded-full bg-[#2B211A] hover:bg-[#1A1410] text-white shadow-xl transition-colors" data-id="element-7828" data-mp="335amdx69feit">
        <span className="w-6 h-6 rounded-full bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-7829" data-mp="3c4nvupudw33j">
          <Sparkles size={13} className="text-white" data-id="element-7830" data-mp="wgxlxr52jm9w" />
        </span>
        <span className="text-sm font-semibold" data-id="element-7831" data-mp="reyh0ctokhau">Set up Giga · {pct}%</span>
      </button>;
  }
  return <div className="fixed bottom-6 left-6 md:left-[256px] z-50 w-80 bg-white rounded-2xl shadow-xl border border-[#E7E0D6] overflow-hidden" data-id="element-7832" data-mp="29cbpxalubsbx">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 bg-[#F5EFE6] border-b border-[#E7E0D6]" data-id="element-7833" data-mp="20cy9tz37o5i8">
        <div className="flex items-start justify-between mb-1" data-id="element-7834" data-mp="20f19zby2vf0o">
          <div className="flex items-center gap-2" data-id="element-7835" data-mp="25ckrqltzi9xd">
            <span className="w-6 h-6 rounded-md bg-[#2B211A] flex items-center justify-center shrink-0" data-id="element-7836" data-mp="2t1m24emdnv5a">
              <Sparkles size={13} className="text-[#EA580C]" data-id="element-7837" data-mp="2x3h1hdkqzygf" />
            </span>
            <h3 className="text-base font-bold text-[#2B211A]" data-id="element-7838" data-mp="2jbkdnlq44apu">
              Get Giga working
            </h3>
          </div>
          <button onClick={() => setMinimized(true)} aria-label="Minimize" className="text-neutral-400 hover:text-[#2B211A] transition-colors" data-id="element-7839" data-mp="2o9u8twbu7bbg">
            <Minus size={16} data-id="element-7840" data-mp="3byx0zy43kmk7" />
          </button>
        </div>
        <div className="flex items-center justify-between mb-3 pl-8" data-id="element-7841" data-mp="3gr6pd9pkdekd">
          <span className="text-sm text-neutral-500" data-id="element-7842" data-mp="1s4vkbvrdwjrv">
            {completed} of {ONBOARDING_STEPS.length} done
          </span>
          <span className="text-sm font-semibold text-[#EA580C]" data-id="element-7843" data-mp="1n4zfjuav4ob9">{pct}%</span>
        </div>
        {/* Progress bar */}
        <div className="h-2 w-full bg-[#E7DCCB] rounded-full overflow-hidden ml-8 max-w-[calc(100%-2rem)]" data-id="element-7844" data-mp="1j4z149nrkd47">
          <motion.div className="h-full bg-[#EA580C] rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${pct}%`
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-7845" data-mp="1e76zgnxbhcmt" />
        </div>
      </div>

      {/* Accordion items */}
      <div className="flex flex-col px-2 py-2 max-h-[46vh] overflow-y-auto" data-id="element-7846" data-mp="221qg46w9w62g">
        {ONBOARDING_STEPS.map(step => {
        const isOpen = expanded === step.id;
        return <div key={step.id} className="rounded-xl" data-id="element-7847" data-mp="2ujud1nv100h2">
              <button onClick={() => setExpanded(isOpen ? null : step.id)} className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F5EFE6] transition-colors text-left" data-id="element-7848" data-mp="2ulf9xk4tbvi3">
                <div className="flex items-center gap-3 min-w-0" data-id="element-7849" data-mp="2x6d6ctrq93kp">
                  {step.done ? <span className="w-5 h-5 rounded-full bg-[#2B211A] flex items-center justify-center text-white shrink-0" data-id="element-7850" data-mp="323dd76weiacv">
                      <Check size={12} strokeWidth={3} data-id="element-7851" data-mp="3b3thgikyxlis" />
                    </span> : <span className="w-5 h-5 rounded-full border-2 border-dashed border-[#C9BBA6] shrink-0" data-id="element-7852" data-mp="3b54k2f2ogn8b" />}
                  <span className={`text-sm font-medium truncate ${step.done ? 'text-neutral-400 line-through' : 'text-[#2B211A]'}`} data-id="element-7853" data-mp="3g0sasywt5ofx">
                    {step.title}
                  </span>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-neutral-400 shrink-0" data-id="element-7854" data-mp="3ursq9nvz2ilk" /> : <ChevronDown size={16} className="text-neutral-400 shrink-0" data-id="element-7855" data-mp="3uqziqkp6vp2d" />}
              </button>
              <AnimatePresence initial={false} data-id="element-7856" data-mp="2pt77hxc8s97">
                {isOpen && !step.done && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-7857" data-mp="7o4f3n42osos">
                    <div className="pl-11 pr-3 pb-3 pt-0.5" data-id="element-7858" data-mp="28osp2euwy7wk">
                      <p className="text-sm text-neutral-500 leading-relaxed mb-3" data-id="element-7859" data-mp="28nhi66668k18">
                        {step.desc}
                      </p>
                      {/* Slack is connected during account creation, so on the
                       "Connect your tools" step we surface it as already-linked
                       (detected) rather than asking for it again, and offer a
                       separate button to connect the rest of the stack. */}
                      {step.id === 2 && slackConnected && <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-[#E7E0D6] bg-[#F5EFE6] px-3 py-2" data-id="element-7860" data-mp="122p0hgn604vh">
                          <span className="flex items-center gap-2 min-w-0" data-id="element-7861" data-mp="38ousrskgqzap">
                            <img src="https://cdn.simpleicons.org/slack" alt="" aria-hidden="true" className="w-4 h-4 shrink-0 object-contain" data-id="element-7862" data-mp="3dnxvv7gvuvg1" />
                            <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-7863" data-mp="2zwayt5f49iw7">
                              Slack
                            </span>
                          </span>
                          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0" data-id="element-7864" data-mp="2px1w9o66qvy8">
                            <Check size={13} strokeWidth={3} data-id="element-7865" data-mp="2uz0yguq6vmtl" />
                            Connected
                          </span>
                        </div>}
                      <button onClick={() => onNavigate(step.nav)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-7866" data-mp="2l0l8iv2wejlo">
                        {step.cta}
                        <ArrowRight size={14} data-id="element-7867" data-mp="j5ue3ydd7wzl" />
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </div>;
}